/**
 * The Patient Advocate workspace (EP-004C phase 3, + assignment authority
 * 2026-09-04).
 *
 * == It is a work queue, not a dashboard ================================
 *
 * Views and one record. MY WORK: the cases assigned to an advocate that
 * they are authorized to open, seen through a lens (Active by default),
 * each saying why it needs attention and what to do next. FOLLOW-UPS: open
 * obligations, soonest first. ALL CASES (managers): every open case with
 * its holder, same lenses. Open a case and the context pane shows the
 * evidence the vetting record carries, the facility's contacts, every
 * action, and the story. UNASSIGNED, work nobody holds, now lives in
 * Manifest Review beside Leads to place (2026-09-30).
 *
 * == Lenses, not tabs ======================================================
 *
 * The Monday board keeps a tab per working state so a PA never searches
 * 1,400 residents for the six that need vetting. The same utility is one
 * row of chips above the list — Active · New · Needs vetting ·
 * Vetted · Ready to submit · More — over the same cases,
 * filtered by the server. Under the chips a filter bar (building, status,
 * who manages the funds, Patient Liability, documents) and a sort narrow the
 * same list; those are URL state and never write anything. More carries the whole
 * canonical vocabulary, grouped (addendum §7). A lens is never a page,
 * never a group a row is moved into, and never a way to set a status.
 *
 * Everything is searchable by resident or facility and paged. No long
 * scrolls. The board's own word, its raw dates and the transfer note are
 * provenance, not the work (addendum §8): they sit behind "Source details",
 * sent only to a reviewer. "Updated" is when eMerge's half-hourly update
 * last ran; if that has stopped the screen says so, because a stuck update
 * can hide work. Monday's sync is retired (2026-10-11) and never makes the
 * workspace "not current"; its last data is said neutrally, in a tooltip.
 *
 * == Managers =============================================================
 *
 * A manager (Jody, Kaleigh) sees a dropdown of advocates that toggles the
 * workspace to exactly what that advocate sees, and can assign or reassign
 * a case, one at a time or every case matching a filter. Those two are the
 * only writes. An advocate never sees the dropdown or the controls.
 *
 * == Frontend visibility is never authorization =========================
 *
 * The server decides what anyone may see. `?as=` is ignored server-side for
 * anyone without the manager capability.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import WorkspaceMenu from '../../components/WorkspaceMenu';
import { WorkspaceLayout, SidebarTrigger, type SidebarGroup } from '../../components/workspaceSidebar';
import { ViewTransition } from '../../components/shadcn/motion';
import { ArrowRightLeft, CalendarClock, CircleDollarSign, History as HistoryIcon, Inbox, Undo2, Users, UserSearch } from 'lucide-react';
import { updatedStamp, mondayRetiredLine, notCurrentText } from '../../components/workspaceFreshness';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import FeedbackDrawer, { FeedbackButton } from '../../components/feedback/FeedbackDrawer';
import WorkflowPulse from '../../components/feedback/WorkflowPulse';
import { PromptSource } from '../../services/feedbackApi';
import ResidentsView, { SourceHealthNotice } from './ResidentsView';
import DobInput from '../../components/emerge/DobInput';
import { Why, firstReason, useWhyId } from '../../components/emerge/WithReason';
import MondayMoveReview from './MondayMoveReview';
import RevetsDue from './RevetsDue';
import UmedSignPanel from './UmedSignPanel';
import MarkDuplicateForm from './MarkDuplicateForm';
import type { UmedProposal, ProviderReminder, ContactTouch, ContactAttempt, TouchDisposition } from './advocateApi';
import { residentsApi, IdentityScope } from './residentsApi';
import {
  advocateApi, personName, shortDate, shortDateTime, relativeDay, daysFromToday, isLens, LENSES, LENS_GROUPS,
  STAGE_LABEL, DISPOSITION_LABEL, BLOCKER_LABEL, LIFECYCLE_LABEL, INTAKE_LABEL, replacementTag, ACTION_LABEL, ATTENTION_LABEL, FINANCIAL_RESPONSIBILITY_LABEL,
  UMED_STAGE_LABEL, UMED_TYPE_LABEL,
  Scope, Counts, Lens, WorkCase, OtherOpenRecord, ResidentHit, FollowUp, FollowUpPage, CaseDetail, FacilityContext, OpenAction, Attention, Advocate, Paged, WorkPage,
  CaseAction, CaseEvent, Demographics, LeadEvidence, UmedCase, Provenance,
  canDo, freshKey, isClosedLead, WORKING_STATUS_WORDS, lensIsAllCasesOnly, PROVIDER_WORDS, TERMINAL_STATUS_WORDS, REASON_REQUIRED_WORDS, STATUS_WORD_LABEL, UPLOAD_CLASS_LABEL, StatusWord, StatusLegendEntry, NoteKind, UploadClass, Readiness, CaseDocument,
  INTAKE_REASON_LABEL, PrivatePayStatus, PrivatePayHandoff, PrivatePayPayer, PrivatePayAch, PrivatePayAchBody, PrivatePayAchKind,
  SalesPage, SalesRow, ORIGIN_LABEL, HistoryPage, HistoryRow, HistoryFilter, isHistoryFilter,
  // EP-010: the operating table's own vocabulary.
  AllCasesPage, FacilityGroup, DocumentState, LIABILITY_CLASS_LABEL, ENROLLMENT_LABEL, Submittal, OfferedProduct, CanKey,
  FinancialResponsibilityType, LiabilityFact,
  // D-30: the building's directory.
  FacilityDirectory, FacilityContactLine, MainPhone, ContactRole, CONTACT_ROLE_LABEL,
  Refine, NO_REFINE, REFINE_KEYS, readRefine, SORTS, FR_FILTERS, LIABILITY_FILTERS, DOCS_FILTERS, WorkFilters,
  // 2026-10-05: the resident's building, corrected natively.
  FacilityChangeKind,
  // #367 round 6: what a refused Ready to submit names.
  BlockingUmed,
  NewSetOffer,
  // 2026-10-15: one file for several residents of one building.
  SharedFiling,
} from './advocateApi';
import ResidentNotesThread from './ResidentNotesThread';
import { forgetMemoryWithoutSession } from './noteStorage';
import { facilityDisplayName, formatFacilityPhone } from './facilityFormat';
import { StateBlock as TideState } from '../../components/tide';
import type { EmptyReason } from '../../components/tide';
// The shared workspace bridge first (Tide phase 2), then this workspace's own sheet.
import '../../styles/tide/workspace.css';
import './advocate.css';
import NotThisResidents, { MisfiledNote, UndoNotThisResidents } from '../documents/NotThisResidents';
// 2026-10-15: the confirm step for a genuinely shared file, one control for every upload form.
import SharedFileReason, { readSharedRefusal, SharedRefusal, FACILITY_WIDE_REASON } from '../documents/SharedFileReason';
import { isPlayable, useRecordingPlayer, RecordingAudio } from '../documents/RecordingPlayer';
// 2026-10-05: the building header, the docked case panel and the facility correction.
import './paDock.css';
import { SendBackAction } from '../../components/sendBack/SendBackDialog';
import FilledStatusChip from '../../components/status/FilledStatusChip';
import BuildingOrClinic from '../../components/emerge/BuildingOrClinic';
import ReturnedToYou, { offersReturnedTab, useReturned } from '../../components/sendBack/ReturnedToYou';
import { FindResidentEntry } from '../../components/residentDirectory';
import { sendBackApi, sendBackPath } from '../../components/sendBack/sendBackApi';
import { useHandoffSignal } from '../../contexts/NotificationsContext';
import { HANDOFF_STATE_LABEL } from '../finance/financeApi';

// EP-009: Monthly sales and All cases are every advocate's; Team cases (the
// manager's tenant-wide open list, formerly labelled "All cases") is the
// manager's. Unassigned moved to Manifest Review (Isaiah Cotto, 2026-09-30).
// T2 (2026-09-20): Residents is the door a person enters eMerge through without Monday, and the manager's identity review.
// Monday move review (ruling 2026-10-05) is the manager's exception queue over the Monday-move ledger.
type View = 'work' | 'follow-ups' | 'returned' | 'sales' | 'history' | 'all' | 'residents' | 'monday-moves';
/*
 * New is a chip on My work now (the `new` lens), not a tab: one
 * predicate, one count, one list. An old `?view=new` link is answered by
 * redirecting to view=work&lens=new (see LEGACY_NEW).
 */
const VIEWS: readonly View[] = ['work', 'follow-ups', 'returned', 'sales', 'history', 'all', 'residents', 'monday-moves'];
const VIEW_LABEL: Record<View, string> = { work: 'My work', 'follow-ups': 'Follow-ups', returned: 'Returned to you', sales: 'My sales', history: 'All cases', all: 'Team cases', residents: 'Residents', 'monday-moves': 'Monday move review' };
const BASE = '/portal/patient-advocate';
/** Where Unassigned lives now. An old `?view=unassigned` link lands there. */
export const UNASSIGNED_HOME = '/portal/manifest?view=unassigned';

const isView = (v: string | null): v is View => v !== null && (VIEWS as readonly string[]).includes(v);

/**
 * EP-004P §19–20: each top-level workspace owns its own secondary state.
 * My work and All cases carry a case lens; Follow-ups carries none. A lens
 * from one workspace is never written into another's URL, so it can never
 * silently intersect another queue.
 */
interface Loc { view: View; caseId: string | null; as: string | null; q: string; page: number; lens: Lens; refine: Refine; month: string | null; filter: HistoryFilter }

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const ZONE = 'America/New_York';
/** The current month in the PA team's zone, 'YYYY-MM'. */
const currentMonth = (): string => new Intl.DateTimeFormat('en-CA', { timeZone: ZONE, year: 'numeric', month: '2-digit' }).format(new Date()).slice(0, 7);
const shiftMonth = (month: string, by: number): string => {
  const y = Number(month.slice(0, 4)); const m = Number(month.slice(5, 7)) - 1 + by;
  const d = new Date(Date.UTC(y, m, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const monthName = (month: string): string => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(new Date(`${month}-15T12:00:00Z`));

const LENSED_VIEWS: readonly View[] = ['work', 'all'];
const lensBelongs = (view: View, raw: string | null): boolean =>
  // Closed (R8) is All cases' own: in My work it would be an empty queue that looks like a bug.
  raw === null || (LENSED_VIEWS.includes(view) && isLens(raw) && (view === 'all' || !lensIsAllCasesOnly(raw)));

function path(l: Partial<Loc>): string {
  const qp = new URLSearchParams();
  const view: View = l.view || 'work';
  if (view !== 'work') qp.set('view', view);
  if (l.caseId) qp.set('case', l.caseId);
  if (l.as) qp.set('as', l.as);
  if (l.q) qp.set('q', l.q);
  if (LENSED_VIEWS.includes(view) && l.lens && l.lens !== 'active') qp.set('lens', l.lens);
  // The filter bar's state rides beside the lens, only for the views that have the bar, and only when set.
  if ((view === 'work' || view === 'all') && l.refine) {
    for (const k of REFINE_KEYS[view]) if (l.refine[k]) qp.set(k, l.refine[k]);
  }
  // EP-009: Monthly sales carries a month; All cases carries a filter. Neither leaks into another view's URL.
  if (view === 'sales' && l.month) qp.set('month', l.month);
  if (view === 'history' && l.filter && l.filter !== 'all') qp.set('filter', l.filter);
  if (l.page && l.page > 1) qp.set('page', String(l.page));
  const s = qp.toString();
  return s ? `${BASE}?${s}` : BASE;
}

/** Moving to another workspace starts it clean: its own default lens, no search, page 1, no case from the old queue. */
const viewPath = (l: Loc, view: View): string => path({ view, as: l.as, caseId: null, q: '', page: 1, lens: 'active', refine: NO_REFINE, month: null, filter: 'all' });

/**
 * What a read that did not succeed says. `failure` marks the server not being
 * reached, failing, or answering without the data (danger tone, Try again);
 * without it the answer is a refusal at a named gate (withheld tone).
 */
export interface Refusal { title: string; body: string; detail?: string; failure?: boolean; status?: number }

const REFUSALS: Record<string, Refusal> = {
  DENY_NO_CAPABILITY: {
    title: 'Your advocate role has not been ratified yet',
    body: 'Your account exists, but the Patient Advocate role on it has not been put into effect. Ratification is a deliberate human step with a named approver. Ask an administrator.',
    detail: 'Decision: DENY_NO_CAPABILITY',
  },
  EMERGE_PHI_ACCESS_DENIED: {
    title: 'This workspace is not open to your account yet',
    body: 'During the pilot, access to eMerge healthcare screens is limited to a named list. Ask an administrator to add you.',
    detail: 'Decision: EMERGE_PHI_ACCESS_DENIED',
  },
};
const GENERIC: Refusal = { title: 'You do not have access to this', body: 'The server declined this request. If you believe that is wrong, ask an administrator to check your role assignment.' };
/** The request never reached the server (apiRequest: no status and no code), or threw before it could. */
export const UNREACHED: Refusal = { title: 'Server not reached', body: 'Could not reach the server, so nothing here is current.', failure: true };
const OFFLINE = UNREACHED;

/**
 * A failure, told apart from a refusal by the HTTP status (2026-10 fix: every
 * non-success used to read as "You do not have access to this", so a 500 or a
 * dropped connection looked like a permissions problem). Null when the answer
 * is a refusal or a server sentence the caller should show.
 */
export function failureFor(r: { status?: number; code?: string | null }): Refusal | null {
  if (r.status === undefined && !r.code) return UNREACHED;
  if (typeof r.status === 'number' && r.status >= 500) {
    return { title: 'Server error', body: 'The server failed while loading this. This is not a permissions problem.', failure: true, status: r.status };
  }
  return null;
}

/**
 * Classify a read that did not succeed. A named gate (the server's codes
 * arrive AUTHZ_-prefixed; the D-04 gate's does not) keeps its own words; then
 * the status decides: unreached or 5xx is a failure, 401/403 is the generic
 * refusal, anything else is the server's own sentence.
 */
export function refusalFor(code: string | undefined | null, status?: number, message?: string): Refusal {
  const known = REFUSALS[String(code || '').replace(/^AUTHZ_/, '')];
  if (known) return known;
  const failed = failureFor({ status, code });
  if (failed) return failed;
  if (status === 401 || status === 403) return GENERIC;
  return { title: 'Not loaded', body: message || 'The server answered without what this screen needs.', failure: true, status };
}

/**
 * A refusal or a failure: the shared Tide StateBlock carrying this workspace's
 * own heading, words and detail. A failure is danger with Try again; a refusal
 * is the withheld tone. Neither is ever an empty list.
 */
export function StateBlock({ refusal, retry }: { refusal: Refusal; retry?: () => void }) {
  return (
    <div className="pa-state">
      <TideState kind="error" heading={refusal.title} headingLevel={2} message={refusal.body} detail={refusal.detail}
        status={refusal.status} tone={refusal.failure ? 'danger' : 'withheld'} onRetry={retry} />
    </div>
  );
}

/**
 * Loading: the Tide StateBlock's static skeleton rows (no shimmer loop), with
 * `role="status"`, no aria-busy and the sentence for assistive technology
 * only. Of two skeletons side by side, only the first speaks.
 */
export function Skeleton({ announce = true }: { announce?: boolean }) {
  return (
    <section className="pa-card">
      <TideState kind="loading" shape="table" label="Loading advocate work…" busy={false} srOnlyLabel announce={announce} />
    </section>
  );
}

/**
 * An empty list as Tide says it: `none` = the queue holds nothing ("Nothing
 * waiting" for a work queue); `filtered` = a lens, filter or page excludes everything.
 */
function Empty({ reason = 'none', queue = false, children }: { reason?: EmptyReason; queue?: boolean; children: React.ReactNode }) {
  return <TideState kind="empty" reason={reason} kindLabel={queue && reason === 'none' ? 'Nothing waiting' : undefined} message={children} />;
}

/** EP-004P §42–43, §73: what each queue is, what puts a case in it, the primary act, what takes it out. Short, no architecture. */
const QUEUE_HELP: Record<View, { line: string; what: string; qualifies: string; act: string; leaves: string }> = {
  work: { line: 'Cases assigned to you and ready for your attention.', what: 'Your own cases, attention first.', qualifies: 'A case assigned to you that is still open.', act: 'Open the first case, read Current work, do the next action, record the outcome.', leaves: 'When you record a closing outcome, or a manager reassigns it.' },
  'follow-ups': { line: 'Open actions that are due or waiting on you.', what: 'The obligations on your cases, soonest due first.', qualifies: 'An open action you own: a follow-up date, a vetting call, a responsible-party contact.', act: 'Do the action, then record the outcome on the case.', leaves: 'When the action is done, or the case moves to a word that no longer needs it.' },
  returned: { line: 'Cases another Patient Advocate or UMED sent back to you, with the reason.', what: 'Work returned to you with a signed send-back: who returned it, when, and why.', qualifies: 'A send-back addressed to you whose task is still open.', act: 'Open the record, read the reason, do the work, then mark it handled on the case.', leaves: 'When you mark it handled on the case.' },
  sales: { line: 'Sales you closed in the selected month, including leads assigned in an earlier month.', what: 'A close-month view: every sale credited to you whose close date falls in the month shown, in America/New_York.', qualifies: 'A case that reached Ready to submit (or the board recorded a Date Closed) in that month, credited to whoever held it at that instant.', act: 'Read it. A prior-month assignment closed this month is this month\'s sale and is marked as such. Amounts come from the board until a native figure exists.', leaves: 'Nothing leaves: a sale stays in the month it closed. A reversal is marked, never erased.' },
  history: { line: 'Every case you have ever been assigned, whether or not you hold it now.', what: 'Your assignment history: current and former cases, closed and open, sold and not.', qualifies: 'Any case you held at any time, from the assignment record.', act: 'Search by resident, facility or CounselEAR ID; open a case to read its story. A former case is read-only: ask a manager to reassign it if you need to work it.', leaves: 'Nothing leaves. Reassignment changes your relationship to Former, not whether you can find it.' },
  all: { line: 'Search the PA cases you are authorized to view.', what: 'Every open case with its holder, through the same lenses as My work.', qualifies: 'Any open case in your authorized scope.', act: 'Search, open, review; reassign from the case.', leaves: 'When the case closes.' },
  'monday-moves': { line: 'Moves Monday made between advocates before 5 Oct that need a manager’s decision.', what: 'The exceptions from the reconciliation of every Monday-driven PA move (18 Sep to the 5 Oct kill). Consistent moves and moves already settled in eMerge were kept as they are and are not here.', qualifies: 'A move whose new advocate does not cover the building, that overrode work or a placement made in eMerge, whose Monday user or resident is in doubt, or that otherwise looks wrong.', act: 'Read the evidence. Accept keeps whoever holds the case now; Reassign places it with the right advocate. Either decision is final and Monday can never move it again.', leaves: 'When you Accept or Reassign it.' },
  residents: { line: 'Bring a person into eMerge, or settle who is who.', what: 'The door a resident enters through without the board, and the identity questions the resolver would not answer alone.', qualifies: 'A person met at a building or found in CounselEAR who is not in eMerge; a review where the same SSN or CounselEAR id points two ways.', act: 'Enter what you know; eMerge checks whether the person exists before creating them. A manager answers reviews with a note.', leaves: 'A created resident goes where any resident goes: tags from CounselEAR, then intake when the TWL tag appears.' },
};

function QueueHelp({ view }: { view: View }) {
  const [open, setOpen] = useState(false);
  const h = QUEUE_HELP[view];
  return (
    <div className="pa-help">
      <p className="pa-help-line">{h.line} <button type="button" className="pa-link" aria-expanded={open} onClick={() => setOpen(!open)}>{open ? 'Hide' : 'What do I do here?'}</button></p>
      {open && (
        <dl className="pa-kv" aria-label="About this queue">
          <dt>What this is</dt><dd>{h.what}</dd>
          <dt>What puts a case here</dt><dd>{h.qualifies}</dd>
          <dt>What to do</dt><dd>{h.act}</dd>
          <dt>What takes it out</dt><dd>{h.leaves}</dd>
        </dl>
      )}
    </div>
  );
}

/** Below this width the open case panel is a full-screen sheet (paDock.css), and so a modal dialog. */
export const SHEET_QUERY = '(max-width: 900px)';
/** Whether a media query matches now, kept current; false where the browser cannot say (no matchMedia). */
export function useMediaMatch(query: string): boolean {
  const read = () => {
    try { return typeof window.matchMedia === 'function' && window.matchMedia(query).matches; } catch { return false; }
  };
  const [m, setM] = useState(read);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    let mq: MediaQueryList;
    try { mq = window.matchMedia(query); } catch { return undefined; }
    const on = () => setM(Boolean(mq.matches));
    on();
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', on); else if (typeof mq.addListener === 'function') mq.addListener(on);
    return () => {
      if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', on); else if (typeof mq.removeListener === 'function') mq.removeListener(on);
    };
  }, [query]);
  return m;
}

/** Past due is a stop; blocked and a provider's follow-up request are attention; old is a fact, said plainly. */
const tone = (a: Attention) => (a.code === 'ACTION_PAST_DUE' ? 'stop' : a.code === 'BLOCKED' || a.code === 'PROVIDER_FOLLOW_UP' ? 'attn' : a.code === 'CONTACT_ATTEMPT' ? 'touch' : '');

/**
 * Remind PA (2026-10-27, docs/provider/REMIND_PA.md): what providers asked about this resident. The open requests in
 * full (who, when, their note); the closed ones as a count. A request is answered when a Patient Advocate records a
 * note or a contact touch on the case (rulings 2026-10-27: nothing else answers it), and expires after its days run
 * out, when the PA managers are told.
 */
export function ProviderFollowUps({ items }: { items?: ProviderReminder[] }) {
  const all = Array.isArray(items) ? items : [];
  if (!all.length) return null;
  const open = all.filter((r) => r.open);
  const closed = all.length - open.length;
  const expired = all.filter((r) => r.status === 'EXPIRED' || r.closed_because === 'EXPIRED').length;
  return (
    <div className="pa-section pa-provider-follow-up" aria-label="Provider follow-up requests">
      <h3>{open.length ? 'A provider asked for a follow-up' : 'Provider follow-up requests'}</h3>
      {open.length > 0 && (
        <ul className="pa-list">
          {open.map((r) => (
            <li key={r.id}>
              <strong>{r.provider_name}</strong> asked on {shortDateTime(r.created_at)}
              {r.note ? <>: <q>{r.note}</q></> : <span className="pa-text none"> (no note)</span>}
              <span className="pa-text none"> · answered when you record a note or a contact touch; otherwise it goes to the PA managers {shortDateTime(r.expires_at)}</span>
            </li>
          ))}
        </ul>
      )}
      {closed > 0 && (
        <p className="pa-text none">
          {closed} earlier request{closed === 1 ? '' : 's'}: {closed - expired} answered, {expired} expired unanswered.
        </p>
      )}
    </div>
  );
}

/**
 * Round 2 (Isaiah Cotto, 2026-10-27: "We want 3 touches per attempted contact"): log each try to reach the resident or
 * their POA with what happened, picked from the server's one list (codes stored, labels shown), and an optional short
 * note. The current attempt reads "Touch N of 3"; "Call - Not interested" completes the attempt and changes nothing
 * else -- the status is still yours to set. A touch also answers any provider's follow-up request.
 */
export function ContactTouches({ caseId, attempt, touches = [], dispositions = [], canWrite, onDone }: {
  caseId: string; attempt?: ContactAttempt | null; touches?: ContactTouch[]; dispositions?: TouchDisposition[];
  canWrite: boolean; onDone: (msg: string) => Promise<void> | void;
}) {
  const whyTouchId = useWhyId('why-touch');
  const [code, setCode] = React.useState('');
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  if (!dispositions.length) return null;
  const a = attempt || null;
  const next = !a || a.complete ? 1 : a.touches + 1;
  const of = (a && a.of) || 3;
  const current = a && !a.complete ? `Touch ${a.touches} of ${of}${a.last_label ? ` · last: ${a.last_label}` : ''}` : null;
  const save = async () => {
    if (!code || busy) return;
    setBusy(true);
    setError(null);
    const r = await advocateApi.recordTouch(caseId, { disposition: code, note: note.trim() || undefined, idempotency_key: freshKey() });
    setBusy(false);
    if (r.success && r.data) {
      setCode('');
      setNote('');
      await onDone(r.data.complete ? `Touch ${r.data.touch} of ${r.data.of} recorded. This contact attempt is complete.` : `Touch ${r.data.touch} of ${r.data.of} recorded.`);
    } else setError(r.message || r.error || 'The touch was not recorded.');
  };
  return (
    <div className="pa-section pa-touches" aria-label="Contact touches">
      <h3>Contact touches</h3>
      <p className="pa-text">{current || (a && a.complete ? `Contact attempt ${a.attempt} is complete${a.conclusive ? ` (${a.last_label})` : ''}.` : 'No touch recorded yet.')} Three touches per attempted contact.</p>
      {canWrite && (
        <div className="pa-form pa-touch-form">
          <label>
            <span>Touch {next} of {of}: what happened</span>
            <select value={code} onChange={(e) => setCode(e.target.value)} disabled={busy} aria-label="What happened">
              <option value="">Choose…</option>
              {dispositions.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
            </select>
          </label>
          <label>
            <span>Note (optional)</span>
            <input type="text" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} disabled={busy} aria-label="Touch note" />
          </label>
          <div className="btns"><button type="button" className="pa-open pa-primary" onClick={save} disabled={!code || busy} aria-describedby={!code ? whyTouchId : undefined}>{busy ? 'Recording…' : 'Record touch'}</button><Why id={whyTouchId} reason={!code && 'Choose what happened first.'} /></div>
          {error && <p role="alert" className="pa-text pa-touch-error">{error}</p>}
        </div>
      )}
      {touches.length > 0 && (
        <ul className="pa-list">
          {touches.map((t) => (
            <li key={t.id}>Touch {t.touch} of {t.of}: <strong>{t.label}</strong> · {shortDateTime(t.at)}{t.by_name ? ` · ${t.by_name}` : ''}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AttentionTags({ items }: { items: Attention[] }) {
  if (items.length === 0) return null;
  return (
    <span className="pa-tags">
      {items.map((a, i) => (
        <span key={`${a.code}-${i}`} className={`pa-tag ${tone(a)}`}
          title={a.detail || (a.code === 'BLOCKED' ? undefined : ATTENTION_LABEL[a.code])}>
          {a.code === 'BLOCKED' ? BLOCKER_LABEL[a.detail as keyof typeof BLOCKER_LABEL] || a.detail
            : a.code === 'CONTACT_ATTEMPT' ? (a.detail || '').replace(/ \(last:.*\)$/, '') || ATTENTION_LABEL[a.code]
              : ATTENTION_LABEL[a.code]}
        </span>
      ))}
    </span>
  );
}

/** Blockers that come from the board's status word; a payor flag beside it does not change the state. */
const STATUS_BLOCKERS = new Set(['CANNOT_REACH_POA', 'MEDICAID_PENDING', 'PENDING_DOCUMENTATION', 'UNABLE_TO_VET', 'FAMILY_UNDECIDED', 'REVERSAL']);

/** The state in the business's words: Needs vetting, Vetted, or the decision, or what happened to the person. */
export function stateLine(c: Pick<WorkCase, 'stage' | 'disposition' | 'lifecycle' | 'blockers'>): string {
  if (c.lifecycle) return LIFECYCLE_LABEL[c.lifecycle];
  if (c.disposition) return DISPOSITION_LABEL[c.disposition];
  if (c.stage === 'VETTING' && !c.blockers.some((b) => STATUS_BLOCKERS.has(b))) return 'Needs vetting';
  if (c.stage === 'VETTED' && c.blockers.includes('PENDING_DOCUMENTATION')) return 'Self managed — documents needed';
  return STAGE_LABEL[c.stage];
}

function dueWords(a: OpenAction | null): { text: string; stop: boolean } {
  if (!a) return { text: 'Nothing due', stop: false };
  if (!a.due_on) return { text: 'Nothing due', stop: false };
  const d = daysFromToday(a.due_on);
  return { text: `${shortDate(a.due_on)} · ${relativeDay(a.due_on)}`, stop: d !== null && d < 0 };
}

/**
 * The due date in a building's table: the date on one line and its age on the
 * next, each kept whole, instead of the narrow column breaking "Sep 29, 2026 ·
 * 6 days ago" into three lines. The words read the same to a screen reader.
 */
function DueWords({ due }: { due: { text: string; stop: boolean } }) {
  const [date, age] = due.text.split(' · ');
  return (
    <span className={`pa-due${due.stop ? ' pa-tag stop' : ''}`}>
      <span className="pa-due-part">{date}</span>
      {age && <><span className="pa-sr-only"> · </span><span className="pa-due-part">{age}</span></>}
    </span>
  );
}

/* ── Search and paging ─────────────────────────────────────────────────── */

/**
 * Take the list away (2026-09-21). April's team triages two thousand leads in
 * a spreadsheet and sends facilities their own residents; until now the only
 * way out of this screen was copying a paged table by hand. The file is the
 * rows the screen is showing — same lens, same search — decided by the server.
 */
export function ExportButton({ what, run }: { what: string; run: () => Promise<string | null> }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="pa-export">
      <button type="button" className="pa-open" disabled={busy} onClick={async () => {
        setBusy(true); setErr(null);
        try { setErr(await run()); } catch { setErr('Could not download the file.'); } finally { setBusy(false); }
      }}>{busy ? 'Preparing…' : `Export ${what}`}</button>
      {err && <span className="pa-notice stop" role="alert">{err}</span>}
    </div>
  );
}

export function SearchBar({ value, onSearch, placeholder }: { value: string; onSearch: (q: string) => void; placeholder: string }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  return (
    <form className="pa-search" role="search" onSubmit={(e) => { e.preventDefault(); onSearch(draft.trim()); }}>
      <input type="search" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={placeholder} aria-label={placeholder} />
      <button type="submit">Search</button>
      {value && <button type="button" className="pa-clear" onClick={() => { setDraft(''); onSearch(''); }}>Clear</button>}
    </form>
  );
}

/**
 * The saved lenses: the frequent five as chips, the whole canonical
 * vocabulary under More, grouped (addendum §7). Counts are the viewed
 * advocate's (My work); All cases shows none. One control, one row, no
 * page per Monday tab — and a filter is not a setter: choosing "Enrolled"
 * here shows the cases so recorded and changes nothing.
 */
function LensBar({ lens, counts, onLens, allCases = false }: { lens: Lens; counts?: Partial<Record<Lens, number>>; onLens: (l: Lens) => void; allCases?: boolean }) {
  // Closed (R8) is offered on All cases only, last among the chips: it is where finished work is reviewed, not worked.
  const offered = LENSES.filter((l) => allCases || !l.allCasesOnly);
  const primary = offered.filter((l) => l.primary);
  const more = offered.filter((l) => !l.primary);
  const inMore = more.some((l) => l.key === lens);
  const n = (k: Lens) => (counts && counts[k] !== undefined ? <span className="pa-count">{counts[k]}</span> : null);
  const named = (l: typeof LENSES[number]) => `${l.label}${counts && counts[l.key] !== undefined ? ` · ${counts[l.key]}` : ''}`;
  return (
    <div className="pa-lenses" role="group" aria-label="View">
      {primary.map((l) => (
        <button key={l.key} type="button" className={`pa-lens${lens === l.key ? ' on' : ''}`} aria-pressed={lens === l.key} title={l.what} onClick={() => onLens(l.key)}>
          {l.chip || l.label}{n(l.key)}
        </button>
      ))}
      <label className={`pa-lens-more${inMore ? ' on' : ''}`}>
        <span className="pa-sr-only">More views</span>
        <select value={inMore ? lens : ''} onChange={(e) => { if (isLens(e.target.value)) onLens(e.target.value); }} aria-label="More views">
          <option value="" disabled>More…</option>
          {LENS_GROUPS.map((g) => {
            const items = more.filter((l) => l.group === g.key);
            return items.length === 0 ? null : (
              <optgroup key={g.key} label={g.label}>
                {items.map((l) => <option key={l.key} value={l.key} title={l.what}>{named(l)}</option>)}
              </optgroup>
            );
          })}
        </select>
      </label>
    </div>
  );
}

/**
 * The filter bar under the chips (My work, Team cases): plain selects, each
 * with a visible label, so nothing here needs a library or a dialog.
 *
 * It narrows the list and writes nothing. The chips' counts are lens-only --
 * they do not follow these filters -- so the count that does follow them is
 * said next to the bar, in words.
 */
export function FilterBar({ kind, value, onChange, onClear, facilities, legend, total, error, notice, closed }: {
  kind: 'work' | 'all'; value: Refine; onChange: (k: keyof Refine, v: string) => void; onClear: () => void;
  facilities: Array<{ id: string; name: string; count?: number }>; legend: StatusLegendEntry[]; total: number | null; error: string | null;
  notice?: string | null; closed?: boolean;
}) {
  const keys = (closed ? ['facility'] : REFINE_KEYS[kind]) as readonly string[];
  const has = (k: keyof Refine) => keys.includes(k);
  const anySet = keys.some((k) => Boolean(value[k as keyof Refine]));
  const narrowed = keys.some((k) => k !== 'sort' && Boolean(value[k as keyof Refine]));
  // The chosen building stays an option even when the page's own list no longer names it.
  const buildings = value.facility && !facilities.some((f) => f.id === value.facility) ? [...facilities, { id: value.facility, name: 'Selected building' }] : facilities;
  const field = (k: keyof Refine, label: string, anyLabel: string, options: ReadonlyArray<{ value: string; label: string }>) => (
    <div className="pa-filter" key={k}>
      <label htmlFor={`pf-${k}`}>{label}</label>
      <select id={`pf-${k}`} value={value[k]} onChange={(e) => onChange(k, e.target.value)}>
        <option value="">{anyLabel}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
  return (
    <div className="pa-filterbar" role="group" aria-label="Filter and sort">
      {has('facility') && field('facility', 'Facility', 'All buildings', buildings.map((f) => ({ value: f.id, label: f.count === undefined ? f.name : `${f.name} · ${f.count}` })))}
      {has('status') && field('status', 'Status set in eMerge', 'Any', legend.map((l) => ({ value: l.word, label: l.label })))}
      {has('fr') && field('fr', 'Financial responsibility', 'Any', FR_FILTERS)}
      {has('liability') && field('liability', 'Patient Liability', 'Any', LIABILITY_FILTERS)}
      {has('docs') && field('docs', 'Documents', 'Any', DOCS_FILTERS)}
      {has('sort') && field('sort', 'Sort', 'Priority (default)', SORTS[kind])}
      {anySet && <button type="button" className="pa-clear" onClick={onClear}>Clear filters</button>}
      {narrowed && total !== null && (
        <p className="pa-filter-count" role="status">
          <strong>{total} resident{total === 1 ? '' : 's'} match{total === 1 ? 'es' : ''}</strong> these filters. The numbers on the chips above do not change with them.
        </p>
      )}
      {has('status') && value.status && <p className="pa-filter-count">Matches only cases whose status was set in eMerge.</p>}
      {notice && <p className="pa-notice pa-filter-error" role="status">{notice}</p>}
      {error && <p className="pa-notice stop pa-filter-error" role="alert">{error}</p>}
    </div>
  );
}

/**
 * Said only when it matters, and only about eMerge's own update (2026-10-12):
 * Monday's retired sync never makes a workspace "not current".
 */
function FreshnessNote({ counts }: { counts: Counts }) {
  const text = notCurrentText(counts);
  if (!text) return null;
  return <div className="pa-note pa-fresh" role="status"><span><strong>Not current.</strong> {text}</span></div>;
}

/**
 * `pages` and `range`, when the server sends them, are the truth (2026-10-05):
 * pages break between buildings, so a page can hold a few more or fewer than
 * `limit` and the sums below would name the wrong residents. Without them --
 * an older server, or a list that is not grouped -- the sums stand.
 */
export function Pager({ page, limit, total, onPage, pages, range }: {
  page: number; limit: number; total: number; onPage: (p: number) => void;
  pages?: number; range?: { from: number; to: number };
}) {
  const last = pages !== undefined ? Math.max(1, pages) : Math.max(1, Math.ceil(total / limit));
  if (last <= 1 && page === 1) return null;
  const from = range ? range.from : total === 0 ? 0 : (page - 1) * limit + 1;
  const to = range ? range.to : Math.min(page * limit, total);
  return (
    <div className="pa-pager">
      <span className="num">{from}–{to} of {total}{pages !== undefined && pages > 1 ? <span className="pa-pager-pages"> · page {page} of {pages}</span> : null}</span>
      <div className="btns">
        <button type="button" onClick={() => onPage(page - 1)} disabled={page <= 1}>Previous</button>
        <button type="button" onClick={() => onPage(page + 1)} disabled={page >= last}>Next</button>
      </div>
    </div>
  );
}

/* ── Rows ──────────────────────────────────────────────────────────────── */

function CaseRow({ c, selected, onOpen, showAssignee }: { c: WorkCase; selected: string | null; onOpen: (id: string) => void; showAssignee?: boolean }) {
  const due = dueWords(c.next_action);
  return (
    <li className={`pa-row${selected === c.id ? ' on' : ''}`}>
      <div className="who">
        <strong>{personName(c)}</strong>
        <span><BuildingOrClinic row={c} fallback="Facility not recorded" />{showAssignee ? ` · ${c.assignee_name || 'nobody'}` : ''}</span>
      </div>
      <div className="state">
        <strong>{stateLine(c)}</strong>
        <AttentionTags items={c.attention} />
      </div>
      <div className="next">
        <strong>{c.next_action ? ACTION_LABEL[c.next_action.action_type] : 'No open action'}</strong>
        <span className={due.stop ? 'pa-tag stop' : ''}>{due.text}</span>
      </div>
      {/* data-case-open: closing the panel (or its dialog below 901px) puts focus back here. No aria-pressed
          (fix round 9): the name already says Open or Close, and a pressed state would say it twice. */}
      <button type="button" className="pa-open" onClick={() => onOpen(c.id)} data-case-open={c.id}
        aria-label={`${selected === c.id ? 'Close' : 'Open'} ${personName(c)}`}>
        {selected === c.id ? 'Close' : 'Open'}
      </button>
    </li>
  );
}

const lensWhy = (lens: Lens, fallback: string) => (lens === 'active' ? fallback : (LENSES.find((l) => l.key === lens)?.what || fallback));

/* ── EP-010: the facility-grouped operating table ──────────────────────── */

/**
 * The PA's day, as a table rather than a card list with a permanently open
 * detail pane.
 *
 * The complaint this answers, in the business's words: "the long component
 * with the patient details is too unorganized and creates a lot of
 * unnecessary navigation." So every fact a PA touches all day is ON the row —
 * status, Patient Liability (editable in place), the latest note, the next
 * action, what evidence exists, the sale — and the detail drawer is for the
 * things they touch occasionally.
 *
 * Residents are grouped by building because that is how the work is actually
 * done: one call to a facility settles four residents. The groups are ordered
 * by their most urgent resident, not alphabetically, so the building that
 * needs the PA first is first. Groups collapse; the state is per-facility and
 * per-view, held in the component rather than the URL, because which buildings
 * a PA has folded away is not something to share in a link.
 */

const cents = (c: number | null | undefined) => (c === null || c === undefined ? null : usd.format(c / 100));

/** The liability, its verification state and what it can carry — three facts, one cell. */
function LiabilityCell({ c, canEdit, onEdit }: { c: WorkCase; canEdit: boolean; onEdit: (c: WorkCase) => void }) {
  const l = c.liability;
  const body = l
    ? (
      <>
        <strong className="pa-money">{cents(l.amount_cents)}</strong>
        <span className={`pa-liab-class${l.classification === 'TOO_LOW' ? ' stop' : l.classification === 'ONE_EAR_ONLY' ? ' attn' : ''}`}>
          {LIABILITY_CLASS_LABEL[l.classification]}
        </span>
        {l.verification_status === 'VERIFIED' && <span className="pa-tag ok">Verified</span>}
        {l.verification_status === 'CORRECTION_PENDING' && <span className="pa-tag attn">Correction pending</span>}
      </>
    )
    : <span className="pa-text none">Not recorded</span>;
  if (!canEdit) return <div className="pa-liab">{body}</div>;
  return (
    <div className="pa-liab">
      {body}
      <button type="button" className="pa-inline-edit" onClick={() => onEdit(c)} aria-label={`${l ? 'Change' : 'Enter'} the Patient Liability for ${personName(c)}`}>
        {l ? 'Change' : 'Enter'}
      </button>
    </div>
  );
}

/**
 * Patient Liability, entered where the PA is working the lead.
 *
 * Dollars in, cents out: the server's contract is cents, and a float is never
 * sent. `$1,619` and `1619` and `1,619.00` all mean the same thing to someone
 * reading a facility statement aloud, so all three are accepted.
 */
function parseDollars(raw: string): number | null {
  const s = String(raw).replace(/[$,\s]/g, '');
  if (!s || !/^\d+(\.\d{0,2})?$/.test(s)) return null;
  return Math.round(Number(s) * 100);
}

/**
 * Who manages the resident's funds.
 *
 * The six types are April's, and the words are the board's, so an advocate
 * moving between systems reads the same thing in both. ACH on File is a
 * checkbox beside them, never a seventh type -- it is payment evidence.
 *
 * Written to eMerge's own record, not to the board's column: that one is
 * MONDAY-owned and the write would be refused and then overwritten.
 */
function FinRespEditor({ c, onClose, onSaved }: { c: WorkCase; onClose: () => void; onSaved: (msg: string) => void | Promise<void> }) {
  const current = c.financial_responsibility;
  const [type, setType] = useState<FinancialResponsibilityType | ''>(current?.type || '');
  const [ach, setAch] = useState(Boolean(current?.ach_on_file));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!type) { setErr('Choose who manages the funds.'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await advocateApi.recordFinancialResponsibility(c.id, { type, achOnFile: ach, note: note.trim() || null });
      if (r.success) await onSaved(`Recorded: ${FINANCIAL_RESPONSIBILITY_LABEL[type] || type}.`);
      else setErr(r.error || r.message || 'That was not recorded.');
    } catch { setErr('eMerge could not be reached. Nothing was recorded.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="pa-editor">
      <h3>Who manages the funds</h3>
      <p className="pa-sub">Who pays the facility, and so who pays us.</p>
      <div className="pa-choices" role="group" aria-label="Who manages the funds">
        {(Object.keys(FINANCIAL_RESPONSIBILITY_LABEL) as FinancialResponsibilityType[]).map((k) => (
          <button key={k} type="button" className={type === k ? 'on' : ''} aria-pressed={type === k}
            disabled={busy} onClick={() => setType(k)}>
            {FINANCIAL_RESPONSIBILITY_LABEL[k]}
          </button>
        ))}
      </div>
      <label className="pa-check">
        <input type="checkbox" checked={ach} disabled={busy} onChange={(e) => setAch(e.target.checked)} />
        ACH on file
      </label>
      <label className="pa-field">
        <span>What you were told (optional)</span>
        <input type="text" value={note} disabled={busy} maxLength={2000}
          onChange={(e) => setNote(e.target.value)} placeholder="Kept as you write it" />
      </label>
      {err && <p className="pa-error" role="alert">{err}</p>}
      <div className="pa-editor-acts">
        <button type="button" className="pa-go" disabled={busy || !type} onClick={save}>{busy ? 'Recording…' : 'Record'}</button>
        <button type="button" className="pa-go ghost" disabled={busy} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

function LiabilityEditor({ c, onClose, onSaved }: { c: WorkCase; onClose: () => void; onSaved: (msg: string) => Promise<void> }) {
  const whyLiabilityId = useWhyId('why-liability');
  const [raw, setRaw] = useState(c.liability ? (c.liability.amount_cents / 100).toFixed(2) : '');
  const [when, setWhen] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const parsed = parseDollars(raw);
  const verified = c.liability?.verification_status === 'VERIFIED';
  return (
    <form className="pa-liab-editor" aria-label={`Patient Liability for ${personName(c)}`} onSubmit={async (e) => {
      e.preventDefault();
      if (parsed === null) { setErr('Enter a dollar amount, for example 1619 or 1,619.00.'); return; }
      setBusy(true); setErr(null);
      try {
        const r = await advocateApi.recordLiability(c.id, { amount_cents: parsed, effective_from: when || null, reason: reason.trim() || null, idempotency_key: freshKey() });
        if (!r.success || !r.data) { setErr(errText(r)); return; }
        onClose();
        await onSaved(!r.data.changed ? 'The liability already read that.' : r.data.correctionPending
          ? 'Recorded for Finance to settle: a verified figure already exists and this one disagrees with it.'
          : `Patient Liability recorded: ${usd.format(parsed / 100)}.`);
      } catch { setErr('Could not reach the server. Nothing was changed.'); } finally { setBusy(false); }
    }}>
      {verified && (
        <p className="pa-text none">
          Finance has verified {cents(c.liability!.amount_cents)}. A different figure here does not replace it —
          it is recorded beside it for Finance to settle.
        </p>
      )}
      <label><span>Monthly Patient Liability</span>
        {/* eslint-disable-next-line jsx-a11y/no-autofocus -- the editor opens on a deliberate click, and the field is its only purpose. */}
        <input inputMode="decimal" value={raw} autoFocus onChange={(e) => { setRaw(e.target.value); setErr(null); }} placeholder="1,619.00" aria-label="Monthly Patient Liability in dollars" />
      </label>
      <label><span>Effective from (blank = today)</span><input type="date" value={when} onChange={(e) => setWhen(e.target.value)} aria-label="Liability effective date" /></label>
      <label><span>Where it came from</span><input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} placeholder="BOM confirmed on the vetting call" aria-label="Liability reason" /></label>
      {parsed !== null && <p className="pa-text none">Recorded as {usd.format(parsed / 100)} per month, stated by you and not yet verified.</p>}
      <div className="btns">
        <button type="submit" className="pa-open pa-primary" disabled={busy || parsed === null} aria-describedby={parsed === null ? whyLiabilityId : undefined}>{busy ? 'Recording…' : 'Record'}</button>
        <Why id={whyLiabilityId} reason={parsed === null && 'Enter the monthly amount in dollars first.'} />
        <button type="button" className="pa-clear" onClick={onClose}>Cancel</button>
      </div>
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
    </form>
  );
}

/**
 * The latest note in one line, and the way into the resident's whole thread.
 *
 * 2026-10-05: this cell used to open a one-off note form under the row, and
 * the row then reloaded out of the view the PA was in, taking the form and
 * the case with it. Now both the preview and "Add note" open the resident's
 * Notes in the side panel -- every note on every case, the composer at the
 * bottom -- and the list does not move. Never a destructive edit: notes are
 * append-only.
 */
function NoteCell({ c, canAdd, onOpenNotes }: { c: WorkCase; canAdd: boolean; onOpenNotes: (c: WorkCase, compose: boolean) => void }) {
  const n = c.latest_note;
  return (
    <div className="pa-notecell">
      {n
        ? (
          <>
            <button type="button" className="pa-note-preview pa-note-open" title={n.preview} onClick={() => onOpenNotes(c, false)}
              aria-label={`Notes for ${personName(c)}, latest: ${n.preview}`}>
              {n.preview}{n.truncated ? '…' : ''}
            </button>
            <span className="pa-note-by">{n.kind === 'VETTING' ? 'Vetting' : 'Note'} · {shortDate(n.recorded_at)}{n.recorded_by_name ? ` · ${n.recorded_by_name}` : ''}</span>
          </>
        )
        : <span className="pa-text none">No notes yet</span>}
      {canAdd && <button type="button" className="pa-inline-edit" onClick={() => onOpenNotes(c, true)} aria-label={`Add a note for ${personName(c)}`}>Add note</button>}
    </div>
  );
}

/**
 * Changing the status from the row.
 *
 * Which words are offered is the SERVER's answer, not a list this screen
 * keeps: `allowed_next_words` depends on the case's stage, and the whole
 * point of Talhia's complaint is that a vetted lead must be able to go back
 * to vetting. So the editor fetches the case when it opens — one request, on
 * a click the PA already made — and hands the same `StatusForm` the drawer
 * uses the real answer. Guessing the list here is how a screen starts
 * offering a transition the server will refuse.
 */
function StatusEditor({ c, onClose, onSaved }: { c: WorkCase; onClose: () => void; onSaved: (msg: string) => Promise<void> }) {
  const [detail, setDetail] = useState<CaseDetail | null>(null);
  const [err, setErr] = useState<Refusal | null>(null);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await advocateApi.case(c.id);
        if (!live) return;
        if (r.success && r.data) setDetail(r.data);
        else setErr(r.code === 'NOT_FOUND'
          ? { title: 'No such case', body: 'This case does not exist or was closed and removed from the queue.' }
          : failureFor(r) || { title: 'This case is not in your authorized scope', body: 'Ask a PA manager to reassign it, which writes the authorization row.', detail: `Decision: ${r.code || 'DENIED'}` });
      } catch { if (live) setErr(OFFLINE); }
    })();
    return () => { live = false; };
  }, [c.id]);

  if (err) return <div className="pa-liab-editor"><StateBlock refusal={err} /><div className="btns"><button type="button" className="pa-open" onClick={onClose}>Close</button></div></div>;
  if (!detail) return <div className="pa-liab-editor"><Skeleton announce={false} /></div>;
  return (
    <div className="pa-liab-editor" aria-label={`Change the status for ${personName(c)}`}>
      <StatusForm detail={detail} onDone={async (msg, o) => { if (o?.stop) return; onClose(); await onSaved(msg); }} />
      <div className="btns"><button type="button" className="pa-open" onClick={onClose}>Cancel</button></div>
    </div>
  );
}

/**
 * Filing a document from the row, in place, the way the liability and the note
 * are filed. It wraps the same `UploadForm` the case drawer uses, so there is
 * one upload in this workspace and not two that can disagree.
 */
function DocumentEditor({ c, classes, onClose, onSaved, building, sharedClasses }: { c: WorkCase; classes: UploadClass[]; onClose: () => void; onSaved: (msg: string) => Promise<void>; building?: BuildingChoice; sharedClasses?: UploadClass[] }) {
  return (
    <div className="pa-liab-editor" aria-label={`Add a document for ${personName(c)}`}>
      <UploadForm caseId={c.id} resetKey={c.documents?.total ?? 0} classes={classes} building={building} sharedClasses={sharedClasses}
        onDone={async (msg, o) => { if (o?.stop) return; onClose(); await onSaved(msg); }} />
      <div className="btns"><button type="button" className="pa-open" onClick={onClose}>Cancel</button></div>
    </div>
  );
}

/**
 * Evidence as named facts, never a count. "3 documents" does not tell a PA
 * whether the audiogram is in, which is the only question this column is
 * asked. Each pip carries its own text, so nothing depends on colour.
 */
type DocSlot = Exclude<keyof DocumentState, 'total'>;
const DOC_SLOTS: ReadonlyArray<{ key: DocSlot; short: string; label: string }> = [
  { key: 'bom_vetting', short: 'B', label: 'BOM vetting' },
  { key: 'audiogram', short: 'A', label: 'Audiogram' },
  { key: 'umed_document', short: 'U', label: 'UMED' },
  { key: 'recording', short: 'R', label: 'Recording' },
];

/**
 * What a PA may file when the server has not said. The server's
 * `scope.document_classes` wins wherever it is present; this exists so the
 * row's upload still offers the real list if a scope load is in flight.
 */
const DEFAULT_DOC_CLASSES: UploadClass[] = ['BOM_VETTING', 'RECORDING', 'UMED', 'AUDIOGRAM', 'DCF', 'FACE_SHEET', 'ACH'];

/**
 * The status, in the board's colour, and changeable from the row.
 *
 * Talhia Zalvidea, 2026-09-23: "The status button went away as well" and "I
 * have to change a vetted lead back to 'needs vetting' but I can't."
 *
 * Both sentences are the same gap. The transition was never forbidden — the
 * server offers every working word from VETTED, `TWL - VETTING` among them —
 * but after EP-010 made this workspace a table the control sat inside the
 * case, under the Work tab, behind a collapsed section. Three clicks from a
 * row she was already looking at, which from where she sits is gone.
 *
 * The hue is Monday's, FILLED as Monday draws it (FilledStatusChip), the word
 * always on the chip. A word the legend does not carry is drawn plainly rather than given
 * somebody else's colour, so a new board spelling stays visible.
 */
function StatusCell({ c, legend, canChange, onChange }: {
  c: WorkCase; legend: StatusLegendEntry[]; canChange: boolean; onChange: (c: WorkCase) => void;
}) {
  // eMerge's own word wins over the board's: a PA who just set a status should
  // see what they set, not what Monday last said.
  const word = (c.status_word || c.source_status_raw || '').trim().toUpperCase();
  const rep = replacementTag(c);
  // A replacement with no status word of its own still gets the REPLACEMENT bar (80 of 82 open ones have none).
  const hit = word ? legend.find((l) => l.word === word) : (rep ? legend.find((l) => l.word === 'REPLACEMENT') : undefined);
  return (
    <div className="pa-statuscell">
      {hit
        ? <FilledStatusChip word={hit.word} hex={hit.color} label={stateLine(c)} className="pa-statuschip pa-statuschip--filled" />
        : <span className="pa-statuschip plain"><strong>{stateLine(c)}</strong></span>}
      {rep && <span className="pa-tag rep">{rep}</span>}
      <AttentionTags items={c.attention} />
      {canChange && <button type="button" className="pa-inline-edit" onClick={() => onChange(c)} aria-label={`Change the status for ${personName(c)}`}>Change status</button>}
    </div>
  );
}

/** The colour language, stated once and closed by default — the UMED board's shape. */
function StatusLegend({ legend }: { legend: StatusLegendEntry[] }) {
  if (!legend.length) return null;
  return (
    <details className="pa-legend">
      <summary>Show the status colours</summary>
      <ul className="pa-legend-list">
        {legend.map((l) => (
          <li key={l.word}>
            <span className="pa-swatch" style={{ background: l.color }} aria-hidden="true" />
            <span className="pa-legend-label">{l.label}</span>
            <span className="pa-legend-word">{l.word}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

function DocsCell({ c, canAdd, onAdd }: { c: WorkCase; canAdd: boolean; onAdd: (c: WorkCase) => void }) {
  const d = c.documents;
  const have = d ? DOC_SLOTS.filter((s) => d[s.key]).map((s) => s.label) : [];
  const missing = d ? DOC_SLOTS.filter((s) => !d[s.key]).map((s) => s.label) : [];
  return (
    <div className="pa-docscell">
      {d
        ? (
          <span className="pa-docs-pips" title={`${have.length ? `On file: ${have.join(', ')}. ` : ''}${missing.length ? `Not on file: ${missing.join(', ')}.` : ''}`}>
            {DOC_SLOTS.map((s) => (
              <span key={s.key} className={`pa-pip${d[s.key] ? ' on' : ''}`} aria-hidden="true">{s.short}</span>
            ))}
            {/* Two sentences, two elements: colour carries nothing here, and a
                screen reader should be able to hear either fact on its own. */}
            <span className="pa-sr-only">{have.length ? `On file: ${have.join(', ')}.` : 'No documents on file.'}</span>
            {missing.length > 0 && <span className="pa-sr-only">{`Not on file: ${missing.join(', ')}.`}</span>}
          </span>
        )
        : <span className="pa-text none">—</span>}
      {/*
        The way in. The upload has always worked, but after EP-010 made this
        workspace a table it lived two clicks away — open the case, find the
        Documents section — and these pips read as a status, not a control.
        A PA with a face sheet in her hand had nowhere on the row to put it.
        Same shape as the liability and the note: edited where it is read.
      */}
      {canAdd && <button type="button" className="pa-inline-edit" onClick={() => onAdd(c)} aria-label={`Add a document for ${personName(c)}`}>Add document</button>}
    </div>
  );
}

/**
 * Who manages the resident's funds, and so who pays us.
 *
 * Talhia Zalvidea, 2026-09-23, sending the board's Financial Responsibility
 * labels: "it can go where it says next action."
 *
 * Read-only, and the screen says so, because `patients.payor_source` is still
 * registered to MONDAY in `integration_field_authority` and no route writes
 * it. Four in five open cases carry nothing at all. Showing a blank as a
 * blank is the honest rendering; inventing a default would put a number on a
 * resident nobody decided.
 */
function FinRespCell({ c, canEdit, onEdit }: { c: WorkCase; canEdit?: boolean; onEdit?: (c: WorkCase) => void }) {
  const fr = c.financial_responsibility;
  // Talhia Zalvidea, 2026-09-24: "having who manages the funds is pretty
  // urgent." 31,320 residents have none, so the empty state is the common
  // one and it has to be the way in, not a dead end.
  const change = canEdit && onEdit
    ? <button type="button" className="pa-linkact" onClick={() => onEdit(c)}>{fr && (fr.type || fr.raw) ? 'Change' : 'Record'}</button>
    : null;
  if (!fr || (!fr.type && !fr.ach_on_file && !fr.raw)) {
    return <div className="pa-frcell"><span className="pa-text none">Not recorded</span>{change}</div>;
  }
  return (
    <div className="pa-frcell">
      {fr.type
        ? <span className="pa-tag">{FINANCIAL_RESPONSIBILITY_LABEL[fr.type] || fr.type}</span>
        : fr.raw && !fr.ach_on_file
          // A spelling the board used and the vocabulary does not hold. Shown
          // as it was written rather than dropped, so a new word is visible.
          ? <span className="pa-tag attn" title="The board used a word eMerge does not map.">{fr.raw}</span>
          : <span className="pa-text none">Not recorded</span>}
      {/* Payment evidence, never a seventh kind of responsible party. */}
      {fr.ach_on_file && <span className="pa-sub-line">ACH on file</span>}
      {/* When eMerge and the board disagree, both are shown. The advocate's
          record is what the row reads; the board's word is not hidden. */}
      {fr.source === 'EMERGE' && fr.board_disagrees && fr.board_type && (
        <span className="pa-sub-line" title="The board still says this. Your record is what eMerge shows.">
          Board says {FINANCIAL_RESPONSIBILITY_LABEL[fr.board_type] || fr.board_type}
        </span>
      )}
      {change}
    </div>
  );
}

/** The sale, if there is one: the amount, or why there is not one yet, and whether it enrolled. */
function SaleCell({ c }: { c: WorkCase }) {
  const s = c.sale;
  if (!s) return <span className="pa-text none">—</span>;
  return (
    <div className="pa-salecell">
      {s.amount === null
        ? <span className="pa-text none" title={s.amount_pending_reason === 'PRICING_RULE_REQUIRED' ? 'No approved price catalog is in effect, so no amount could be derived.' : undefined}>Amount pending</span>
        : <strong className="pa-money">{money(s.amount)}</strong>}
      <span className="pa-sale-sub">
        {s.product ? s.product.label : 'Configuration not recorded'}
        {s.term && s.term.status === 'RESOLVED' ? ` · ${s.term.months} mo` : ''}
      </span>
      {s.reversed_at
        ? <span className="pa-tag stop">Reversed</span>
        : s.enrollment_state === 'ENROLLED'
          ? <span className="pa-tag ok">Enrolled</span>
          : s.enrollment_state === 'NOT_ENROLLED_FINAL'
            ? <span className="pa-tag stop">Did not enrol</span>
            : <span className="pa-tag">{ENROLLMENT_LABEL[s.enrollment_state]}</span>}
    </div>
  );
}

/** What the operating table edits in place. A note is written in the resident's thread, in the side panel. */
type RowEdit = 'liability' | 'document' | 'status' | 'finresp';

/**
 * How wide the operating table is. The editor row and the open case both span
 * it, and a hard-coded 9 in two places is how a new column silently leaves a
 * gap at the end of those rows.
 */
const PA_COLUMNS = 11;

interface RowActs {
  canLiability: boolean;
  canFinResp: boolean;
  canNote: boolean;
  /** Whether this caller may file a document: capability AND the `documents` write group. */
  canDoc: boolean;
  /** Whether this caller may move the case to another status word. */
  canStatus: boolean;
  /** One governed colour per word, from the server. Empty means draw every chip plainly. */
  legend: StatusLegendEntry[];
  /** What the server says may be filed. Never guessed on the client. */
  docClasses: UploadClass[];
  /** 2026-10-15: what the server says may be filed for a whole building at once (none from an older server). */
  sharedClasses: UploadClass[];
  editing: { id: string; what: RowEdit } | null;
  onEdit: (c: WorkCase, what: RowEdit) => void;
  onCloseEdit: () => void;
  /** A row act landed. `caseId` (fix round 10): the case it was about, so a refusal waiting for it retires. */
  onSaved: (msg: string, caseId?: string) => Promise<void>;
  /**
   * Open the resident's Notes in the side panel (2026-10-05); `compose` puts
   * the cursor in the composer. The case opens in the DOCKED panel beside the
   * table (Isaiah Cotto, 2026-10-05: "I do not want some disgustingly long
   * dropdown that is super difficult to navigate"), which replaced the case
   * unfolding under its row (2026-09-22). The panel is drawn from the URL, not
   * from the row, so a list that reloads without the row -- a status that
   * moved the resident to another view -- never takes the open case with it.
   */
  onOpenNotes: (c: WorkCase, compose: boolean) => void;
}

/**
 * The same resident's other open lead rows.
 *
 * Isaiah Cotto, 2026-09-30: "The PAs are seeing duplicates." Talhia's Groves
 * Center list showed Helen Haynes, Karen Uhlig and Oscar Valentine twice each
 * and Jennifer Underwood three times -- every one of them HER OWN row, so it
 * was noise in her list rather than a fight over who holds the resident.
 *
 * The server now sends one row per resident. This says what it stood in for,
 * because a duplicate that vanishes with no trace is how a PA stops trusting
 * the list -- and the records are real: 202 residents hold 469 open rows, and
 * only 8 of those 202 have exactly one row carrying the work, so nothing can
 * responsibly be merged yet.
 */
function DuplicateRecords({ others }: { others?: OtherOpenRecord[] }) {
  if (!others || !others.length) return null;
  const where = others.map((o) => `${STAGE_LABEL[o.stage as keyof typeof STAGE_LABEL] || o.stage}${o.source_system ? ` (${o.source_system === 'EMERGE' ? 'eMerge' : 'the board'})` : ''}`).join(' · ');
  return (
    <span className="pa-dupe" title={`Also open for this resident: ${where}. Shown once until the records are merged.`}>
      {others.length === 1 ? '1 more record' : `${others.length} more records`} for this resident
    </span>
  );
}

/** When the lead was placed with the PA: the date, and how long ago, so fresh work is the first thing a row says. */
function AssignedCell({ c }: { c: Pick<WorkCase, 'assigned_on'> }) {
  if (!c.assigned_on) return <span className="pa-text none">Date unknown</span>;
  return <><span>{shortDate(c.assigned_on)}</span><span className="pa-sub-line">{relativeDay(c.assigned_on)}</span></>;
}

function TableRow({ c, selected, onOpen, showAssignee, acts, group }: { c: WorkCase; selected: string | null; onOpen: (id: string) => void; showAssignee?: boolean; acts: RowActs; group?: FacilityGroup }) {
  const due = dueWords(c.next_action);
  const open = acts.editing && acts.editing.id === c.id ? acts.editing.what : null;
  return (
    <>
      <tr className={`pa-oprow${selected === c.id ? ' on' : ''}`}
        /* The record is the control, same as the UMED board. A click that
           landed on a cell's own control — the liability editor, Add note —
           belongs to that control: a PA entering a number is not asking to
           open the case as well. */
        onClick={(e) => {
          if ((e.target as HTMLElement).closest('button, a, input, select, textarea')) return;
          onOpen(c.id);
        }}>
        <th scope="row" className="pa-col-who">
          <strong>{personName(c)}</strong>
          {showAssignee && <span className="pa-sub-line">{c.assignee_name || 'nobody'}</span>}
          <DuplicateRecords others={c.other_open_records} />
        </th>
        <td className="pa-col-state" data-label="Status">
          <StatusCell c={c} legend={acts.legend} canChange={acts.canStatus && !isClosedLead(c)} onChange={(x) => acts.onEdit(x, 'status')} />
        </td>
        <td className="pa-col-assigned" data-label="Date assigned"><AssignedCell c={c} /></td>
        <td className="pa-col-liab" data-label="Patient Liability"><LiabilityCell c={c} canEdit={acts.canLiability} onEdit={(x) => acts.onEdit(x, 'liability')} /></td>
        <td className="pa-col-note" data-label="Latest note"><NoteCell c={c} canAdd={acts.canNote} onOpenNotes={acts.onOpenNotes} /></td>
        <td className="pa-col-finresp" data-label="Financial Responsibility"><FinRespCell c={c} canEdit={acts.canFinResp} onEdit={(x) => acts.onEdit(x, 'finresp')} /></td>
        <td className="pa-col-next" data-label="Next action">{c.next_action ? ACTION_LABEL[c.next_action.action_type] : <span className="pa-text none">No open action</span>}</td>
        <td className="pa-col-due" data-label="Due"><DueWords due={due} /></td>
        <td className="pa-col-docs" data-label="Docs"><DocsCell c={c} canAdd={acts.canDoc} onAdd={(x) => acts.onEdit(x, 'document')} /></td>
        <td className="pa-col-sale" data-label="Sale"><SaleCell c={c} /></td>
        <td className="pa-col-open">
          <button type="button" className="pa-open" aria-pressed={selected === c.id} onClick={() => onOpen(c.id)} data-case-open={c.id}
            aria-label={`${selected === c.id ? 'Close' : 'Open'} ${personName(c)}`}>
            {selected === c.id ? 'Close' : 'Open'}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="pa-editrow">
          <td colSpan={PA_COLUMNS}>
            {open === 'liability' ? <LiabilityEditor c={c} onClose={acts.onCloseEdit} onSaved={(msg: string) => acts.onSaved(msg, c.id)} />
              : open === 'finresp' ? <FinRespEditor c={c} onClose={acts.onCloseEdit} onSaved={(msg: string) => acts.onSaved(msg, c.id)} />
              : open === 'status' ? <StatusEditor c={c} onClose={acts.onCloseEdit} onSaved={(msg: string) => acts.onSaved(msg, c.id)} />
              : <DocumentEditor c={c} classes={acts.docClasses} building={buildingOf(group)} sharedClasses={acts.sharedClasses} onClose={acts.onCloseEdit} onSaved={(msg: string) => acts.onSaved(msg, c.id)} />}
          </td>
        </tr>
      )}
    </>
  );
}

/** The building's initials, for the header's tile: "Seabranch Health and Rehabilitation Center" is "SH". */
function facilityMonogram(name: string): string {
  const words = name.replace(/[^A-Za-z0-9 ]/g, ' ').split(/\s+/).filter((w) => w && !/^(and|of|at|the|for|on|by|in)$/i.test(w));
  return (words.slice(0, 2).map((w) => w[0]).join('') || '?').toUpperCase();
}

/**
 * The building's number, formatted, a tap to call it and a click to copy it
 * (the PA team, 2026-10-05: the raw digits sat on a line of their own).
 * Outside the collapse button, so neither folds the group.
 */
function FacilityPhone({ name, raw }: { name: string; raw: string }) {
  const phone = formatFacilityPhone(raw);
  const [copied, setCopied] = useState(false);
  if (!phone) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(phone.display);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch { /* No clipboard (an old browser, a denied permission): the number is on screen to select. */ }
  };
  return (
    <span className="pa-facphone-wrap">
      <a className="pa-facphone" href={`tel:${phone.tel}`} title={`Call ${name}`} aria-label={`Call ${name}, ${phone.display}`}>
        <svg aria-hidden="true" className="pa-facphone-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" />
        </svg>{phone.display}
      </a>
      <button type="button" className="pa-copy" onClick={copy} aria-label={`Copy ${name}'s number`}>
        {copied ? 'Copied' : 'Copy'}
      </button>
      <span className="pa-sr-only" role="status">{copied ? `${phone.display} copied` : ''}</span>
    </span>
  );
}

/** One building. Collapsible, with a header that says which building, how to call it, and how much work is in it. */
function FacilityGroupTable({ g, selected, onOpen, showAssignee, acts, collapsed, onToggle }: {
  g: FacilityGroup; selected: string | null; onOpen: (id: string) => void; showAssignee?: boolean; acts: RowActs;
  collapsed: boolean; onToggle: () => void;
}) {
  const key = g.facility_id || '__none__';
  // Display only: a name typed in capitals reads in title case; the building's own record is never rewritten.
  const name = g.facility_id ? facilityDisplayName(g.facility_name) : g.facility_name;
  // 2026-10-15: one vetting call or recording for the building's residents in this list.
  const building = buildingOf(g);
  const shareable = acts.docClasses.filter((x) => acts.sharedClasses.includes(x));
  const canFileBuilding = acts.canDoc && Boolean(building) && shareable.length > 0;
  const [filing, setFiling] = useState(false);
  return (
    <section className={`pa-fgroup${g.facility_id ? '' : ' none'}`} aria-label={name}>
      {/* Only the fold and the name are the heading; the number and the counts sit beside it, so the heading reads as the building's name. */}
      <div className="pa-fgroup-head">
        <h3 className="pa-fgroup-h">
          <button type="button" aria-expanded={!collapsed} aria-controls={`fg-${key}`} onClick={onToggle}>
            <span className="pa-fgroup-caret" aria-hidden="true">{collapsed ? '▸' : '▾'}</span>
            <span className="pa-fgroup-tile" aria-hidden="true">{g.facility_id ? facilityMonogram(name) : '?'}</span>
            <span className="pa-fgroup-title">
              <span className="pa-fgroup-name" title={name}>{name}</span>
              {(g.name_shared || g.continued) && (
                <span className="pa-fgroup-where">
                  {g.name_shared && g.facility_city ? g.facility_city : g.name_shared ? 'city not recorded' : ''}
                  {g.facility_record_hint ? ` · ${g.facility_record_hint}` : ''}
                  {g.continued ? `${g.name_shared ? ' · ' : ''}continued from the previous page` : ''}
                </span>
              )}
            </span>
          </button>
        </h3>
        <div className="pa-fgroup-side">
          {canFileBuilding && (
            <button type="button" className="pa-inline-edit" aria-expanded={filing} onClick={() => setFiling((v) => !v)}
              aria-label={`File one recording or BOM vetting for the residents of ${name} in this list`}>File for this building</button>
          )}
          {g.facility_phone && <FacilityPhone name={name} raw={g.facility_phone} />}
          <span className="pa-fgroup-counts">
            <span className="pa-fgroup-n">{`${g.residents} resident${g.residents === 1 ? '' : 's'}`}</span>
            {g.attention > 0 && <span className="pa-tag attn">{g.attention} need{g.attention === 1 ? 's' : ''} attention</span>}
          </span>
        </div>
      </div>
      {filing && building && (
        <div className="pa-liab-editor pa-fgroup-file">
          <UploadForm caseId={building.residents[0].caseId} resetKey={0} classes={shareable} sharedClasses={acts.sharedClasses} building={building} onlyBuilding
            onDone={async (msg, o) => { if (o?.stop) return; setFiling(false); await acts.onSaved(msg); }} />
          <div className="btns"><button type="button" className="pa-open" onClick={() => setFiling(false)}>Cancel</button></div>
        </div>
      )}
      {!collapsed && (
        <div className="pa-tablewrap" id={`fg-${key}`}>
          <table className="pa-table pa-optable">
            <thead>
              <tr>
                <th scope="col" className="pa-col-who">Resident</th>
                <th scope="col" className="pa-col-state">Status</th>
                <th scope="col" className="pa-col-assigned">Date assigned</th>
                <th scope="col" className="pa-col-liab">Patient Liability</th>
                <th scope="col" className="pa-col-note">Latest note</th>
                <th scope="col" className="pa-col-finresp">Financial Responsibility</th>
                <th scope="col" className="pa-col-next">Next action</th>
                <th scope="col" className="pa-col-due">Due</th>
                <th scope="col" className="pa-col-docs" title="BOM vetting · Audiogram · UMED · Recording">Docs</th>
                <th scope="col" className="pa-col-sale">Sale</th>
                <th scope="col" className="pa-col-open"><span className="pa-sr-only">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {g.items.map((c) => <TableRow key={c.id} c={c} selected={selected} onOpen={onOpen} showAssignee={showAssignee} acts={acts} group={g} />)}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * The grouped table, or the plain rows when the server did not group (an
 * older backend, or a view that does not carry groups). Falling back rather
 * than rendering nothing: a PA whose server is one deploy behind should still
 * see their work.
 */
/**
 * Which buildings this viewer folded, remembered per view and lens (2026-10-05).
 * A per-viewer convenience, so browser storage: every read and write is
 * guarded, and a private window or blocked storage simply forgets.
 */
const COLLAPSE_KEY = (scopeKey: string) => `pa.fgroups.collapsed.v1:${scopeKey}`;
function readCollapsed(scopeKey: string): Set<string> {
  try {
    const raw = window.localStorage.getItem(COLLAPSE_KEY(scopeKey));
    const list = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(list) ? list.filter((x) => typeof x === 'string') : []);
  } catch { return new Set(); }
}
function writeCollapsed(scopeKey: string, keys: Set<string>) {
  try {
    if (keys.size) window.localStorage.setItem(COLLAPSE_KEY(scopeKey), JSON.stringify(Array.from(keys)));
    else window.localStorage.removeItem(COLLAPSE_KEY(scopeKey));
  } catch { /* storage refused: the fold lasts until the page is left */ }
}

function GroupedWork({ groups, items, selected, onOpen, showAssignee, acts, scopeKey = 'work' }: {
  groups: FacilityGroup[] | undefined; items: WorkCase[]; selected: string | null; onOpen: (id: string) => void; showAssignee?: boolean; acts: RowActs;
  /** view:lens, so a building folded on Needs vetting is not folded on Vetted. */
  scopeKey?: string;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => readCollapsed(scopeKey));
  const shown = useRef(scopeKey);
  if (shown.current !== scopeKey) { shown.current = scopeKey; setCollapsed(readCollapsed(scopeKey)); }
  const save = (next: Set<string>) => { writeCollapsed(scopeKey, next); return next; };
  const toggle = (k: string) => setCollapsed((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return save(n); });
  if (!groups) return <ul className="pa-rows">{items.map((c) => <CaseRow key={c.id} c={c} selected={selected} onOpen={onOpen} showAssignee={showAssignee} />)}</ul>;
  const keys = groups.map((g) => g.facility_id || '__none__');
  const allFolded = keys.length > 0 && keys.every((k) => collapsed.has(k));
  return (
    <div className="pa-fgroups">
      {groups.length > 1 && (
        <div className="pa-fgroups-bar">
          <span className="pa-fgroups-sum">{groups.length} buildings, A to Z</span>
          <button type="button" className="pa-inline-edit" onClick={() => setCollapsed((prev) => {
            const n = new Set(prev);
            keys.forEach((k) => (allFolded ? n.delete(k) : n.add(k)));
            return save(n);
          })}>{allFolded ? 'Expand all' : 'Collapse all'}</button>
        </div>
      )}
      {groups.map((g) => {
        const key = g.facility_id || '__none__';
        return (
          <FacilityGroupTable key={key} g={g} selected={selected} onOpen={onOpen} showAssignee={showAssignee} acts={acts}
            collapsed={collapsed.has(key)} onToggle={() => toggle(key)} />
        );
      })}
    </div>
  );
}

/** Whether the server says a filter (not the sort) narrowed this list: then an empty list is "no matches", not "nothing". */
const refined = (f?: WorkFilters): boolean => Boolean(f && Object.entries(f.applied || {}).some(([k, v]) => k !== 'sort' && Boolean(v)));

function WorkList({ data, selected, onOpen, onPage, whose, acts }: { data: WorkPage; selected: string | null; onOpen: (id: string) => void; onPage: (p: number) => void; whose: string; acts: RowActs }) {
  return (
    <>
      {data.awaiting_scope > 0 && (
        <div className="pa-note">
          <span>
            <strong>{data.awaiting_scope} of {whose} cases {data.awaiting_scope === 1 ? 'is' : 'are'} not in authorized scope yet.</strong>{' '}
            The case is assigned but no authorization row exists for it. Reassigning it in this workspace writes one; nothing here widens access otherwise.
          </span>
        </div>
      )}
      <section className="pa-card">
        <div className="pa-card-head">
          <div><h2>{whose === 'your' ? 'My work' : 'Their work'}</h2><p className="why">{lensWhy(data.lens, 'Attention first, then soonest due, then oldest. Residents at one building sit together.')}</p></div>
          <span className="pa-count">{data.total}</span>
        </div>
        {data.items.length === 0
          ? <Empty queue reason={data.total === 0 && data.lens === 'active' && !refined(data.filters) ? 'none' : 'filtered'}>{data.total === 0 ? (data.lens === 'active' ? 'Nothing here needs work right now.' : 'Nothing assigned here matches this view.') : 'Nothing on this page.'}</Empty>
          : <GroupedWork groups={data.groups} items={data.items} selected={selected} onOpen={onOpen} acts={acts} scopeKey={`work:${data.lens}`} />}
        <Pager page={data.page} limit={data.limit} total={data.total} onPage={onPage} pages={data.pages} range={data.range} />
      </section>
    </>
  );
}

/** What DCF has and has not given back, in the business's words. */
function evidenceLine(f: FollowUp): string {
  const noca = f.noca_status === 'RECEIVED' ? 'NOCA received' : f.noca_status === 'REJECTED' ? 'NOCA rejected' : 'no NOCA yet';
  const flmmis = f.flmmis_status === 'NOT_REFLECTED' ? 'not in FLMMIS' : f.flmmis_status === 'EXCEPTION' ? 'FLMMIS exception' : 'FLMMIS unknown';
  return `${noca} · ${flmmis}`;
}

/**
 * Follow-ups is the UMED approval chase (EP-007). April Torino, 2026-09-17:
 * only residents whose UMED was submitted and is not approved 21 days later.
 * Longest wait first, because that is the one being forgotten.
 */
function FollowUpList({ data, selected, onOpen, onPage }: { data: FollowUpPage; selected: string | null; onOpen: (id: string) => void; onPage: (p: number) => void }) {
  const cadence = data.cadence_days ?? 21;
  return (
    <section className="pa-card">
      <div className="pa-card-head">
        <div>
          <h2>Follow-ups</h2>
          <p className="why">Residents whose UMED has been with DCF {cadence} days or more without an approval reaching FLMMIS. Longest wait first.</p>
        </div>
        <span className="pa-count">{data.total}</span>
      </div>
      {data.items.length === 0
        ? <Empty queue>No resident is waiting {cadence} days or more on a UMED approval.</Empty>
        : (
          <ul className="pa-rows">
            {data.items.map((f) => (
              <li key={f.umed_case_id} className={`pa-row${selected === f.id ? ' on' : ''}`}>
                <div className="who"><strong>{personName(f)}</strong><span><BuildingOrClinic row={f} fallback="Facility not recorded" /></span></div>
                <div className="state">
                  <strong>{f.days_waiting} days with DCF</strong>
                  <span>{evidenceLine(f)}</span>
                </div>
                <div className="next">
                  <strong className={f.overdue_by > 0 ? 'pa-tag stop' : ''}>Sent {shortDate(f.went_to_dcf_on)}</strong>
                  <span>
                    {f.board_approved_on
                      ? `board says approved ${shortDate(f.board_approved_on)} — not yet in FLMMIS`
                      : `${cadence}-day follow-up due`}
                  </span>
                </div>
                <button type="button" className="pa-open" aria-pressed={selected === f.id} onClick={() => onOpen(f.id)}>Open</button>
              </li>
            ))}
          </ul>
        )}
      <Pager page={data.page} limit={data.limit} total={data.total} onPage={onPage} />
    </section>
  );
}

/**
 * "No open cases match" was a dead end: it is true of this queue and says
 * nothing about the person. Two residents of one name were on file — one
 * whose case nobody had taken, one whose case closed in 2025 and who owns
 * hearing aids — and the screen reported neither (packet §17).
 */
function ResidentFallback({ q }: { q: string }) {
  const [state, setState] = useState<{ loading: boolean; failed: boolean; items: ResidentHit[]; total: number }>({ loading: false, failed: false, items: [], total: 0 });
  useEffect(() => {
    let live = true;
    if (!q || q.trim().length < 2) { setState({ loading: false, failed: false, items: [], total: 0 }); return () => { live = false; }; }
    setState((s) => ({ ...s, loading: true, failed: false }));
    advocateApi.searchResidents({ q }).then((r) => {
      if (!live) return;
      // A failed lookup is not an answer about a person. Saying "eMerge holds
      // no resident by that name" when the request 500'd is the worst shape a
      // failure can take on this screen: it reads as a fact about the
      // resident. 2026-09-21: it did exactly that.
      const d = r.success ? r.data : null;
      if (!d) { setState({ loading: false, failed: true, items: [], total: 0 }); return; }
      setState({ loading: false, failed: false, items: d.items, total: d.total });
    }).catch(() => { if (live) setState({ loading: false, failed: true, items: [], total: 0 }); });
    return () => { live = false; };
  }, [q]);

  if (!q) return <p className="pa-empty">No open cases are active right now.</p>;
  if (state.loading) return <p className="pa-empty">No open cases match. Looking for the resident…</p>;
  if (state.failed) return <p className="pa-notice stop">No open cases match. The resident lookup failed, so this says nothing about whether eMerge holds them — try again, and tell an administrator if it keeps failing.</p>;
  if (state.total === 0) return <p className="pa-empty">No open cases match, and eMerge holds no resident by that name.</p>;
  return (
    <div className="pa-fallback">
      <p className="pa-empty">
        No open case matches, but eMerge holds {state.total === 1 ? 'this resident' : `${state.total} residents`} by that name.
      </p>
      <ul className="pa-rows">
        {state.items.map((r) => (
          <li key={r.id} className="pa-row">
            <div className="who">
              <strong>{r.name || (r.counselear_id ? `CounselEAR ${r.counselear_id}` : 'No name on file')}</strong>
              {!r.name && <span className="pa-tag">eMerge holds no name for this resident</span>}
              <span><BuildingOrClinic row={r} fallback="Facility not recorded" />{r.counselear_id ? ` · CounselEAR ${r.counselear_id}` : ''}</span>
            </div>
            <div className="state">
              <strong>{r.situation_words}</strong>
              <span>
                {r.latest_case
                  ? r.latest_case.closed_on
                    ? `Last case closed ${shortDate(r.latest_case.closed_on)}`
                    : `Open since ${shortDate(r.latest_case.opened_on)}${r.latest_case.advocate ? ` · ${r.latest_case.advocate}` : ''}`
                  : 'No case has ever been opened'}
              </span>
            </div>
            <div className="flags">
              {r.service_history.devices && <span className="pa-tag">Has hearing aids</span>}
              {r.service_history.enrolled && <span className="pa-tag">Enrolled</span>}
              {r.situation === 'OPEN_UNASSIGNED' && <span className="pa-tag attn">Nobody assigned</span>}
            </div>
          </li>
        ))}
      </ul>
      <p className="why">Same name does not mean the same person: each row is its own record, with its own CounselEAR id.</p>
    </div>
  );
}

/**
 * All cases -> Closed (R8, 2026-10-01): finished work, reviewable. One line
 * per case, newest closure first: who, where, what it closed as, when, and
 * the reason given. No working columns -- there is no next action on a
 * closed case -- and Open still opens the whole record.
 */
function ClosedList({ data, selected, onOpen, onPage }: { data: AllCasesPage; selected: string | null; onOpen: (id: string) => void; onPage: (p: number) => void }) {
  return (
    <section className="pa-card">
      <div className="pa-card-head">
        <div>
          <h2>Closed cases</h2>
          <p className="why">Finished work, newest first. A disposition takes a case out of every working view; it stays here, in each advocate&apos;s History, and in resident search.</p>
        </div>
        <span className="pa-count">{data.total}</span>
      </div>
      {data.items.length === 0
        ? <Empty reason="filtered">No closed case matches.</Empty>
        : (
          <ul className="pa-rows" aria-label="Closed cases">
            {data.items.map((c) => {
              const word = (c.status_word || '').trim().toUpperCase() as StatusWord;
              const closedAs = STATUS_WORD_LABEL[word] || stateLine(c);
              return (
                <li key={c.id} className={`pa-row${selected === c.id ? ' on' : ''}`}>
                  <div className="who">
                    <strong>{personName(c)}</strong>
                    <span><BuildingOrClinic row={c} fallback="Facility not recorded" />{c.assignee_name ? ` · ${c.assignee_name}` : ''}</span>
                  </div>
                  <div className="state">
                    <strong>{closedAs}</strong>
                    <span>{c.closed_on ? `Closed ${shortDate(c.closed_on)}` : 'Closed'}</span>
                  </div>
                  <div className="next pa-closed-why">
                    {c.closing_reason ? <span>{c.closing_reason}</span> : <span className="pa-text none">No reason recorded</span>}
                  </div>
                  <div className="pa-actions">
                    <button type="button" className="pa-open" aria-pressed={selected === c.id} onClick={() => onOpen(c.id)}>Open</button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      <Pager page={data.page} limit={data.limit} total={data.total} onPage={onPage} />
    </section>
  );
}

function AllList({ data, lens, selected, onOpen, onPage, acts, q }: { data: AllCasesPage; lens: Lens; selected: string | null; onOpen: (id: string) => void; onPage: (p: number) => void; acts: RowActs; q: string }) {
  if (lens === 'closed') return <ClosedList data={data} selected={selected} onOpen={onOpen} onPage={onPage} />;
  return (
    <section className="pa-card">
      <div className="pa-card-head">
        <div><h2>Team cases</h2><p className="why">{lensWhy(lens, 'Every open case that is active, and who holds it — grouped by building.')}</p></div>
        <span className="pa-count">{data.total}</span>
      </div>
      {data.items.length === 0
        ? <ResidentFallback q={q} />
        : <GroupedWork groups={data.groups} items={data.items} selected={selected} onOpen={onOpen} showAssignee acts={acts} scopeKey={`all:${lens}`} />}
      <Pager page={data.page} limit={data.limit} total={data.total} onPage={onPage} pages={data.pages} range={data.range} />
    </section>
  );
}

export function AdvocateSelect({ advocates, value, onChange, allowNone, label }: { advocates: Advocate[]; value: string; onChange: (v: string) => void; allowNone?: boolean; label: string }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
      <option value="">{allowNone ? 'Nobody' : 'Choose an advocate…'}</option>
      {advocates.filter((a) => !a.manager || value === a.id).map((a) => (
        <option key={a.id} value={a.id}>{a.name}{a.ratified ? '' : ' (not yet ratified)'} · {a.open_cases}</option>
      ))}
    </select>
  );
}

/**
 * A tab reached by the keyboard in a strip that scrolls sideways (the header's tabs beside the laptop
 * side sheet, the panel's sections) comes WHOLLY into view (fix round 5): Chrome leaves a half-shown
 * one where it is, under the strip's end fade. The strips' scroll-padding keeps it clear of the fade.
 */
function revealInStrip(e: React.FocusEvent<HTMLElement>): void {
  const t = e.target as HTMLElement;
  const strip = e.currentTarget;
  if (t === strip || strip.scrollWidth <= strip.clientWidth || typeof t.scrollIntoView !== 'function') return;
  t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
}

/* ── Case context ──────────────────────────────────────────────────────── */

/*
 * The third pane, as sections on a grid (addendum §8): CURRENT WORK beside
 * RESIDENT, FINANCIAL beside DECISION-MAKER, EVIDENCE across, ENROLLMENT
 * beside UMED, FACILITY beside NOTES, then the STORY — meaningful events
 * only. The board's own word, the transfer note and the raw board dates are
 * reconciliation facts, not the work: they sit behind "Source details",
 * which the server sends only to a reviewer. The PA sees what a fact means,
 * not how engineering obtained it.
 */

function Text({ value }: { value: string | null | undefined }) {
  const v = (value || '').trim();
  return v ? <p className="pa-text">{v}</p> : <p className="pa-text none">Not recorded</p>;
}

const NotRecorded = () => <span className="pa-text none">Not recorded</span>;

/**
 * One titled section of the panel grid; `wide` spans the row. `fold` makes it a
 * collapsible <details> with the title as its visible summary (the Work tab's
 * reference cells); it stays a labelled region either way.
 */
function Cell({ title, wide, fold, children }: { title: string; wide?: boolean; fold?: { open: boolean }; children: React.ReactNode }) {
  if (fold) {
    return (
      <section className={`pa-cell pa-fold${wide ? ' wide' : ''}`} aria-label={title}>
        <details open={fold.open}>
          <summary><h3>{title}</h3></summary>
          <div className="pa-fold-body">{children}</div>
        </details>
      </section>
    );
  }
  return <section className={`pa-cell${wide ? ' wide' : ''}`} aria-label={title}><h3>{title}</h3>{children}</section>;
}

/** The next action, when it is due, what is in the way, and the other open obligations. */
function CurrentWork({ c, actions }: { c: CaseDetail['case']; actions: CaseAction[] }) {
  const next = dueWords(c.next_action);
  const others = actions.filter((a) => a.status === 'OPEN' && (!c.next_action || a.id !== c.next_action.id));
  return (
    <Cell title="Current work" wide>
      {c.next_action ? (
        <>
          <p className="pa-lead">{ACTION_LABEL[c.next_action.action_type]}</p>
          <p className={`pa-sub-line${next.stop ? ' stop' : ''}`}>{next.text}</p>
          {c.next_action.reason && <p className="pa-text none">{c.next_action.reason}</p>}
        </>
      ) : <p className="pa-lead none">Nothing open</p>}
      {c.blockers.length > 0 && <p className="pa-text"><strong>In the way:</strong> {c.blockers.map((b) => BLOCKER_LABEL[b]).join(', ')}</p>}
      {others.length > 0 && (
        <ul className="pa-actions" aria-label="Other open actions">
          {others.map((a) => {
            const d = daysFromToday(a.due_on);
            return (
              <li key={a.id}>
                <span>{ACTION_LABEL[a.action_type]}</span>
                <span className={`due${d !== null && d < 0 ? ' stop' : ''}`}>{a.due_on ? shortDate(a.due_on) : 'no date'}</span>
              </li>
            );
          })}
        </ul>
      )}
      <dl className="pa-kv">
        <dt>Last contact</dt><dd>{c.last_contact_on ? `${shortDate(c.last_contact_on)} · ${relativeDay(c.last_contact_on)}` : <NotRecorded />}</dd>
        <dt>Opened</dt><dd>{c.opened_on ? shortDate(c.opened_on) : <NotRecorded />}</dd>
        <dt>Assigned</dt><dd>{c.assigned_on ? `${shortDate(c.assigned_on)} · ${relativeDay(c.assigned_on)}` : <span className="pa-text none">Date unknown</span>}</dd>
      </dl>
    </Cell>
  );
}

/* ── PA write enablement (2026-09-14): the acts, each its own form ─────── */

/** A real 429, in words a person can act on: nothing changed, wait this long, the entry is still here. */
export function rateLimitedText(retryAfterSeconds?: number): string {
  const min = Math.max(1, Math.ceil((retryAfterSeconds && retryAfterSeconds > 0 ? retryAfterSeconds : 15 * 60) / 60));
  return `Nothing was changed. Too many requests from your account in the last few minutes. Wait about ${min} minute${min === 1 ? '' : 's'}, then press it again. Your entry is still here.`;
}

/**
 * What a panel form reports (fix round 8). A success is `onDone(msg)`. A write that is REFUSED is also
 * reported, as `onDone(msg, { stop: true })`, so that a refusal answered after the PA moved on to another
 * resident is not lost with the unmounted form: the shell parks it with its own case. The form keeps its
 * inline error, so the shell ignores a stop for the case still on screen.
 */
type PanelDone = (msg: string, opts?: { stop?: boolean }) => Promise<void>;

/** The words for what Ready to submit is missing (SUB 03/04: an SSN or a Medicaid number is enough, with the DOB). */
const readinessLabel = (m: string): string => (m === 'UMED_DOCUMENT' ? 'the UMED' : m === 'AUDIOGRAM' ? 'the audiogram' : m === 'SSN_OR_MEDICAID' ? 'an SSN or a Medicaid number' : m.toLowerCase());
/** Where the identifier is recorded, said where it is missing (Talhia Zalvidea, 2026-10-27: "I have Medicaid number not SSN"). */
const IDENTIFIER_NEEDED_WORDS = 'Record the SSN or the Medicaid number on the Identity tab; with the date of birth either one is enough to submit.';
export const errText = (r: { error?: string; message?: string; code?: string; detail?: unknown; status?: number; retryAfterSeconds?: number }): string => {
  // No status means no response. The act never reached the server, so nothing
  // was written — and this is the only signal for it, because a connection
  // failure has no body and therefore no code.
  if (r.status === undefined) return 'Could not reach the server. Nothing was changed.';
  // 2026-10-05: "Please slow down and try again later" said neither what happened nor when. The form keeps its entry.
  if (r.status === 429) return rateLimitedText(r.retryAfterSeconds);
  if (r.status >= 500) return 'The server failed while recording this. Nothing was changed, and it is not a permissions problem.';
  if (r.code === 'STALE') return 'This case changed since you opened it. Reload to see the current state.';
  // Ruling 11 (2026-10-07): a second Ready to submit while the resident's UMED is in flight is refused as a
  // duplicate. The server's sentence names the UMED's stage; this says what did NOT happen.
  // 20261029p: the rechargeable replacement is sold only on a replacement of both ears, and only through a UMED.
  if (r.code === 'PRODUCT_NOT_FOR_THIS_CASE') return `${r.message || r.error || 'That configuration cannot be sold on this case.'} Nothing was recorded.`;
  if (r.code === 'PRODUCT_UMED_ONLY') return 'The rechargeable replacement goes through a UMED, not private pay. Mark the case ready to submit instead. Nothing was recorded.';
  if (r.code === 'UMED_ALREADY_IN_PROGRESS') return `${r.message || r.error || 'This resident already has a UMED in progress. Talk to the UMED team before submitting again.'} Nothing was recorded: no sale and no second UMED.`;
  if (r.code === 'NOT_READY' && r.detail && typeof r.detail === 'object' && Array.isArray((r.detail as { missing?: string[] }).missing)) {
    const missing = (r.detail as { missing: string[] }).missing;
    return `Not ready: missing ${missing.map(readinessLabel).join(' and ')}.${missing.includes('SSN_OR_MEDICAID') ? ` ${IDENTIFIER_NEEDED_WORDS}` : ''}`;
  }
  // 2026-10-27 (Talhia Zalvidea, Ronald Rauen): the review draft's refusal said "resolve the required evidence" and
  // never which. Each blocker carries its own sentence (what is missing, and what to do); say every one.
  if (r.code === 'PA_DRAFT_BLOCKED') {
    const list = r.detail && typeof r.detail === 'object' ? (r.detail as { blockers?: unknown }).blockers : null;
    const said = Array.isArray(list)
      ? Array.from(new Set(list.map((b) => String((b && typeof b === 'object' && ((b as { message?: string }).message || (b as { code?: string }).code)) || '').trim()).filter(Boolean)))
      : [];
    if (said.length) return `The draft cannot be prepared. ${said.join(' ')}`;
  }
  return r.error || r.message || 'That did not go through.';
};

function StatusForm({ detail, onDone }: { detail: CaseDetail; onDone: PanelDone }) {
  const whyStatusId = useWhyId('why-status');
  const words = detail.allowed_next_words || [];
  const [word, setWord] = useState<StatusWord | ''>('');
  const [reason, setReason] = useState('');
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const refuse = (m: string) => { setErr(m); void onDone(m, { stop: true }); };
  const version = detail.native?.version ?? 1;
  const terminal = word !== '' && TERMINAL_STATUS_WORDS.includes(word);
  // R2 (2026-10-01): the server refuses these without a reason; the form asks first, in words.
  const needsReason = word !== '' && REASON_REQUIRED_WORDS.includes(word);
  const toProvider = word !== '' && PROVIDER_WORDS.includes(word);
  const reasonMissing = needsReason && !reason.trim();
  // An empty list is a real answer and it used to render as an empty dropdown,
  // which reads as "the screen is broken" rather than "this case cannot move
  // from here". Say which it is.
  if (!words.length) {
    return (
      <p className="pa-text none" role="status">
        There is no status this case can move to from <strong>{stateLine(detail.case)}</strong>.
        A manager can reopen it if that is wrong.
      </p>
    );
  }
  return (
    <form className="pa-form" aria-label="Change the case status" onSubmit={async (e) => {
      e.preventDefault(); if (!word || busy) return;
      if (reasonMissing) { setTried(true); return; }
      setBusy(true); setErr(null);
      try {
        const r = await advocateApi.changeStatus(detail.case.id, { status: word, reason: reason.trim() || undefined, effective_at: when || undefined, version, idempotency_key: freshKey() });
        if (!r.success || !r.data) { refuse(errText(r)); return; }
        setWord(''); setReason(''); setWhen(''); setTried(false);
        // The change follows the resident onto their other open records (2026-10-01), and says so.
        const carried = r.data.siblingsCarried?.length || 0;
        const left = r.data.siblingsNotCarried?.length || 0;
        const also = `${carried ? ` Also applied to ${carried === 1 ? 'the other record' : `the ${carried} other records`} for this resident.` : ''}${left ? ` ${left === 1 ? 'One other record' : `${left} other records`} for this resident could not take this status and ${left === 1 ? 'was' : 'were'} left as ${left === 1 ? 'it was' : 'they were'}.` : ''}`;
        const handback = r.data.resubmittal
          ? (r.data.resubmittal.created ? ' The resident is on Manifest Review\'s Resubmittals list for the provider.' : ' The resident was already on Manifest Review\'s Resubmittals list; nothing was added twice.')
          : r.data.verification
            ? (r.data.verification.created ? ' A VA has the resident on Manifest Review\'s Needs verification list; if they qualify, it is placed again with the PA who held it, or goes to Leads to place.' : ' A VA already had this resident on the Needs verification list; nothing was added twice.')
            : '';
        await onDone(r.data.replayed ? 'That change was already recorded.' : `${r.data.closed ? `Closed: ${STATUS_WORD_LABEL[r.data.word as StatusWord] || r.data.word}.` : `Now ${STATUS_WORD_LABEL[r.data.word as StatusWord] || r.data.word}.`}${also}${handback}`);
      } catch { refuse('Could not reach the server. Nothing was changed.'); } finally { setBusy(false); }
    }}>
      <label><span>Move to</span>
        <select value={word} onChange={(e) => setWord(e.target.value as StatusWord | '')} aria-label="New status">
          <option value="">Choose…</option>
          {words.map((w) => <option key={w} value={w}>{STATUS_WORD_LABEL[w] || w}</option>)}
        </select>
      </label>
      <label>
        <span>{needsReason ? 'Why (required, kept on the record)' : 'Why (kept on the record)'}</span>
        <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} aria-label="Status reason"
          aria-required={needsReason} aria-invalid={tried && reasonMissing ? true : undefined} aria-describedby={tried && reasonMissing ? `status-reason-missing-${detail.case.id}` : undefined} />
      </label>
      {tried && reasonMissing && <p id={`status-reason-missing-${detail.case.id}`} className="pa-notice stop" role="alert">Say why. {STATUS_WORD_LABEL[word as StatusWord] || word} is a decision about the resident, and the reason stays on the record.</p>}
      <label><span>Effective date (blank = today)</span><input type="date" value={when} onChange={(e) => setWhen(e.target.value)} aria-label="Status effective date" /></label>
      {terminal && (
        <p className="pa-text none">
          This closes the case with today&apos;s date (or the date above) as when it closed. It leaves your working views and stays findable in History and in resident search.
          {toProvider && ' The resident also goes on Manifest Review\'s Resubmittals list, once, for the provider to see at the next visit.'}
          {word === 'NEEDS VERIFICATION' && ' A VA checks the resident\'s benefits from Manifest Review\'s Needs verification list; if they qualify, the lead is placed again with the PA who held it, or goes to Leads to place.'}
        </p>
      )}
      <div className="btns"><button type="submit" className="pa-open pa-primary" disabled={!word} aria-disabled={busy || undefined} aria-describedby={!word ? whyStatusId : undefined}>{busy ? 'Recording…' : terminal ? 'Close the case' : 'Record status'}</button><Why id={whyStatusId} reason={!word && 'Choose a status first.'} /></div>
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
    </form>
  );
}

/**
 * D-31 (Isaiah Cotto 2026-10-02; April to confirm): a closed lead the resident
 * came back to. One act, "Reopen this lead", in place of every working act:
 * the case is closed, and nothing else can be recorded on it until it is not.
 * The same case reopens as a new holding interval; documents stay with the
 * resident.
 */
function defaultReopenWord(detail: CaseDetail): StatusWord {
  // Mirrors the server: the working word the case had before it closed, else Vetting with the facility.
  const closing = [...(detail.native?.transitions || [])].reverse().find((t) => t.to_stage === 'CLOSED' || t.to_stage === 'DISPOSITIONED');
  const from = closing?.from_word ? closing.from_word.trim().toUpperCase().replace(/\s*-\s*/g, '-').replace(/\s+/g, ' ') : '';
  return WORKING_STATUS_WORDS.find((w) => w.toUpperCase().replace(/\s*-\s*/g, '-').replace(/\s+/g, ' ') === from) || 'TWL - VETTING';
}

function ReopenLead({ detail, onDone, manager = false }: { detail: CaseDetail; onDone: PanelDone; manager?: boolean }) {
  const [reason, setReason] = useState('');
  const [word, setWord] = useState<StatusWord | ''>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<React.ReactNode>(null);
  const refuse = (m: string, shown?: React.ReactNode) => { setErr(shown ?? m); void onDone(m, { stop: true }); };
  const fallback = defaultReopenWord(detail);
  return (
    <form className="pa-form" aria-label="Reopen this lead" onSubmit={async (e) => {
      e.preventDefault();
      if (reason.trim().length < 3) { setErr('Say why you are reopening it (for example, that the resident asked to be resubmitted).'); return; }
      setBusy(true); setErr(null);
      try {
        const r = await advocateApi.reopenCase(detail.case.id, { reason: reason.trim(), word: word || undefined, idempotency_key: freshKey() });
        if (!r.success || !r.data) {
          if (r.code === 'ALREADY_OPEN') {
            const other = r.detail?.caseId;
            refuse('This resident already has an open case.', <>This resident already has an open case: {other ? <Link to={path({ caseId: other })}>open it instead</Link> : 'open it instead'}.</>);
          } else if (r.code === 'HELD_BY_VA') refuse("A VA is verifying this resident's benefits (Needs verification). The lead comes back to the Patient Advocate when they qualify.");
          else if (r.code === 'DUPLICATE') {
            const dup = r.detail?.duplicateOf;
            refuse('This case was closed as a duplicate of another record for the same resident. Work that one.', <>This case was closed as a duplicate of {dup ? <Link to={path({ caseId: dup })}>another record</Link> : 'another record'} for the same resident. Work that one.</>);
          } else refuse(errText(r));
          return;
        }
        const d = r.data;
        const at = STATUS_WORD_LABEL[d.word as StatusWord] || d.word;
        setReason(''); setWord('');
        await onDone(d.replayed ? 'That reopening was already recorded.'
          : d.placement === 'LEADS_TO_PLACE' ? `Reopened at ${at}. It is waiting in Leads to place.`
            : d.placement === 'PRIOR_HOLDER' ? `Reopened at ${at}. It is placed with the Patient Advocate who held it.`
              : d.placement === 'RESIDENT_PA' ? `Reopened at ${at}. It is placed with the Patient Advocate who holds this resident.`
              : `Reopened at ${at}. It is placed with you.`);
      } catch { refuse('Could not reach the server. Nothing was changed.'); } finally { setBusy(false); }
    }}>
      <p className="pa-text none">Reopens this case and takes it off Resubmittals if it was there. Documents already added stay with the resident.{manager && ' It goes to you if you hold it, otherwise back to the Patient Advocate who held it if they still hold the role, otherwise to the Patient Advocate who holds another open case of this resident, otherwise to Leads to place.'}</p>
      <label>
        <span>Why (required, kept on the record)</span>
        <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} aria-label="Reopen reason" aria-required />
      </label>
      <label><span>Reopen at</span>
        <select value={word} onChange={(e) => setWord(e.target.value as StatusWord | '')} aria-label="Reopen at">
          <option value="">{`Where it was: ${STATUS_WORD_LABEL[fallback]}`}</option>
          {WORKING_STATUS_WORDS.map((w) => <option key={w} value={w}>{STATUS_WORD_LABEL[w] || w}</option>)}
        </select>
      </label>
      <div className="btns"><button type="submit" className="pa-open pa-primary" disabled={busy}>{busy ? 'Reopening…' : 'Reopen this lead'}</button></div>
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
    </form>
  );
}

function ResponsiblePartyForm({ detail, onDone }: { detail: CaseDetail; onDone: PanelDone }) {
  const rp = detail.native?.responsible_party;
  const ev = detail.evidence;
  const [name, setName] = useState(rp?.name || ev?.responsible_party_name || '');
  const [phone, setPhone] = useState(rp?.phone || ev?.responsible_party_phone || '');
  const [email, setEmail] = useState(rp?.email || ev?.responsible_party_email || '');
  const [address, setAddress] = useState(rp?.address || ev?.responsible_party_address || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const refuse = (m: string) => { setErr(m); void onDone(m, { stop: true }); };
  return (
    <form className="pa-form" aria-label="Update the responsible party" onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setErr(null);
      try {
        const r = await advocateApi.responsibleParty(detail.case.id, { name: name.trim(), phone: phone.trim(), email: email.trim(), address: address.trim(), version: detail.native?.version ?? 1 });
        if (!r.success || !r.data) { refuse(errText(r)); return; }
        await onDone('Responsible party recorded.');
      } catch { refuse('Could not reach the server. Nothing was changed.'); } finally { setBusy(false); }
    }}>
      <label><span>Name</span><input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} aria-label="Responsible party name" /></label>
      <label><span>Phone</span><input value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} aria-label="Responsible party phone" /></label>
      <label><span>Email</span><input value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} aria-label="Responsible party email" /></label>
      <label><span>Address</span><input value={address} onChange={(e) => setAddress(e.target.value)} maxLength={400} aria-label="Responsible party address" /></label>
      <div className="btns"><button type="submit" className="pa-open pa-primary" disabled={busy}>{busy ? 'Recording…' : 'Record responsible party'}</button></div>
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
    </form>
  );
}

function DobForm({ detail, onDone }: { detail: CaseDetail; onDone: PanelDone }) {
  const whyDobId = useWhyId('why-dob');
  const [dob, setDob] = useState('');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const refuse = (m: string) => { setErr(m); void onDone(m, { stop: true }); };
  return (
    <form className="pa-form" aria-label="Correct the date of birth" onSubmit={async (e) => {
      e.preventDefault(); if (!confirm) { setConfirm(true); return; } setBusy(true); setErr(null);
      try {
        const r = await advocateApi.correctDob(detail.case.id, { dob, reason: reason.trim(), idempotency_key: freshKey() });
        if (!r.success || !r.data) { refuse(errText(r)); return; }
        setDob(''); setReason(''); setConfirm(false);
        await onDone(r.data.changed === false ? 'The date of birth already read that.' : 'Date of birth corrected; the prior value is kept on the record.');
      } catch { refuse('Could not reach the server. Nothing was changed.'); } finally { setBusy(false); }
    }}>
      <p className="pa-text none">On file: {detail.demographics?.dob ? shortDate(detail.demographics.dob) : 'not recorded'}</p>
      {/* Typed or pasted MM/DD/YYYY (2026-10-30): the browser's date control refused a paste. */}
      <label><span>Corrected date of birth</span><DobInput value={dob} onChange={(iso) => { setDob(iso); setConfirm(false); }} aria-label="Corrected date of birth" hintClassName="pa-text none" /></label>
      <label><span>Why</span><input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} aria-label="DOB reason" /></label>
      <div className="btns"><button type="submit" className="pa-open pa-primary" disabled={busy || !dob || !reason.trim()} aria-describedby={!dob || !reason.trim() ? whyDobId : undefined}>{busy ? 'Recording…' : confirm ? `Confirm: set DOB to ${dob}` : 'Correct date of birth'}</button><Why id={whyDobId} reason={firstReason([!dob, 'Enter the corrected date of birth first.'], [!reason.trim(), 'Say why first.'])} /></div>
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
    </form>
  );
}

function SsnForm({ detail, onDone }: { detail: CaseDetail; onDone: PanelDone }) {
  const whySsnId = useWhyId('why-ssn');
  const onFile = Boolean(detail.demographics?.ssn_on_file);
  const [ssn, setSsn] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const refuse = (m: string) => { setErr(m); void onDone(m, { stop: true }); };
  return (
    <form className="pa-form" aria-label="Record the SSN" autoComplete="off" onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setErr(null);
      try {
        const r = await advocateApi.setSsn(detail.case.id, { ssn, reason: reason.trim(), idempotency_key: freshKey() });
        setSsn('');
        if (!r.success || !r.data) { refuse(errText(r)); return; }
        setReason('');
        await onDone(r.data.state === 'REPLACED' ? 'SSN replaced; recorded encrypted.' : 'SSN recorded, encrypted. It is never shown here.');
      } catch { setSsn(''); refuse('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
    }}>
      <p className="pa-text none">{onFile ? 'An SSN is on file (encrypted). Recording one here replaces it.' : 'No SSN on file. It is encrypted on entry and never shown.'}</p>
      <label><span>SSN</span><input type="password" inputMode="numeric" value={ssn} onChange={(e) => setSsn(e.target.value)} maxLength={11} aria-label="SSN" autoComplete="off" /></label>
      <label><span>Why</span><input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} aria-label="SSN reason" /></label>
      <div className="btns"><button type="submit" className="pa-open pa-primary" disabled={busy || ssn.replace(/\D/g, '').length !== 9 || !reason.trim()} aria-describedby={ssn.replace(/\D/g, '').length !== 9 || !reason.trim() ? whySsnId : undefined}>{busy ? 'Recording…' : onFile ? 'Replace SSN' : 'Record SSN'}</button><Why id={whySsnId} reason={firstReason([ssn.replace(/\D/g, '').length !== 9, 'Enter all nine digits of the SSN first.'], [!reason.trim(), 'Say why first.'])} /></div>
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
    </form>
  );
}

/**
 * SUB 03/04 (Kaleigh Vazquez and Josie Evans, 2026-10-08): with the date of birth, an SSN OR a Medicaid number is enough
 * to submit. Talhia Zalvidea, 2026-10-27: "I have Medicaid number not SSN" -- the PA had nowhere to put it. The number
 * is sent once and never shown back: the screen knows only whether one is on file. One already on file is not replaced
 * from here (the server refuses MEDICAID_NUMBER_ON_FILE); the UMED team changes it on the UMED board.
 */
function MedicaidNumberForm({ detail, onDone }: { detail: CaseDetail; onDone: PanelDone }) {
  const whyMedicaidId = useWhyId('why-medicaid');
  const onFile = Boolean(detail.demographics?.medicaid_on_file);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const refuse = (m: string) => { setErr(m); void onDone(m, { stop: true }); };
  return (
    <form className="pa-form" aria-label="Record the Medicaid number" autoComplete="off" onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setErr(null);
      try {
        const r = await advocateApi.recordMedicaidNumber(detail.case.id, { medicaid_number: value.trim(), idempotency_key: freshKey() });
        if (!r.success || !r.data) { refuse(errText(r)); return; }
        setValue('');
        await onDone(r.data.changed ? 'Medicaid number recorded. With the date of birth it is enough to submit.' : 'That Medicaid number is already on file.');
      } catch { refuse('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
    }}>
      <p className="pa-text none">{onFile
        ? 'A Medicaid number is on file. It is not shown here; to change it, ask the UMED team.'
        : 'No Medicaid number on file. With the date of birth, a Medicaid number is enough to submit when there is no SSN.'}</p>
      <label><span>Medicaid number</span><input value={value} onChange={(e) => setValue(e.target.value)} maxLength={40} aria-label="Medicaid number" autoComplete="off" placeholder="CounselEAR: Employee ID" /></label>
      <div className="btns"><button type="submit" className="pa-open pa-primary" disabled={busy || !value.trim()} aria-describedby={!value.trim() ? whyMedicaidId : undefined}>{busy ? 'Recording…' : 'Record Medicaid number'}</button><Why id={whyMedicaidId} reason={!value.trim() && 'Enter the Medicaid number first.'} /></div>
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
    </form>
  );
}

/** How eMerge came to hold the resident at a building, in words. */
function facilityProvenance(rf: NonNullable<CaseDetail['resident_facility']>): string {
  if (rf.resolved_by_type === 'HUMAN' || rf.method === 'HUMAN_CONFIRMED') {
    return `Decided in eMerge${rf.decided_by_name ? ` by ${rf.decided_by_name}` : ''}${rf.resolved_at ? ` on ${shortDate(rf.resolved_at)}` : ''}`;
  }
  if (rf.method === 'SOURCE_AUTHORITATIVE' || rf.resolved_by_type === 'SOURCE') return 'Linked from Monday (migration); not yet confirmed by a person';
  if (rf.method === 'EXISTING_POINTER') return 'Carried over from the old record; not yet confirmed by a person';
  return 'Matched by address; not yet confirmed by a person';
}

/**
 * Correct the resident's building (the owner, 2026-10-05: "the ability, along
 * with managers, to be able to move a patient to the correct facility if they
 * are showing under the wrong facility in eMerge").
 *
 * One command for the RESIDENT, not this row: it confirms the building as a
 * person's decision, ends the wrong one, and moves every open case and UMED
 * with it. Monday never puts it back. A building is chosen from eMerge's own
 * list -- never typed -- and the reason is kept on the record.
 */
function CorrectFacilityForm({ detail, onDone }: { detail: CaseDetail; onDone: PanelDone }) {
  const whyFacilityId = useWhyId('why-facility');
  const rf = detail.resident_facility || null;
  const [kind, setKind] = useState<FacilityChangeKind>('CORRECTION');
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<{ id: string; name: string; city: string | null; state: string | null }[]>([]);
  const [hitsErr, setHitsErr] = useState<string | null>(null);
  const [to, setTo] = useState<{ id: string; name: string; city: string | null } | null>(null);
  const [reason, setReason] = useState('');
  const [when, setWhen] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const refuse = (m: string) => { setErr(m); void onDone(m, { stop: true }); };
  // One key per intended change: a retry after a timeout is the same change, never a second one.
  const [key, setKey] = useState(freshKey);
  useEffect(() => {
    if (q.trim().length < 2) { setHits([]); setHitsErr(null); return undefined; }
    let live = true;
    const t = window.setTimeout(async () => {
      const r = await advocateApi.facilities(q.trim());
      if (!live) return;
      if (r.success && r.data) { setHits(r.data.items); setHitsErr(null); return; }
      setHits([]); setHitsErr(errText(r));
    }, 180);
    return () => { live = false; window.clearTimeout(t); };
  }, [q]);
  const reset = () => { setTo(null); setQ(''); setReason(''); setWhen(''); setConfirm(false); setKey(freshKey()); };
  return (
    <form className="pa-form pa-correct" aria-label="Correct the facility" onSubmit={async (e) => {
      e.preventDefault();
      if (!to || reason.trim().length < 3) return;
      if (!confirm) { setConfirm(true); return; }
      setBusy(true); setErr(null);
      try {
        const r = await advocateApi.correctFacility(detail.case.id, {
          facility_id: to.id, kind, reason: reason.trim(), effective_at: kind === 'MOVE' && when ? when : undefined,
          version: detail.native?.version ?? 1, expected_relationship_id: rf ? rf.relationship_id : null, idempotency_key: key,
        });
        if (!r.success || !r.data) { refuse(r.code === 'ALREADY_THERE' ? (r.error || r.message || 'Already there.') : errText(r)); setConfirm(false); return; }
        const d = r.data;
        const label = facilityDisplayName(d.facilityName);
        const moved = `${d.casesMoved} open case${d.casesMoved === 1 ? '' : 's'}${d.umedCasesMoved ? ` and ${d.umedCasesMoved} UMED` : ''}`;
        const cover = d.coverage && d.coverage.holderMatches === false && d.coverage.facilityPaName
          ? ` ${label} is covered by ${d.coverage.facilityPaName}; a manager reassigns the resident if they should move too.` : '';
        reset();
        await onDone(d.replayed ? 'That correction was already recorded.' : `${kind === 'MOVE' ? 'Moved' : 'Corrected'} to ${label}. ${moved} now show${d.casesMoved === 1 && !d.umedCasesMoved ? 's' : ''} ${label}.${cover}`);
      } catch { refuse('Could not reach the server. Nothing was changed.'); } finally { setBusy(false); }
    }}>
      <div className="pa-seg pa-correct-kind" role="group" aria-label="What happened">
        <button type="button" aria-pressed={kind === 'CORRECTION'} onClick={() => { setKind('CORRECTION'); setConfirm(false); }}>Wrong building in eMerge</button>
        <button type="button" aria-pressed={kind === 'MOVE'} onClick={() => { setKind('MOVE'); setConfirm(false); }}>Resident moved</button>
      </div>
      <label><span>The right building</span>
        {to
          ? (
            <span className="pa-picked">
              <strong>{facilityDisplayName(to.name)}</strong>{to.city ? <span className="pa-sub-line"> · {to.city}</span> : null}
              <button type="button" className="pa-inline-edit" onClick={() => { setTo(null); setConfirm(false); }}>Change</button>
            </span>
          )
          : <input value={q} onChange={(e) => { setConfirm(false); setQ(e.target.value); }} placeholder="Type two or more letters of the building's name" aria-label="Search buildings" />}
      </label>
      {hitsErr && <p className="pa-notice stop" role="alert">{hitsErr}</p>}
      {!to && q.trim().length >= 2 && !hitsErr && hits.length === 0 && <p className="pa-text none" role="status">No building in eMerge matches that name.</p>}
      {!to && hits.length > 0 && (
        <ul className="pa-hits" aria-label="Buildings">
          {hits.map((h) => (
            <li key={h.id}>
              <button type="button" className="pa-hit" disabled={Boolean(rf && rf.facility_id === h.id && detail.case.facility_id === h.id)}
                onClick={() => { setTo({ id: h.id, name: h.name, city: h.city }); setHits([]); }}>
                <strong>{facilityDisplayName(h.name)}</strong>{' '}
                <span>{[h.city, h.state].filter(Boolean).join(', ') || 'City not recorded'}{rf && rf.facility_id === h.id ? ' · on the resident record now' : ''}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <label><span>Why (required, kept on the record)</span>
        <input value={reason} onChange={(e) => { setReason(e.target.value); setConfirm(false); }} maxLength={2000} aria-label="Correction reason" aria-required
          placeholder={kind === 'MOVE' ? 'For example: the facility said she transferred on the 1st' : 'For example: the census shows her at this building'} />
      </label>
      {kind === 'MOVE' && <label><span>Move date (blank = today)</span><input type="date" value={when} onChange={(e) => setWhen(e.target.value)} aria-label="Move date" /></label>}
      <p className="pa-text none">Every open case and UMED for this resident moves now, and Monday's sync will not move them back. A new lead that later arrives from Monday at another building shows there until it is corrected too. Who holds the resident does not change.</p>
      <Why id={whyFacilityId} reason={firstReason([!to, 'Choose the building first.'], [reason.trim().length < 3, 'Say why (at least 3 characters) first.'])} />
      <div className="btns">
        <button type="submit" className="pa-open pa-primary" disabled={busy || !to || reason.trim().length < 3} aria-describedby={!to || reason.trim().length < 3 ? whyFacilityId : undefined}>
          {busy ? 'Recording…' : confirm && to ? `Confirm: ${kind === 'MOVE' ? 'moved to' : 'correct to'} ${facilityDisplayName(to.name)}` : kind === 'MOVE' ? 'Record the move' : 'Correct the facility'}
        </button>
        {confirm && <button type="button" className="pa-clear" onClick={() => setConfirm(false)}>Not yet</button>}
      </div>
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
    </form>
  );
}

/** Where eMerge has the resident, where this case shows them, and on whose say-so. Read by everyone who can open the case. */
function FacilityRecord({ detail }: { detail: CaseDetail }) {
  const rf = detail.resident_facility || null;
  const c = detail.case;
  const differs = Boolean(rf && c.facility_id && rf.facility_id !== c.facility_id);
  return (
    <Cell title="Facility" wide>
      <dl className="pa-kv">
        <dt>Resident record</dt>
        <dd>{rf ? <><strong>{facilityDisplayName(rf.facility_name || 'Not recorded')}</strong>{rf.facility_city ? ` · ${rf.facility_city}` : ''}<span className="pa-sub-line pa-prov">{facilityProvenance(rf)}</span></> : <NotRecorded />}</dd>
        <dt>This case</dt>
        <dd>{c.facility_name ? facilityDisplayName(c.facility_name) : <BuildingOrClinic row={c} fallback={<NotRecorded />} />}<span className="pa-sub-line pa-prov">{detail.native?.facility_source === 'EMERGE' ? 'Set in eMerge' : 'From the Monday lead'}</span></dd>
      </dl>
      {differs && <p className="pa-notice attn" role="status">This case shows a different building from the resident record. Correct the facility to bring them together.</p>}
    </Cell>
  );
}

/**
 * Files above this go straight to S3. The web server refuses a body over
 * 25 MB, so a long recording cannot travel the ordinary route; below it the
 * in-process upload is one request instead of three.
 */
const DIRECT_UPLOAD_OVER_BYTES = 8 * 1024 * 1024;

/**
 * The residents of one building in the list on screen (2026-10-15): who a building-wide filing may be for. Only
 * what the list shows -- "in this list" -- and the server authorises each one again.
 */
interface BuildingChoice { facilityName: string; residents: { caseId: string; name: string }[] }

const buildingOf = (g: FacilityGroup | undefined): BuildingChoice | undefined => (g && g.facility_id && g.items.length > 1
  ? { facilityName: facilityDisplayName(g.facility_name), residents: g.items.map((x) => ({ caseId: x.id, name: personName(x) })) }
  : undefined);

const SHARED_REFUSED_WHY: Record<string, string> = {
  NOT_YOURS: 'not a case you hold', NOT_FOUND: 'not found', NO_RESIDENT: 'no resident record', RESIDENT_MERGED: 'merged into another record',
  MARKED_NOT_THIS_RESIDENTS: 'this file is marked "Not this resident\'s" there',
};

/** A building-wide filing's refusal, with the residents it names said by name (the list on screen knows them). */
function sharedFilingRefusal(r: { error?: string; message?: string; code?: string; detail?: any; status?: number; retryAfterSeconds?: number }, building: BuildingChoice | undefined): string {
  const refused: { caseId: string; code: string }[] = Array.isArray(r.detail?.refused) ? r.detail.refused : [];
  if (!refused.length) return errText(r);
  const nameOf = new Map((building?.residents || []).map((x) => [x.caseId, x.name]));
  const list = refused.map((x) => `${nameOf.get(x.caseId) || 'a resident'} (${SHARED_REFUSED_WHY[x.code] || x.code})`).join(', ');
  return `Nothing was filed for anyone. Leave out ${list}, then file again.`;
}

/**
 * Adding a document. Takes a case id rather than a whole `CaseDetail` so the
 * operating table can offer it on the row (Isaiah, 2026-09-23: "In the PA
 * portal they are missing the area to submit docs") without first loading the
 * case. `resetKey` clears the file input once the upload lands.
 *
 * 2026-10-15 (Talhia Zalvidea, Lady Lake): one BOM vetting recording covers every resident of a building.
 *   - A refusal "already filed on another resident" for a kind that may be shared shows the confirm step here: the
 *     reason (a preset for a vetting call) and "Upload anyway, it is shared". An audiogram, a UMED or a NOCA has none.
 *   - `building`: the residents of this building in the list. For a BOM vetting file or a recording the form offers
 *     "File for all residents of this building in this list": one upload, each resident chosen (all, by default), one
 *     reason. All or nothing: a resident the server refuses refuses the whole filing, and the form says who.
 *   - `onlyBuilding`: the building header's form, which files for the building and nothing else.
 */
function UploadForm({ caseId, resetKey, classes, onDone, building, sharedClasses = [], onlyBuilding = false }: {
  caseId: string; resetKey: string | number; classes: UploadClass[]; onDone: PanelDone;
  building?: BuildingChoice; sharedClasses?: UploadClass[]; onlyBuilding?: boolean;
}) {
  const whyUploadId = useWhyId('why-upload');
  const shareable = classes.filter((c) => sharedClasses.includes(c));
  const offered = onlyBuilding ? shareable : classes;
  const [cls, setCls] = useState<UploadClass>(offered[0] || 'BOM_VETTING');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // The same bytes are filed on another resident (2026-10-14): refused, and for the classes that may be shared, confirmable with a reason.
  const [shared, setShared] = useState<SharedRefusal | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const canBuilding = Boolean(building && building.residents.length > 1 && shareable.includes(cls));
  const [forBuilding, setForBuilding] = useState(onlyBuilding);
  const [chosen, setChosen] = useState<Set<string>>(() => new Set((building?.residents || []).map((x) => x.caseId)));
  const asBuilding = canBuilding && forBuilding;
  const refuse = (m: string) => { setErr(m); void onDone(m, { stop: true }); };
  const inputKey = `${caseId}:${resetKey}`;
  const label = UPLOAD_CLASS_LABEL[cls];

  const fileForBuilding = async (f: File) => {
    const ids = (building?.residents || []).map((x) => x.caseId).filter((id) => chosen.has(id));
    const why = overrideReason.trim();
    if (!ids.length) { setErr('Choose at least one resident to file this for.'); return; }
    if (why.length < 3) { setErr('Say why this file is shared (at least 3 characters).'); return; }
    const anchor = ids.includes(caseId) ? caseId : ids[0];
    const r = f.size > DIRECT_UPLOAD_OVER_BYTES
      ? await advocateApi.fileSharedDirect(anchor, ids, cls, f, why, freshKey(), (x) => setSent(x))
      : await advocateApi.fileShared(ids, cls, f, why, freshKey());
    if (!r.success || !r.data) { setErr(sharedFilingRefusal(r, building)); return; }
    const d: SharedFiling = r.data;
    setFile(null); setShared(null); setOverrideReason('');
    await onDone(`${label} filed for ${d.filed} resident${d.filed === 1 ? '' : 's'} of ${building!.facilityName}${d.alreadyOnFile ? `; ${d.alreadyOnFile} already had it on file` : ''}.`);
  };

  return (
    <form className="pa-form" aria-label={onlyBuilding ? `File one file for ${building?.facilityName || 'this building'}` : 'Add a document'} onSubmit={async (e) => {
      e.preventDefault(); if (!file) return; setBusy(true); setErr(null); setSent(null);
      try {
        if (asBuilding) { await fileForBuilding(file); return; }
        const big = file.size > DIRECT_UPLOAD_OVER_BYTES;
        const override = shared && shared.overridable ? overrideReason.trim() : '';
        if (shared && shared.overridable && override.length < shared.minLength) { setErr(`Say why this file is shared (at least ${shared.minLength} characters).`); return; }
        const r = big
          ? await advocateApi.uploadDocumentDirect(caseId, cls, file, (f) => setSent(f), override || undefined)
          : await advocateApi.uploadDocument(caseId, cls, file, freshKey(), override || undefined);
        if (!r.success || !r.data) {
          if (r.code === 'DOCUMENT_BELONGS_TO_ANOTHER_RESIDENT') {
            const said = r.error && r.error !== 'Request failed' ? r.error : 'This exact file is already filed on another resident.';
            setShared(readSharedRefusal(r.detail, cls));
            setErr(said); return;
          }
          refuse(errText(r)); return;
        }
        setFile(null); setShared(null); setOverrideReason('');
        await onDone(r.data.deduplicated ? 'That file was already on the record.' : `${label} added.`);
      } catch { refuse('Could not reach the server. Nothing was added.'); } finally { setBusy(false); setSent(null); }
    }}>
      <label><span>What it is</span>
        <select value={cls} onChange={(e) => { setCls(e.target.value as UploadClass); setShared(null); setErr(null); }} aria-label="Document class">{offered.map((c) => <option key={c} value={c}>{UPLOAD_CLASS_LABEL[c]}</option>)}</select>
      </label>
      <label><span>File</span><input key={inputKey} type="file" onChange={(e) => { setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null); setShared(null); setErr(null); }} aria-label="Document file" accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.tif,.tiff,.heic,.mp3,.m4a,.mp4,.mpeg,.mpg,.wav,.webm,.ogg,.amr,.3gp,.mov,.avi,.wma,.wmv,.docx,.doc,.msg,.eml" /></label>
      {file && file.size > DIRECT_UPLOAD_OVER_BYTES && (
        <p className="why">{Math.round(file.size / (1024 * 1024))} MB — this goes straight to storage, so it may take a moment.</p>
      )}
      {canBuilding && !onlyBuilding && (
        <label className="pa-check"><input type="checkbox" checked={forBuilding} onChange={(e) => { setForBuilding(e.target.checked); setErr(null); }} />
          <span>File for all residents of this building in this list</span></label>
      )}
      {asBuilding && building && (
        <fieldset className="pa-shared-who">
          <legend>{`Residents of ${building.facilityName} (${chosen.size} of ${building.residents.length} chosen)`}</legend>
          {building.residents.map((x) => (
            <label key={x.caseId} className="pa-check"><input type="checkbox" checked={chosen.has(x.caseId)}
              onChange={(e) => setChosen((prev) => { const n = new Set(prev); if (e.target.checked) n.add(x.caseId); else n.delete(x.caseId); return n; })} />
              <span>{x.name}</span></label>
          ))}
          <p className="why">One upload, filed on each resident chosen with the reason below. If any one of them cannot take it, nothing is filed for anyone and this says who.</p>
        </fieldset>
      )}
      {(asBuilding || (shared && shared.overridable)) && (
        <SharedFileReason tone="pa" value={overrideReason} onChange={setOverrideReason}
          preset={asBuilding ? FACILITY_WIDE_REASON : shared ? shared.preset : null} maxLength={shared ? shared.maxLength : 500}
          label={asBuilding ? 'Why is this file shared? (recorded on every resident)' : 'Why is this file shared?'} />
      )}
      <Why id={whyUploadId} reason={firstReason([!file, 'Choose a file first.'], [asBuilding && chosen.size === 0, 'Tick at least one resident first.'])} />
      <div className="btns">
        <button type="submit" className="pa-open pa-primary" disabled={busy || !file || (asBuilding && chosen.size === 0)} aria-describedby={!file || (asBuilding && chosen.size === 0) ? whyUploadId : undefined}>
          {busy ? (sent === null ? 'Adding…' : `Uploading ${Math.round(sent * 100)}%`)
            : asBuilding ? `File for ${chosen.size} resident${chosen.size === 1 ? '' : 's'}`
              : shared && shared.overridable ? 'Upload anyway, it is shared' : 'Add document'}
        </button>
      </div>
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
      {err && !asBuilding && shared && shared.overridable && (
        <p className="why">{'If this file really is shared, say why above and choose "Upload anyway, it is shared".'}
          {shared.facilityWide && canBuilding && !forBuilding && (
            <> <button type="button" className="pa-inline-edit" onClick={() => { setForBuilding(true); setErr(null); setShared(null); }}>File it for all residents of this building in this list</button></>
          )}
        </p>
      )}
    </form>
  );
}

/**
 * Prepare submittal — one workflow, not four screens (EP-010 §5.3, §6).
 *
 *   choose the units  ->  the amount is derived  ->  the term follows from the
 *   Patient Liability  ->  Ready to Submit
 *
 * The PA never types a total. The amount comes from the approved catalog and
 * the term from TERMS-RULING-001, and both version strings travel onto the
 * sale, so a sale can always say which catalog priced it.
 *
 * When no catalog version has been approved this says PRICING_RULE_REQUIRED
 * in plain words and still offers the handoff — the resident is ready, the
 * price list is not, and holding the resident hostage to a pricing approval
 * would be the wrong failure. The sale is then written with no amount and the
 * reason recorded, and My Sales says "Amount pending" rather than a number
 * somebody would have to defend later.
 */
const PRODUCT_NOTE: Record<string, string> = {
  LIABILITY_TOO_LOW: 'No term in the schedule fits this resident’s Patient Liability.',
  TERMS_UNRESOLVED: 'No Patient Liability is recorded, so no term can be chosen yet.',
};

function ProductChoice({ p, chosen, onChoose }: { p: OfferedProduct; chosen: boolean; onChoose: () => void }) {
  const term = p.term;
  const unavailable = p.total_cents === null;
  return (
    <label className={`pa-product${chosen ? ' on' : ''}${unavailable ? ' off' : ''}`}>
      <input type="radio" name="sale-product" checked={chosen} disabled={unavailable} onChange={onChoose} aria-label={p.label} />
      <span className="pa-product-body">
        <span className="pa-product-head">
          <strong>{p.label}</strong>
          <span className="pa-money">{p.total_cents === null ? 'No approved price' : usd.format(p.total_cents / 100)}</span>
        </span>
        <span className="pa-product-term">
          {term && term.status === 'RESOLVED' && term.months !== undefined
            ? `${term.months} month${term.months === 1 ? '' : 's'} at ${usd.format((term.monthly_cents || 0) / 100)}`
            : term
              ? PRODUCT_NOTE[term.status] || 'Term not resolved.'
              : 'Term not resolved.'}
          {p.floor_cents !== null && term && term.status === 'LIABILITY_TOO_LOW' && ` Lowest accepted is ${usd.format(p.floor_cents / 100)}.`}
        </span>
        {p.itemisation_inferred && <span className="pa-text none">The itemisation for this configuration was inferred, not stated by the business.</span>}
      </span>
    </label>
  );
}

/** Which ears a replacement case is for: the intake_kind suffix, else its replacement request's side (as the server reads it). */
function replacementSideOfCase(c: { intake_kind?: string | null; replacement_side?: string | null }): 'BOTH' | 'LEFT' | 'RIGHT' | null {
  const kind = String(c.intake_kind || '');
  if (!kind.startsWith('REPLACEMENT')) return null;
  if (kind === 'REPLACEMENT_BOTH') return 'BOTH';
  if (kind === 'REPLACEMENT_L') return 'LEFT';
  if (kind === 'REPLACEMENT_R') return 'RIGHT';
  const s = String(c.replacement_side || '').toUpperCase();
  return s === 'BOTH' || s === 'LEFT' || s === 'RIGHT' ? s : null;
}

export function ReadyToSubmit({ detail, sub, subErr, onDone, onProposal }: { detail: CaseDetail; sub: Submittal | null; subErr: string | null; onDone: PanelDone; onProposal?: (p: UmedProposal | null) => void }) {
  const whyReadyId = useWhyId('why-ready');
  const [product, setProduct] = useState<string | null>(null);
  // EP-010 (Isaiah, 2026-09-22): "PAs should also be allowed to enter a custom
  // amount as well in the event the lead needs special pricing or the deal
  // requires." Off by default — the catalog is the answer almost every time,
  // and a price box standing open invites a typed number where a chosen one
  // belongs.
  const [custom, setCustom] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [draft, setDraft] = useState<import('./advocateApi').PAReadyDraft | null>(null);
  const [preview, setPreview] = useState<{ url: string; accessId: string } | null>(null);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const previewFrameRef = useRef<HTMLIFrameElement | null>(null);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // #367 round 6: the UMEDs a refusal names; the advocate may open those they may see.
  const [blocking, setBlocking] = useState<BlockingUmed[]>([]);
  // New set, not a replacement (Isaiah, 2026-10-29): offered only after the server refused ALREADY_SOLD, from what the
  // refusal says (the earlier sale, its UMED, and what would stop a new set). The server decides every gate again.
  const [newSetOffer, setNewSetOffer] = useState<NewSetOffer | null>(null);
  const [newSetReason, setNewSetReason] = useState('');
  const [newSetTried, setNewSetTried] = useState(false);
  const refuse = (m: string) => { setErr(m); void onDone(m, { stop: true }); };
  const ready = sub?.readiness ?? null;
  const blocked = ready?.state === 'BLOCKED';
  const priced = sub?.catalog.status === 'ACTIVE';
  const label = readinessLabel;
  // 20261029p: a replacement-only product (the $1,500 rechargeable replacement) is offered only on a replacement case
  // whose side fits its ears -- two ears only on a BOTH-ears replacement (director's default 2026-10-29). The server
  // already leaves it out of any other case's offer (pricingCatalog.offeredOn); this holds for an offer read before.
  const side = replacementSideOfCase(detail.case);
  const products = (sub?.products ?? []).filter((p) => !p.replacement_case_only
    || (p.ear_count === 2 ? side === 'BOTH' : side === 'LEFT' || side === 'RIGHT'));
  const chosen = products.find((p) => p.code === product) || null;
  // EP-010: a custom amount answers "what is this sale worth", so it satisfies
  // the requirement a configuration otherwise carries. The PA may still name
  // the units beside it, and when they do the catalog figure the deal departed
  // from stays on the record next to the one they typed.
  const customCents = custom ? parseDollars(customAmount) : null;
  const customReady = Boolean(custom && customCents !== null && customReason.trim());
  const needsProduct = !chosen;
  const termsResolved = chosen?.term?.status === 'RESOLVED';
  const customMismatch = Boolean(custom && customCents !== null && chosen && customCents !== chosen.total_cents);
  const proposal = { product_code: product, ...(customReady ? { override_amount_cents: customCents, override_reason: customReason.trim() } : {}) };
  // The UMED sign panel reads the units (and any custom amount) chosen here.
  const proposalKey = JSON.stringify(product ? proposal : null);
  useEffect(() => { onProposal?.(product ? (JSON.parse(proposalKey) as UmedProposal) : null); }, [proposalKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onProposal?.(null), []); // eslint-disable-line react-hooks/exhaustive-deps
  const inputKey = JSON.stringify([detail.case.id, detail.native?.version, detail.demographics, detail.documents, sub, proposal, custom]);
  const inputRef = useRef(inputKey);
  inputRef.current = inputKey;
  const resetReview = () => { setDraft(null); setPreview(null); setPreviewLoaded(false); setPreviewFailed(false); setReviewId(null); setConfirm(false); setNewSetOffer(null); setNewSetTried(false); };
  useEffect(() => { resetReview(); }, [inputKey]);
  useEffect(() => {
    const frame = previewFrameRef.current;
    const failed = () => { setPreviewLoaded(false); setPreviewFailed(true); setReviewId(null); setConfirm(false); };
    frame?.addEventListener('error', failed);
    return () => frame?.removeEventListener('error', failed);
  }, [preview]);
  const preparable = Boolean(sub && priced && !blocked && !needsProduct && termsResolved && !customMismatch && (!custom || customReady));
  const newSetReasonMin = newSetOffer?.reasonMin ?? 3;
  const newSetReasonShort = newSetReason.trim().length < newSetReasonMin;

  /** The one act. `newSet` carries the PA's reason when they confirm a new set after an ALREADY_SOLD refusal. */
  const submit = async (newSet?: { reason: string }) => {
    setBusy(true); setErr(null);
    try {
      const r = await advocateApi.readyToSubmit(detail.case.id, {
        version: detail.native?.version ?? 1, idempotency_key: freshKey(), product_code: product,
        review_id: reviewId!,
        ...(customReady ? { override_amount_cents: customCents, override_reason: customReason.trim() } : {}),
        ...(newSet ? { new_set: newSet } : {}),
      });
      if (!r.success || !r.data) {
        if (['PA_REVIEW_STALE', 'PA_REVIEW_REQUIRED', 'PA_DRAFT_BLOCKED', 'STALE', 'PRICING_RULE_REQUIRED', 'PAYMENT_TERMS_MISSING'].includes(r.code || '')) resetReview();
        setBlocking(Array.isArray(r.detail?.blockingUmeds) ? r.detail!.blockingUmeds! : []);
        // The refusal (ALREADY_SOLD) or a new-set gate (NEW_SET_*) says what a new set would need; anything else ends the offer.
        if ((r.code === 'ALREADY_SOLD' || String(r.code || '').startsWith('NEW_SET_')) && r.detail?.newSet) setNewSetOffer(r.detail.newSet);
        else if (r.code !== 'ALREADY_SOLD' && !String(r.code || '').startsWith('NEW_SET_')) setNewSetOffer(null);
        refuse(errText(r)); return;
      }
      setBlocking([]);
      setConfirm(false);
      setNewSetOffer(null); setNewSetReason(''); setNewSetTried(false);
      const amount = r.data.amountCents === null || r.data.amountCents === undefined ? null : usd.format(r.data.amountCents / 100);
      const copies = r.data.siblingsClosed?.length || 0;
      const kept = r.data.siblingsKept?.length || 0;
      const tail = `${copies ? ` ${copies === 1 ? 'The duplicate record' : `${copies} duplicate records`} for this resident ${copies === 1 ? 'was' : 'were'} closed.` : ''}${kept ? ` ${kept === 1 ? 'One other record has its own sale or UMED and stayed' : `${kept} other records have their own sale or UMED and stayed`} open for a manager to review.` : ''}`;
      const newSetWords = r.data.newSet?.confirmed ? ' Recorded as a new set: a new sale and a new UMED, separate from the earlier one.' : '';
      await onDone(r.data.replayed
        ? 'This case is already with UMED, and its sale is already recorded.'
        : `${amount
          ? `Ready to submit — ${amount}${r.data.amountSource === 'APPROVED_OVERRIDE' ? ' (custom amount)' : ''} recorded in My sales and handed to UMED creation.`
          : 'Ready to submit — recorded in My sales with the amount pending, and handed to UMED creation.'}${newSetWords}${tail}`);
    } catch { refuse('Could not reach the server. Nothing was changed.'); } finally { setBusy(false); }
  };

  return (
    <div className="pa-form" aria-label="Prepare submittal">
      {subErr && <p className="pa-notice stop" role="alert">{subErr} The check did not run, so this is not a verdict on the case.</p>}
      {!sub && !subErr && <p className="pa-text none">Checking the evidence and the price list…</p>}

      {sub && (
        <>
          {/* 1. What the resident can carry. */}
          <p className="pa-sub-line">
            Patient Liability:{' '}
            {sub.liability
              ? <><strong>{usd.format(sub.liability.amount_cents / 100)}</strong> · {LIABILITY_CLASS_LABEL[sub.liability.classification]}</>
              : <span className="pa-text none">Not recorded — no term can be chosen until it is</span>}
          </p>

          {/* 2. The units, and the amount each derives. */}
          {priced ? (
            <fieldset className="pa-products">
              <legend>Units sold</legend>
              {products.map((p) => (
                <ProductChoice key={p.code} p={p} chosen={product === p.code} onChoose={() => { setProduct(p.code); setConfirm(false); }} />
              ))}
              <p className="pa-text none">
                Priced by {sub.catalog.version}, termed by {sub.catalog.term_policy_version}. Both are recorded on the sale; a later
                catalog change never re-prices it.
              </p>
            </fieldset>
          ) : (
            <p className="pa-notice stop" role="status">
              <strong>No approved price catalog is in effect.</strong> Packet review and Ready to submit are blocked until an approved priced product is available.
            </p>
          )}

          {/* 2b. A custom amount, when the deal is not the catalog's. */}
          <div className="pa-custom">
            <label className="pa-check">
              <input type="checkbox" checked={custom} onChange={(e) => { setCustom(e.target.checked); setConfirm(false); }} aria-label="Enter a custom sale amount" />
              This sale is not the catalog price
            </label>
            {custom && (
              <div className="pa-custom-body">
                <label><span>Sale amount</span>
                  <input inputMode="decimal" value={customAmount} onChange={(e) => { setCustomAmount(e.target.value); setConfirm(false); }} placeholder="4,500.00" aria-label="Custom sale amount in dollars" />
                </label>
                <label><span>Why (required, kept on the sale)</span>
                  <input value={customReason} onChange={(e) => { setCustomReason(e.target.value); setConfirm(false); }} maxLength={2000} placeholder="Facility negotiated a package rate" aria-label="Custom amount reason" />
                </label>
                {customAmount && customCents === null && <p className="pa-notice stop" role="alert">Enter a dollar amount, for example 4500 or 4,500.00.</p>}
                {customCents !== null && <p className="pa-text none">Recorded as {usd.format(customCents / 100)}, as a custom amount with your name and this reason on it{chosen ? `, beside the catalog's ${chosen.total_cents !== null ? usd.format(chosen.total_cents / 100) : 'figure'}` : ''}.</p>}
                {custom && !customReason.trim() && <p className="pa-text none">A custom amount needs a reason — it is the sentence a closed month will be asked to justify.</p>}
              </div>
            )}
          </div>

          {/* 3. The evidence. */}
          {blocked
            ? <p className="pa-notice stop">Not ready: missing {ready!.missing.map(label).join(' and ')}.{ready!.missing.some((m) => m === 'UMED_DOCUMENT' || m === 'AUDIOGRAM') ? ' Add it in Documents.' : ''}{ready!.missing.includes('SSN_OR_MEDICAID') ? ` ${IDENTIFIER_NEEDED_WORDS}` : ''}</p>
            : ready
              ? <p className="pa-text">{ready.state === 'READY' ? 'The UMED and the audiogram are on file.' : `Nothing proven missing${ready.untracked.length ? `; not yet verified: ${ready.untracked.map(label).join(', ')}` : ''}.`}</p>
              : null}

          {chosen && !termsResolved && <p className="pa-notice stop" role="status">Payment terms are unresolved. Packet review and Ready to submit are blocked.</p>}
          {customMismatch && <p className="pa-notice stop" role="status">The custom amount differs from the approved catalog total. Packet review and Ready to submit are blocked until authoritative recalculated payment terms are available.</p>}
          <div className="btns">
            <button type="button" className="pa-open" disabled={busy || !preparable} onClick={async () => {
              const currentKey = inputKey;
              setBusy(true); setErr(null); resetReview();
              try {
                const result = await advocateApi.prepareReadyDraft(detail.case.id, proposal);
                if (inputRef.current !== currentKey) return;
                if (!result.success || !result.data) { setErr(errText(result)); return; }
                setDraft(result.data.draft);
              } catch { setErr('Could not prepare the draft. Nothing was handed off.'); } finally { setBusy(false); }
            }}>{busy ? 'Preparing...' : 'Prepare review draft'}</button>
            {draft && <button type="button" className="pa-open" disabled={busy} onClick={async () => {
              const currentKey = inputKey;
              setBusy(true); setErr(null); setPreview(null); setPreviewLoaded(false); setPreviewFailed(false); setReviewId(null); setConfirm(false);
              try {
                const result = await advocateApi.previewReadyDraft(detail.case.id, { ...proposal, draft_id: draft.id });
                if (inputRef.current !== currentKey) return;
                if (!result.success || !result.data) { resetReview(); setErr(errText(result)); return; }
                setPreview({ url: result.data.url, accessId: result.data.access_event_id });
              } catch { setErr('Could not open the draft. Review was not recorded.'); } finally { setBusy(false); }
            }}>Preview draft</button>}
          </div>
          {draft && <p className="pa-text">Draft generated {shortDateTime(draft.generatedAt)}. {draft.pageCount} pages.</p>}
          {preview && draft && <section aria-label="PA draft preview">
            <iframe ref={previewFrameRef} title="PA review draft PDF" src={preview.url} style={{ width: '100%', height: 520, border: 0 }} onLoad={() => { if (!previewFailed) setPreviewLoaded(true); }} />
            <a href={preview.url} target="_blank" rel="noopener noreferrer">Open draft PDF in a new tab</a>
            {previewFailed && <p className="pa-notice stop" role="alert">The draft preview failed. Open a new preview before acknowledging completeness.</p>}
            <button type="button" className="pa-open" disabled={busy || !previewLoaded || previewFailed || Boolean(reviewId)} onClick={async () => {
              const currentKey = inputKey;
              setBusy(true); setErr(null);
              try {
                const result = await advocateApi.reviewReadyDraft(detail.case.id, { ...proposal, draft_id: draft.id, sha256: draft.sha256,
                  input_fingerprint: draft.inputFingerprint, preview_access_id: preview.accessId });
                if (inputRef.current !== currentKey) return;
                if (!result.success || !result.data) { resetReview(); setErr(errText(result)); return; }
                setReviewId(result.data.reviewId);
              } catch { setErr('Could not record the review. Ready to submit remains blocked.'); } finally { setBusy(false); }
            }}>{reviewId ? 'Draft review recorded' : 'Acknowledge completeness of this draft'}</button>
          </section>}

          {/* 4. What the one act will do. */}
          {confirm && (
            <div className="pa-confirm">
              <p className="pa-text"><strong>Mark ready to submit?</strong> In one step this records the sale{chosen ? ` of ${chosen.label}` : ''}{customReady && customCents !== null ? ` at ${usd.format(customCents / 100)} (a custom amount)` : chosen && chosen.total_cents !== null ? ` at ${usd.format(chosen.total_cents / 100)}` : ''}, credits it to whoever holds this case now, shows it in My sales, and creates the UMED exactly once.</p>
            </div>
          )}
        </>
      )}

      <Why id={whyReadyId} reason={firstReason([!preparable, 'Finish what is listed above first.'], [!draft, 'Prepare and preview the review draft first.'], [!preview, 'Preview the review draft first.'], [previewFailed, 'The packet preview failed: load it again first.'], [!previewLoaded, 'Wait for the packet preview to load.'], [!reviewId, 'Acknowledge the draft first (the button above).'])} />
      <div className="btns">
        <button type="button" className="pa-open pa-primary" disabled={busy || !preparable || !reviewId || previewFailed || !previewLoaded} aria-describedby={!preparable || !reviewId || previewFailed || !previewLoaded ? whyReadyId : undefined} onClick={async () => {
          if (!confirm) { setConfirm(true); return; }
          await submit();
        }}>{busy ? 'Handing off…' : confirm ? 'Confirm: ready to submit' : 'Mark ready to submit'}</button>
        {confirm && <button type="button" className="pa-open" onClick={() => setConfirm(false)}>Not yet</button>}
      </div>
      {needsProduct && !busy && <p className="pa-text none">Choose the units sold. A custom amount still requires a priced product.</p>}
      {err && (
        <p className="pa-notice stop" role="alert">
          {err}
          {blocking.filter((b) => b.viewable).map((b) => (
            <React.Fragment key={b.umedCaseId}>{' '}<Link to={`/portal/umed?view=work&case=${encodeURIComponent(b.umedCaseId)}`}>Open the UMED ({b.stageWords})</Link></React.Fragment>
          ))}
        </p>
      )}
      {newSetOffer && (
        <NewSetSection
          offer={newSetOffer} reason={newSetReason} onReason={(v) => { setNewSetReason(v); }}
          tried={newSetTried} short={newSetReasonShort} reasonMin={newSetReasonMin}
          busy={busy} canSubmit={Boolean(reviewId && preparable && previewLoaded && !previewFailed)}
          onConfirm={async () => {
            if (newSetReasonShort) { setNewSetTried(true); return; }
            await submit({ reason: newSetReason.trim() });
          }}
        />
      )}
    </div>
  );
}

/** 2026-07-20 → 07/20/26, read as a calendar day (never through a clock). */
const usDay = (iso: string | null | undefined): string | null => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(2, 4)}` : null);

/**
 * New set, not a replacement (Isaiah Cotto, 2026-10-29, Option A). Shown only after Ready to submit was refused
 * ALREADY_SOLD: what it does, the earlier sale and UMED the refusal named, then either what stops it (each gate in the
 * server's words) or a required reason and the confirm. The earlier sale and UMED are never changed; the server decides
 * every gate again when the PA confirms.
 */
function NewSetSection({ offer, reason, onReason, tried, short, reasonMin, busy, canSubmit, onConfirm }: {
  offer: NewSetOffer; reason: string; onReason: (v: string) => void; tried: boolean; short: boolean; reasonMin: number;
  busy: boolean; canSubmit: boolean; onConfirm: () => void | Promise<void>;
}) {
  const whyNewsetId = useWhyId('why-newset');
  const umeds = offer.priorUmeds || [];
  const when = usDay(offer.priorSaleOn);
  const invalid = tried && short;
  return (
    <section className="pa-newset" aria-labelledby="newset-head">
      <h4 id="newset-head" className="pa-newset-head">New set, not a replacement</h4>
      <p className="pa-text">
        Records a NEW sale and a NEW UMED, separate from the earlier one. The earlier sale and its UMED are not changed.
        Use this only when the resident qualifies for a new set; if the provider saw them for a replacement, record it on Replacements.
      </p>
      <p className="pa-sub-line">
        Earlier sale: <strong>{when || 'date not recorded'}</strong>
        {' · '}Earlier UMED: <strong>{umeds.length ? umeds.map((u) => u.stateWords).join('; ') : 'none on record'}</strong>
      </p>
      {!offer.available ? (
        <div className="pa-notice stop" role="status">
          <strong>A new set cannot be recorded yet.</strong>
          <ul className="pa-newset-list">
            {offer.blockers.map((b) => <li key={b.code}>{b.message}</li>)}
          </ul>
        </div>
      ) : (
        <>
          <label>
            <span>Why this is a new set (required, kept on the record)</span>
            <textarea
              value={reason} onChange={(e) => onReason(e.target.value)} maxLength={1000} required aria-required="true"
              aria-invalid={invalid} aria-describedby={invalid ? 'newset-reason-err' : undefined}
              placeholder="Earlier aids are past warranty; the resident qualifies for a new set"
            />
          </label>
          {invalid && <p id="newset-reason-err" className="pa-notice stop" role="alert">Say why this is a new set and not a replacement ({reasonMin} characters or more). The reason stays on the record.</p>}
          {!canSubmit && <p className="pa-text none" id={whyNewsetId}>The reviewed draft is no longer current. Prepare, preview and acknowledge the draft again, then confirm.</p>}
          <div className="btns">
            <button type="button" className="pa-open pa-primary" disabled={busy || !canSubmit} aria-busy={busy} aria-describedby={!canSubmit ? whyNewsetId : undefined} onClick={() => { void onConfirm(); }}>
              {busy ? 'Recording the new set…' : 'Confirm new set: record a new sale and a new UMED'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Prepare submittal, folded unless the case can be submitted now.
 *
 * It opens by itself only when the evidence check says READY and the case is
 * VETTED: a PA with other work to do first (a note, a call, a document) is not
 * handed the sale form before the case is there. So the check runs here, once
 * per case and whenever its documents change, and the fold is keyed on its
 * verdict -- the verdict arriving is the one time the fold re-decides itself.
 */
/** "Generate & sign UMED": opens the docked sign panel (surrogate e-sign on the resident's behalf). */
function GenerateUmed({ caseId, proposal, onDone }: { caseId: string; proposal: UmedProposal | null; onDone: PanelDone }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="pa-act pa-act-link">
      <button type="button" className="pa-act-head" aria-expanded={open} onClick={() => setOpen(true)}><span aria-hidden="true">✍</span> Generate &amp; sign UMED</button>
      {open && <UmedSignPanel key={caseId} caseId={caseId} proposal={proposal} onClose={() => setOpen(false)} onSigned={onDone} />}
    </div>
  );
}
function PrepareSubmittal({ detail, onDone, onProposal }: { detail: CaseDetail; onDone: PanelDone; onProposal?: (p: UmedProposal | null) => void }) {
  // A closed lead cannot be marked ready to submit; it was rendered, then refused (2026-10-02). Reopen it first.
  if (isClosedLead(detail.case)) return null;
  return <PrepareSubmittalOpen detail={detail} onDone={onDone} onProposal={onProposal} />;
}
function PrepareSubmittalOpen({ detail, onDone, onProposal }: { detail: CaseDetail; onDone: PanelDone; onProposal?: (p: UmedProposal | null) => void }) {
  const [sub, setSub] = useState<Submittal | null>(null);
  const [subErr, setSubErr] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    (async () => {
      const r = await advocateApi.submittal(detail.case.id);
      if (!live) return;
      if (r.success && r.data) { setSub(r.data); setSubErr(null); return; }
      // `null` is the loading state, so a failure coerced into it would leave
      // "Checking…" on screen forever with the button disabled — an advocate
      // waiting on a check that already finished.
      setSub(null);
      setSubErr(errText(r));
    })();
    return () => { live = false; };
  }, [detail.case.id, detail.documents?.length]);
  const now = detail.case.stage === 'VETTED' && sub?.readiness?.state === 'READY';
  return <Act key={now ? 'ready' : 'idle'} title="Prepare submittal" open={now}><ReadyToSubmit detail={detail} sub={sub} subErr={subErr} onDone={onDone} onProposal={onProposal} /></Act>;
}

/**
 * Private pay goes straight to Finance (no UMED).
 *
 * A private-pay sale is not a UMED sale: the family pays, so there is nothing
 * for UMED to submit. The PA marks it here and Finance receives exactly one
 * handoff labelled "Private pay (no UMED)". A SPEND-DOWN is NOT private pay for
 * this purpose -- it still needs a UMED, so that goes through Prepare submittal.
 * Both facts are the PA's to confirm, and the server refuses without them.
 *
 * Offered on the same capability as Prepare submittal (ready_to_submit); the
 * server decides again. Folded: it loads the price list and the refusals known
 * up front only when opened.
 */
const PRIVATE_PAY_LABEL = 'Private pay (no UMED)';
/*
 * Where a private-pay handoff Finance did not return stands (Copilot on #369, P2). Every state before CLOSED is
 * Finance's work in progress: "With Finance". CLOSED is Finance's own end state, and only Finance's close act sets it
 * on a private-pay handoff, so the PA reads Finance's own word for it (HANDOFF_STATE_LABEL, the Finance work screen),
 * not "With Finance" forever. RETURNED_TO_UMED is said apart (returned to you). The handoff is still the sale's one
 * handoff either way: nothing here offers a second.
 */
const privatePayWhere = (state: string): string => (state === 'CLOSED' ? `${HANDOFF_STATE_LABEL.CLOSED} by Finance` : 'With Finance');
function PrivatePayToFinance({ detail, onDone }: { detail: CaseDetail; onDone: PanelDone }) {
  return <Act title="Private pay → Finance (no UMED)"><PrivatePayForm detail={detail} onDone={onDone} /></Act>;
}
/** Isaiah, 2026-10-29: private pay is paid in full only ("we do not offer monthly payments"). */
function planPreview(totalCents: number): string {
  return `${usd.format(totalCents / 100)} paid in full`;
}
/** Who pays, in the words Finance reads it in. A handoff made before the payer was asked says so. */
const PAYER_WORD: Record<PrivatePayPayer, string> = { FAMILY: 'Family', FACILITY: 'Facility' };
const payerLine = (p: PrivatePayPayer | null | undefined): string =>
  p === 'FAMILY' || p === 'FACILITY' ? `Paid by: ${PAYER_WORD[p]}` : 'Payer not recorded (handed before the payer was asked)';
/**
 * A handoff's terms as made. v2 is paid in full; a handoff made under pp-terms-v1-2026-10-07 may be monthly and stays
 * valid, so it still reads N × monthly = total.
 */
const handoffTermsText = (t: PrivatePayHandoff['terms']): string =>
  t.term_months > 1 ? `${t.term_months} × ${usd.format(t.monthly_cents / 100)} = ${usd.format(t.total_cents / 100)}` : `${usd.format(t.total_cents / 100)} paid in full`;

/**
 * A card, bank or SSN number is never an email. As the server reads it (achFormService.sensitiveNumberIn): NFKC first,
 * then every non-digit dropped from each part of the address (before and after the last '@'); nine or more digits in a
 * part is refused, whatever separates them (space, dot, dash, underscore, slash, plus, tab, full-width digits).
 */
const SENSITIVE_DIGITS = 9;
// The constructor, not a literal: the 'u' flag in a literal is refused at this project's ES5 target.
const DIGIT_ANY_SCRIPT = new RegExp('\\p{Nd}', 'gu');
const digitCount = (t: string): number => (t.match(DIGIT_ANY_SCRIPT) || []).length;
const sensitiveNumberIn = (raw: string): boolean => {
  const s = raw.normalize('NFKC');
  const at = s.lastIndexOf('@');
  const parts = at >= 0 ? [s.slice(0, at), s.slice(at + 1)] : [s];
  return parts.some((p) => digitCount(p) >= SENSITIVE_DIGITS);
};
// eslint-disable-next-line no-control-regex
const EMAIL_SHAPE = /^[^\s\x00-\x1f\x7f@]+@[^\s@]+\.[^\s@]+$/;
/** The client-side guard on the payer's email (the server checks again). Null means it passes. */
const achEmailProblem = (raw: string): string | null => {
  const v = raw.trim();
  if (!v) return null;
  if (sensitiveNumberIn(v)) return 'That looks like a card, bank or SSN number. Card, bank and SSN numbers are never entered in eMerge. Enter only the payer’s email address.';
  if (!EMAIL_SHAPE.test(v)) return 'Enter the payer’s email address, like name@example.com.';
  return null;
};
const ACH_HISTORY_LABEL: Record<PrivatePayAchKind, string> = {
  SENT: 'Sent', SEND_FAILED: 'Send failed (not sent)', SEND_UNKNOWN: 'Send not confirmed', HANDLED_OUTSIDE: 'Handled outside eMerge',
};

/**
 * The ACH form goes to the payer (Isaiah, 2026-10-29): either eMerge emails the BLANK form, or the PA says it is handled
 * outside eMerge. One send per click: the button is disabled while a call is in flight and every click carries a fresh
 * idempotency key. A send that already went is said, and a second one needs an explicit confirm (the server answers
 * 409 ALREADY_SENT unless confirm_resend). eMerge never takes card or bank numbers: the email field refuses them.
 */
function PrivatePayAchPanel({ caseId, ach, reload }: { caseId: string; ach: PrivatePayAch; reload: () => void }) {
  const whyAchId = useWhyId('why-ach');
  const whyAchAgainId = useWhyId('why-ach-again');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const flight = useRef(false);
  const [again, setAgain] = useState<{ at: string; recipient: string } | null>(null);
  const [confirmAgain, setConfirmAgain] = useState(false);
  const [done, setDone] = useState<{ message: string; stop: boolean } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const sent = again || (ach.last_sent && ach.last_sent.state === 'SENT' ? { at: ach.last_sent.at, recipient: ach.last_sent.recipient } : null);
  const unsure = !sent && ach.last_sent && ach.last_sent.state === 'UNKNOWN' ? ach.last_sent : null;
  const problem = achEmailProblem(email);
  const canSend = ach.send.enabled && !ach.in_flight;
  const history = [...(ach.history || [])].sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));

  const post = async (body: PrivatePayAchBody, kind: 'SEND' | 'OUTSIDE') => {
    if (flight.current) return;
    flight.current = true; setBusy(true); setErr(null); setDone(null);
    try {
      const r = await advocateApi.privatePayAchForm(caseId, body);
      if (r.success && r.data) {
        setDone({ message: r.data.message, stop: r.data.outcome === 'FAILED' || r.data.outcome === 'UNKNOWN' });
        setAgain(null); setConfirmAgain(false);
        reload();
      } else if (r.code === 'ALREADY_SENT') {
        const d = (r.detail && typeof r.detail === 'object' ? r.detail : {}) as { at?: string; recipient?: string };
        setAgain({ at: d.at || ach.last_sent?.at || '', recipient: d.recipient || ach.last_sent?.recipient || 'the payer' });
        setConfirmAgain(true);
        reload();
      } else if (r.code === 'ACH_FORM_CHANGED') {
        // Refused before anything was claimed or sent: the server's form file is not the reviewed blank form.
        setErr('The form file on the server changed; nothing was sent. Tell the eMerge team, and handle the form outside eMerge for now.');
      } else if (kind === 'SEND' && (r.status === undefined || r.status >= 500)) {
        // The send may have gone even though the answer did not come back. Say so; the history below is the record.
        setErr('eMerge could not confirm whether the form was sent. Check the history below before sending again.');
        reload();
      } else {
        setErr(errText(r));
      }
    } catch {
      setErr(kind === 'SEND' ? 'eMerge could not confirm whether the form was sent. Check the history below before sending again.' : 'Could not reach the server. Nothing was changed.');
    } finally { flight.current = false; setBusy(false); }
  };
  const send = (resend: boolean) => {
    if (problem || !email.trim()) { setErr(problem || 'Enter the payer’s email address.'); return; }
    void post({ choice: 'SEND', recipient_email: email.trim(), ...(resend ? { confirm_resend: true as const } : {}), idempotency_key: freshKey() }, 'SEND');
  };

  return (
    <fieldset className="pa-products" aria-label="ACH form to the payer">
      <legend>Send the ACH form to the payer</legend>
      <p className="pa-text none">{ach.form?.title || 'Credit Card / ACH Authorization Form'} (blank). The payer fills it in and returns it; eMerge never takes the numbers.</p>

      {sent && <p className="pa-notice" role="status">The ACH form was already sent on {shortDate(sent.at)} to {sent.recipient}</p>}
      {unsure && <p className="pa-notice stop" role="status">An ACH form send to {unsure.recipient} on {shortDate(unsure.at)} could not be confirmed. It may or may not have reached the payer.</p>}
      {ach.handled_outside && <p className="pa-notice" role="status">The ACH form was handled outside eMerge on {shortDate(ach.handled_outside.at)}{ach.handled_outside.by_name ? ` by ${ach.handled_outside.by_name}` : ''}.</p>}
      {ach.in_flight && <p className="pa-notice" role="status">An ACH form send is in progress. Wait for it to finish.</p>}

      <label><span>Payer&apos;s email</span>
        <input type="email" inputMode="email" autoComplete="off" value={email} aria-label="Payer's email" aria-invalid={Boolean(problem)}
          onChange={(e) => { setEmail(e.target.value); setErr(null); setConfirmAgain(false); }} placeholder="name@example.com" />
      </label>
      <p className="pa-text none">
        eMerge sends only the blank form{ach.return_to ? <>; the payer returns the signed form to <strong>{ach.return_to}</strong> (you are cc&apos;d)</> : '; the payer returns the signed form to your work email'}.
        Never type card or bank numbers here.
      </p>
      {problem && <p className="pa-notice stop" role="alert">{problem}</p>}
      {!ach.send.enabled && <p className="pa-notice stop" role="status">{ach.send.reason || 'Sending the form from eMerge is not available right now.'} You can still record that it is handled outside eMerge.</p>}

      {confirmAgain && sent ? (
        <div className="pa-confirm">
          <p className="pa-text"><strong>Send it again?</strong> The form already went to {sent.recipient} on {shortDate(sent.at)}. A second send puts a second copy in their inbox.</p>
          <div className="btns">
            <button type="button" className="pa-open pa-primary" disabled={busy || !canSend || Boolean(problem) || !email.trim()} aria-describedby={!canSend || Boolean(problem) || !email.trim() ? whyAchAgainId : undefined} onClick={() => send(true)}>{busy ? 'Sending…' : 'Send it again'}</button>
            <Why id={whyAchAgainId} reason={firstReason([ach.in_flight, 'A form is already on its way; wait for it.'], [!ach.send.enabled, 'Sending the ACH form is not open for this case.'], [!email.trim(), 'Enter the email address first.'], [Boolean(problem), problem || 'Check the email address.'])} />
            <button type="button" className="pa-open" disabled={busy} onClick={() => setConfirmAgain(false)}>Do not send again</button>
          </div>
        </div>
      ) : (
        <>
        <Why id={whyAchId} reason={firstReason([ach.in_flight, 'A form is already on its way; wait for it.'], [!ach.send.enabled, 'Sending the ACH form is not open for this case.'], [!email.trim(), 'Enter the email address first.'], [Boolean(problem), problem || 'Check the email address.'])} />
        <div className="btns">
          <button type="button" className="pa-open pa-primary" disabled={busy || !canSend || Boolean(problem) || !email.trim()} aria-describedby={!canSend || Boolean(problem) || !email.trim() ? whyAchId : undefined}
            onClick={() => { if (sent) { setConfirmAgain(true); return; } send(false); }}>{busy ? 'Sending…' : 'Send the ACH form from eMerge'}</button>
          <button type="button" className="pa-open" disabled={busy}
            onClick={() => void post({ choice: 'OUTSIDE', idempotency_key: freshKey() }, 'OUTSIDE')}>Handled outside eMerge</button>
        </div>
        </>
      )}

      {done && (done.stop
        ? <p className="pa-notice stop" role="alert">{done.message}</p>
        : <p className="pa-notice" role="status">{done.message}</p>)}
      {err && <p className="pa-notice stop" role="alert">{err}</p>}

      {history.length > 0 && (
        <ul className="pa-notes" aria-label="ACH form history">
          {history.map((x, i) => (
            <li key={`${x.kind}:${x.at}:${i}`}>
              <p className="pa-text">
                <strong>{ACH_HISTORY_LABEL[x.kind] || x.kind}</strong> · {shortDate(x.at)}{x.recipient ? ` · to ${x.recipient}` : ''}{x.by_name ? ` · by ${x.by_name}` : ''}
              </p>
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}

function PrivatePayForm({ detail, onDone }: { detail: CaseDetail; onDone: PanelDone }) {
  const caseId = detail.case.id;
  const [status, setStatus] = useState<PrivatePayStatus | null>(null);
  const [statusErr, setStatusErr] = useState<string | null>(null);
  const [product, setProduct] = useState<string | null>(null);
  const [payer, setPayer] = useState<PrivatePayPayer | null>(null);
  const [paying, setPaying] = useState(false);
  const [notSpendDown, setNotSpendDown] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refuse = (m: string) => { setErr(m); void onDone(m, { stop: true }); };
  const reload = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const b = await advocateApi.privatePay(caseId);
        if (!live) return;
        if (b.success && b.data) { setStatus(b.data); setStatusErr(null); } else { setStatus(null); setStatusErr(errText(b)); }
      } catch { if (live) { setStatus(null); setStatusErr('Could not reach the server. Nothing was changed.'); } }
    })();
    return () => { live = false; };
  }, [caseId, tick]);

  const cat = status?.catalog ?? null;
  const chosen = cat?.products.find((p) => p.code === product) || null;
  const preview = chosen ? planPreview(chosen.total_cents) : null;
  const blockers = status?.blockers || [];
  const h = status?.handoff || null;
  const live = h && !h.returned ? h : null;
  const loading = !status && !statusErr;
  const armed = Boolean(status && cat && blockers.length === 0 && !live && paying && notSpendDown && chosen && payer);
  const dirty = () => setConfirm(false);
  const ach = status?.ach;

  return (
    <div className="pa-form" aria-label="Private pay to Finance">
      {statusErr && <p className="pa-notice stop" role="alert">{statusErr} The check did not run, so this is not a verdict on the case.</p>}
      {loading && <p className="pa-text none">Checking the case and the price list…</p>}

      {h && h.returned && (
        <p className="pa-notice stop" role="status">
          Finance returned this private-pay handoff to you on {shortDate(h.returned.at)}{h.returned.by_name ? ` (${h.returned.by_name})` : ''}{h.returned.reason ? `: ${h.returned.reason}` : '.'}
        </p>
      )}

      {live && (
        <p className="pa-text" role="status">
          <strong>{privatePayWhere(live.state)} — {PRIVATE_PAY_LABEL}</strong> · handed {shortDate(live.handed_at)}{live.handed_by_name ? ` by ${live.handed_by_name}` : ''} · {handoffTermsText(live.terms)} · {payerLine(live.payer ?? live.terms.payer)}
        </p>
      )}

      {!live && blockers.length > 0 && (
        <ul className="pa-notes" aria-label="Why private pay cannot be handed over">
          {blockers.map((x) => <li key={x.code}><p className="pa-notice stop" role="status">{x.message}</p></li>)}
        </ul>
      )}

      {!live && status && (
        <>
          {cat ? (
            <fieldset className="pa-products">
              <legend>Units sold</legend>
              {cat.products.map((p) => (
                <label key={p.code} className={`pa-product${product === p.code ? ' on' : ''}`}>
                  <input type="radio" name="pp-product" checked={product === p.code} onChange={() => { setProduct(p.code); dirty(); }} aria-label={p.label} />
                  <span className="pa-product-body">
                    <span className="pa-product-head"><strong>{p.label}</strong><span className="pa-money">{usd.format(p.total_cents / 100)}</span></span>
                  </span>
                </label>
              ))}
              <p className="pa-text none">The total is the catalog price ({cat.version}); it is not negotiable here and is recorded on the sale. Private pay is paid in full.</p>
              {preview && <p className="pa-text" role="status" aria-label="Payment preview">{chosen!.label}: {preview}</p>}
            </fieldset>
          ) : (
            <p className="pa-notice stop" role="status" id={`why-privatepay-catalog-${caseId}`}>
              <strong>No approved price catalog is in effect.</strong> A private-pay sale is priced from the catalog, so it cannot be handed to Finance until a pricing version is approved.
            </p>
          )}

          {cat && (
            <fieldset className="pa-products">
              <legend>Who pays</legend>
              <label className="pa-check">
                <input type="radio" name="pp-payer" checked={payer === 'FAMILY'} onChange={() => { setPayer('FAMILY'); dirty(); }} />
                The family (or the resident) pays
              </label>
              <label className="pa-check">
                <input type="radio" name="pp-payer" checked={payer === 'FACILITY'} onChange={() => { setPayer('FACILITY'); dirty(); }} />
                The facility pays
              </label>
              <p className="pa-text none">Finance sees who pays on the handoff. This is not the same as who manages the resident&apos;s funds.</p>
            </fieldset>
          )}

          <fieldset className="pa-products">
            <legend>Confirm both</legend>
            <label className="pa-check">
              <input type="checkbox" checked={paying} onChange={(e) => { setPaying(e.target.checked); dirty(); }} />
              This sale is paid privately
            </label>
            <label className="pa-check">
              <input type="checkbox" checked={notSpendDown} onChange={(e) => { setNotSpendDown(e.target.checked); dirty(); }} />
              This is not a spend-down (a spend-down still needs a UMED — use Prepare submittal)
            </label>
          </fieldset>

          {confirm && chosen && preview && payer && (
            <div className="pa-confirm">
              <p className="pa-text"><strong>Hand to Finance as private pay?</strong> In one step this records the sale of {chosen.label} at the catalog price of {usd.format(chosen.total_cents / 100)}, paid as {preview} by {payer === 'FAMILY' ? 'the family (or the resident)' : 'the facility'}, closes this lead as private pay, and gives Finance exactly one handoff labelled {PRIVATE_PAY_LABEL}. No UMED is created.</p>
            </div>
          )}

          {/* With no approved catalog the notice above is the reason (one line, not two); the button names it. */}
          <Why id={`why-privatepay-${caseId}`} reason={!armed && Boolean(cat) && firstReason(
            [Boolean(live), 'This case is already with Finance.'],
            [blockers.length > 0, 'Clear what is listed above first.'],
            [true, [!chosen && 'Choose the units sold.', !payer && 'Choose who pays.', !(paying && notSpendDown) && 'Tick both confirmations to continue.'].filter(Boolean).join(' ')],
          )} />
          <div className="btns">
            <button type="button" className="pa-open pa-primary" disabled={busy || !armed} aria-describedby={armed ? undefined : cat ? `why-privatepay-${caseId}` : `why-privatepay-catalog-${caseId}`} onClick={async () => {
              if (!confirm) { setConfirm(true); return; }
              if (!chosen || !payer) return;
              setBusy(true); setErr(null);
              try {
                const r = await advocateApi.privatePayToFinance(caseId, {
                  confirm_private_pay: true, not_spend_down: true, payer, product_code: chosen.code,
                  payment_plan: 'PAID_IN_FULL',
                  version: detail.native?.version ?? 1, idempotency_key: freshKey(),
                });
                if (!r.success || !r.data) { refuse(errText(r)); return; }
                setConfirm(false);
                await onDone(r.data.replayed
                  ? 'This sale is already with Finance as private pay.'
                  : `Private pay — ${usd.format(r.data.totalCents / 100)} handed to Finance (no UMED).`);
              } catch { refuse('Could not reach the server. Nothing was changed.'); } finally { setBusy(false); }
            }}>{busy ? 'Handing off…' : confirm ? 'Confirm: hand to Finance' : 'Hand to Finance as private pay'}</button>
            {confirm && <button type="button" className="pa-open" onClick={() => setConfirm(false)}>Not yet</button>}
          </div>
        </>
      )}
      {err && <p className="pa-notice stop" role="alert">{err}</p>}

      {status && ach && (ach.available
        ? <PrivatePayAchPanel caseId={caseId} ach={ach} reload={reload} />
        : ach.refusal && <p className="pa-text none" role="status">The ACH form cannot be sent from here yet: {ach.refusal.message}</p>)}
    </div>
  );
}

/**
 * On a closed lead: where its private-pay handoff stands, one line, and (when the PA may act and the server says the
 * ACH act is available) the ACH form panel. A failed read renders nothing.
 */
function PrivatePayClosedNote({ caseId, canAch = false }: { caseId: string; canAch?: boolean }) {
  const [s, setS] = useState<PrivatePayStatus | null>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await advocateApi.privatePay(caseId);
        if (live) setS(r.success && r.data ? r.data : null);
      } catch { if (live) setS(null); }
    })();
    return () => { live = false; };
  }, [caseId, tick]);
  const h = s?.handoff ?? null;
  if (!h) return null;
  if (h.returned) return <p className="pa-notice stop" role="status">Finance returned this private-pay handoff on {shortDate(h.returned.at)}{h.returned.reason ? `: ${h.returned.reason}` : '.'} Reopen the lead to fix it and hand it again, or prepare a submittal if it needs a UMED.</p>;
  return (
    <>
      <p className="pa-text none" role="status">{privatePayWhere(h.state)} — {PRIVATE_PAY_LABEL} {h.state === 'CLOSED' ? '· handed' : 'since'} {shortDate(h.handed_at)} · {payerLine(h.payer ?? h.terms?.payer)}</p>
      {canAch && s?.ach?.available && <PrivatePayAchPanel caseId={caseId} ach={s.ach} reload={reload} />}
    </>
  );
}

/**
 * A titled, collapsible act. EP-010 §4.5: the old pane stacked eight forms
 * open at once, so the PA scrolled past seven of them to reach the one they
 * wanted. Each is now folded until asked for, and the one that matters most
 * on this case opens by default.
 */
function Act({ title, children, open: initial = false }: { title: string; children: React.ReactNode; open?: boolean }) {
  const [open, setOpen] = useState(initial);
  return (
    <div className={`pa-act${open ? ' on' : ''}`}>
      <button type="button" className="pa-act-head" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span> {title}
      </button>
      {open && <div className="pa-act-body">{children}</div>}
    </div>
  );
}

/**
 * The acts the caller may take on this case: only what is held and open (the
 * server decides again on every call).
 *
 * `only` narrows the set to one drawer section's acts, so Documents does not
 * offer an SSN field and Identity does not offer a file upload. The
 * capability check is unchanged in every case — narrowing is about where a
 * control lives, never about who may use it.
 */
type ActGroup = 'work' | 'documents' | 'identity';
const ACT_GROUP: Record<CanKey, ActGroup> = {
  // Who manages the funds sits with the work: an advocate records it while
  // they are on the case, not in an identity panel they have to go and find.
  ready_to_submit: 'work', change_status: 'work', add_note: 'work', record_liability: 'work',
  record_financial_responsibility: 'work',
  add_document: 'documents',
  /*
   * The responsible party moved from "Correct the record" to the work panel
   * on 2026-09-24.
   *
   * Dominique Genua: "PA Board, they are unable to edit POA/responsible
   * party information." They could -- PATIENT_ADVOCATE holds
   * advocacy.responsible_party.write and the form was there. It was under a
   * different tab, folded in a disclosure, and nobody found it.
   *
   * Calling the POA is not a correction to a record. It is the work: it is
   * the call the advocate is about to make. So it sits where they already
   * are. A DOB or an SSN really is a correction, and those stay.
   */
  update_responsible_party: 'work',
  correct_dob: 'identity', set_ssn: 'identity', record_medicaid_number: 'identity', transfer_facility: 'identity',
  // 2026-10-05: the resident's building, corrected for every case at once. It replaces "The resident moved buildings".
  correct_facility: 'identity',
};

/**
 * `lead` is what the Work tab shows between the status line and the acts: the
 * current work. It is a prop so the order is one decision made here, and so a
 * caller with no act to offer still gets it back.
 *
 * The Work tab's order is the PA's day, not the data's: where the case stands,
 * what is due, the acts they reach for all day (status, note, the responsible
 * party, a document), and only then Prepare submittal -- the act the case is
 * heading towards, which opens by itself once it is ready.
 */
function WorkTheCase({ detail, scope, onChanged, only, lead, manager = false, onOpenNotes }: {
  detail: CaseDetail; scope: Scope; onChanged: PanelDone; only: ActGroup; lead?: React.ReactNode; manager?: boolean;
  /** Notes are written in the resident's thread (2026-10-05): the Work tab's "Add a note" goes there. */
  onOpenNotes?: () => void;
}) {
  const c = detail.case;
  const [proposal, setProposal] = useState<UmedProposal | null>(null);
  const closed = isClosedLead(c);
  const withUmed = c.stage === 'READY_TO_SUBMIT' || c.stage === 'SUBMITTED';
  // Adding a document is offered on the Work tab as well as on Documents: the PA is on the case, with the file in hand.
  const may = (k: CanKey) => canDo(scope, k) && (ACT_GROUP[k] === only || (k === 'add_document' && only === 'work'));
  const any = (Object.keys(ACT_GROUP) as CanKey[]).some(may);
  // D-31: a closed lead offers ONE working act. Who may is the status capability (the server decides again).
  if (closed && only === 'work') {
    return (
      <section className="pa-section pa-work" aria-label="Work the case">
        <p className="pa-text none">Status: <strong>{detail.native?.status_word ? (STATUS_WORD_LABEL[detail.native.status_word as StatusWord] || detail.native.status_word) : stateLine(c)}</strong>{detail.native?.stage_changed_at ? ` · since ${shortDate(detail.native.stage_changed_at)}` : ''}</p>
        <PrivatePayClosedNote caseId={c.id} canAch={may('ready_to_submit')} />
        {lead && <div className="pa-panel">{lead}</div>}
        {may('change_status') && <div id={`reopen-lead-${c.id}`} tabIndex={-1}><Act title="Reopen this lead" open><ReopenLead detail={detail} onDone={onChanged} manager={manager} /></Act></div>}
      </section>
    );
  }
  if (!any) return lead ? <div className="pa-panel">{lead}</div> : null;
  const readyNow = may('ready_to_submit') && !closed && !withUmed;
  return (
    <section className="pa-section pa-work" aria-label={only === 'work' ? 'Work the case' : only === 'documents' ? 'Add evidence' : 'Correct the record'}>
      {only === 'work' && (
        <p className="pa-text none">Status: <strong>{detail.native?.status_word ? (STATUS_WORD_LABEL[detail.native.status_word as StatusWord] || detail.native.status_word) : stateLine(c)}</strong>{detail.native?.stage_changed_at ? ` · since ${shortDate(detail.native.stage_changed_at)}` : detail.native?.status_source === 'MONDAY' ? ' · date unknown (from the board)' : ''}</p>
      )}
      {lead && <div className="pa-panel">{lead}</div>}
      {may('change_status') && <Act title="Change the status"><StatusForm detail={detail} onDone={onChanged} /></Act>}
      {/* 2026-10-29: its own act, not a word in the status list: it needs the case it duplicates and a note. */}
      {may('change_status') && (
        <Act title="Mark as duplicate">
          <MarkDuplicateForm caseId={c.id} onDone={onChanged} caseLink={(id, label) => <Link to={path({ caseId: id })}>{label}</Link>} />
        </Act>
      )}
      {may('add_note') && onOpenNotes && (
        <div className="pa-act pa-act-link">
          <button type="button" className="pa-act-head" onClick={onOpenNotes}><span aria-hidden="true">✎</span> Add a note <span className="pa-sub-line">in the resident&apos;s notes</span></button>
        </div>
      )}
      {may('update_responsible_party') && <Act title="Responsible party (POA)" open><ResponsiblePartyForm key={`rp:${detail.native?.version ?? 0}`} detail={detail} onDone={onChanged} /></Act>}
      {may('add_document') && <Act title="Add a document" open><UploadForm caseId={c.id} resetKey={detail.documents?.length ?? 0} classes={scope.document_classes || DEFAULT_DOC_CLASSES} onDone={onChanged} /></Act>}
      {canDo(scope, 'ready_to_submit') && canDo(scope, 'add_document') && !closed && only === 'work' && <GenerateUmed caseId={c.id} proposal={proposal} onDone={onChanged} />}
      {readyNow && <PrepareSubmittal detail={detail} onDone={onChanged} onProposal={setProposal} />}
      {readyNow && <PrivatePayToFinance detail={detail} onDone={onChanged} />}
      {may('correct_dob') && <Act title="Correct the date of birth"><DobForm detail={detail} onDone={onChanged} /></Act>}
      {may('set_ssn') && <Act title="Record the SSN"><SsnForm detail={detail} onDone={onChanged} /></Act>}
      {may('record_medicaid_number') && <Act title="Record the Medicaid number"><MedicaidNumberForm detail={detail} onDone={onChanged} /></Act>}
      {may('correct_facility') && <Act title={"Correct the resident's facility"}><CorrectFacilityForm key={`cf:${detail.case.id}`} detail={detail} onDone={onChanged} /></Act>}
    </section>
  );
}

/** Every status with its date, every note, every correction — the case's own record. */
function Chronology({ detail }: { detail: CaseDetail }) {
  const n = detail.native;
  if (!n) return null;
  return (
    <Cell title="Status history" wide>
      {n.transitions.length === 0 ? <p className="pa-text none">No status recorded in eMerge yet.</p> : (
        <ol className="pa-story" aria-label="Status transitions">
          {n.transitions.map((t) => (
            <li key={t.id}>
              <span className="when">{t.effective_at ? shortDate(t.effective_at) : 'date unknown'}</span>
              <span className="what"><strong>{STATUS_WORD_LABEL[t.to_word as StatusWord] || t.to_word}</strong>{t.from_word ? ` (from ${STATUS_WORD_LABEL[t.from_word as StatusWord] || t.from_word})` : ''}{t.actor_name ? ` · ${t.actor_name}` : ''}{t.source === 'MONDAY' ? ' · from the board' : ''}{t.reason ? ` · ${t.reason}` : ''}</span>
            </li>
          ))}
        </ol>
      )}
      {n.notes.length > 0 && (
        <ul className="pa-notes" aria-label="Notes">
          {n.notes.map((x) => <li key={x.id}><span className="pa-text none">{shortDate(x.recorded_at)} · {x.kind === 'VETTING' ? 'Vetting' : 'Note'}{x.recorded_by_name ? ` · ${x.recorded_by_name}` : ''}</span><br />{x.note}</li>)}
        </ul>
      )}
      {n.corrections.length > 0 && (
        <ul className="pa-notes" aria-label="Corrections">
          {n.corrections.map((x) => <li key={x.id}><span className="pa-text none">{shortDate(x.recorded_at)} · {x.field === 'DOB' ? `Date of birth ${x.prior_value ? `${shortDate(x.prior_value)} → ` : ''}${x.new_value ? shortDate(x.new_value) : ''}` : x.field === 'SSN' ? `SSN ${x.new_value === 'REPLACED' ? 'replaced' : 'recorded'} (encrypted)` : 'Facility moved'}{x.actor_name ? ` · ${x.actor_name}` : ''}</span><br />{x.reason}</li>)}
        </ul>
      )}
    </Cell>
  );
}

/**
 * The documents, as a PA reads them: what is on file first, then the files.
 *
 * Isaiah, 2026-10-01: "I need the documents in patient records better
 * designed so that PAs can access the recording and see the available docs."
 *
 * The order is the business's, not the alphabet's: the two the UMED handoff
 * refuses to go without, then the recording a PA is most often looking for,
 * then the rest. A class with nothing in it says so in words; "Missing" is
 * not used, because for most classes nobody has ruled that one is required.
 */
const DOC_ORDER: UploadClass[] = ['UMED', 'AUDIOGRAM', 'RECORDING', 'BOM_VETTING', 'DCF', 'FACE_SHEET', 'ACH'];
/** What Ready to Submit checks (`NOT_READY` names exactly these two). */
const NEEDED_TO_SUBMIT: ReadonlySet<string> = new Set(['UMED', 'AUDIOGRAM']);

const docLabel = (cls: string) => UPLOAD_CLASS_LABEL[cls as UploadClass] || cls.replace(/_/g, ' ').toLowerCase().replace(/^./, (s) => s.toUpperCase());
const docWhen = (d: CaseDocument) => d.source_system === 'MONDAY' ? (d.source_created_at || d.imported_at) : d.created_at;
// Playable here (isPlayable): the type says audio or video, or a recording's file name does -- pages/documents/RecordingPlayer, shared with UMED.
function docKind(d: CaseDocument): string {
  const t = (d.content_type || '').toLowerCase();
  const n = (d.original_filename || '').toLowerCase();
  if (isPlayable(d)) return 'Audio';
  if (t === 'application/pdf' || n.endsWith('.pdf')) return 'PDF';
  if (t.startsWith('image/')) return 'Image';
  if (/word|officedocument/.test(t) || /\.docx?$/.test(n)) return 'Word';
  if (t === 'message/rfc822' || /\.(msg|eml)$/.test(n)) return 'Email';
  return 'File';
}
function docSize(bytes: number): string | null {
  if (!bytes || bytes <= 0) return null;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DocumentsPanel({ caseId, docs, canMark = false, canUndo = false, onMarked }: { caseId: string; docs: CaseDocument[]; canMark?: boolean; canUndo?: boolean; onMarked?: (msg: string) => void }) {
  const byClass = new Map<string, CaseDocument[]>();
  for (const d of docs) {
    const list = byClass.get(d.document_class) || [];
    list.push(d);
    byClass.set(d.document_class, list);
  }
  byClass.forEach((list) => list.sort((a, b) => String(docWhen(b) || '').localeCompare(String(docWhen(a) || ''))));
  // Known classes in the business's order, then anything the importer brought that the list does not name.
  const groups: string[] = [...DOC_ORDER, ...Array.from(byClass.keys()).filter((k) => !DOC_ORDER.includes(k as UploadClass)).sort()];
  const jump = (cls: string) => {
    const el = document.getElementById(`docs-${caseId}-${cls}`);
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };
  return (
    <section className="pa-docpanel" aria-label="Documents on file">
      <ul className="pa-doccheck" aria-label="What is on file">
        {DOC_ORDER.map((cls) => {
          // A document marked "Not this resident's" is shown below, struck through, but it is not on file.
          const list = (byClass.get(cls) || []).filter((d) => !d.misfiled);
          const latest = list[0];
          const gap = !list.length && NEEDED_TO_SUBMIT.has(cls);
          return (
            <li key={cls} className={list.length ? 'on' : gap ? 'gap' : ''}>
              <button type="button" disabled={!list.length} onClick={() => jump(cls)}
                aria-label={`${docLabel(cls)}: ${list.length ? `${list.length} on file` : gap ? 'not on file, needed to submit' : 'not on file'}`}>
                {/* The shape says it as well as the colour: filled is on file, outlined is not. The sentence stays the primary signal. */}
                <span className={`pa-doccheck-glyph ${list.length ? 'solid' : 'outline'}`} aria-hidden="true">{list.length ? '\u25a0' : '\u25a1'}</span>
                <span className="pa-doccheck-label">{docLabel(cls)}</span>
                <span className="pa-doccheck-state">
                  {list.length
                    ? `${list.length} on file${latest && docWhen(latest) ? ` · ${shortDate(docWhen(latest) as string)}` : ''}`
                    : gap ? 'Not on file · needed to submit' : 'Not on file'}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {docs.length === 0
        ? <p className="pa-text none">Nothing on file in eMerge for this resident.</p>
        : groups.filter((cls) => byClass.has(cls)).map((cls) => {
          const list = byClass.get(cls) as CaseDocument[];
          return (
            <section key={cls} className="pa-docgroup" id={`docs-${caseId}-${cls}`} aria-label={`${docLabel(cls)} files`}>
              <h3>{docLabel(cls)} <span className="pa-count">{list.length}</span></h3>
              <ul className="pa-docs" aria-label={`${docLabel(cls)} documents`}>
                {list.map((d) => <DocumentRow key={d.id} caseId={caseId} doc={d} canMark={canMark} canUndo={canUndo} onMarked={onMarked} />)}
              </ul>
            </section>
          );
        })}
    </section>
  );
}

/**
 * One file. A recording plays here, under its own name, rather than in a tab
 * the PA then has to find; everything else opens. Each press asks the server
 * for a fresh link, because every read is audited and a link lives 300 s.
 */
/**
 * What a 410 DOCUMENT_GONE means, said in one fixed sentence. The client drops the
 * body's `error` (it keeps `message` only), so the words live here, as in the UMED workspace.
 */
const DOCUMENT_GONE_SENTENCE = 'eMerge has a record of this document but the file is not in storage. Tell your manager; it was not deleted by you.';

const PA_MARKABLE = ['FACE_SHEET', 'UMED', 'RECORDING', 'DCF', 'AUDIOGRAM', 'BOM_VETTING', 'ACH'];
function DocumentRow({ caseId, doc, canMark = false, canUndo = false, onMarked }: { caseId: string; doc: CaseDocument; canMark?: boolean; canUndo?: boolean; onMarked?: (msg: string) => void }) {
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The shared player (pages/documents/RecordingPlayer): `started` -- an error after playback began is an expired link, not a format.
  const player = useRecordingPlayer();
  const playable = isPlayable(doc);
  const kind = docKind(doc);
  const label = docLabel(doc.document_class);
  const name = doc.original_filename || `${label} (no file name)`;
  const when = docWhen(doc);
  const size = docSize(doc.byte_size);

  const link = async (): Promise<string | null> => {
    setErr(null); setBusy(true); player.retry();
    try {
      const r = await advocateApi.documentUrl(caseId, doc.id);
      if (!r.success || !r.data) {
        // 410: eMerge has the record but the file is not in storage. The server's own sentence is the answer;
        // it is not a format problem, so the player is closed and the "format" message is never shown.
        if (r.code === 'DOCUMENT_GONE') { player.gone(); setErr(DOCUMENT_GONE_SENTENCE); return null; }
        setErr(errText(r)); return null;
      }
      return r.data.url;
    } catch { setErr('Could not reach the server.'); return null; } finally { setBusy(false); }
  };
  const open = async () => { const u = await link(); if (u) window.open(u, '_blank', 'noopener,noreferrer'); };
  const play = async () => { const u = await link(); if (u) player.start(u); };

  return (
    <li className="pa-doc">
      <span className={`pa-dockind${kind === 'Audio' ? ' audio' : ''}`} aria-hidden="true">{kind}</span>
      <div className="pa-doc-body">
        <span className="pa-doc-name" title={name} style={doc.misfiled ? { textDecoration: 'line-through' } : undefined}>{name}</span>
        <span className="pa-doc-meta">
          {[kind, size, doc.source_system === 'MONDAY' ? `from the board${when ? `, ${shortDate(when)}` : ''}` : `added ${shortDate(doc.created_at)}`].filter(Boolean).join(' · ')}
        </span>
        <MisfiledNote misfiled={doc.misfiled} refiled={doc.refiled} undone={doc.undone} tone="pa" />
      </div>
      <div className="pa-doc-acts">
        {/*
          Ruling R3 (2026-10-14): a PA manager may undo the mark; ruling 2026-10-28: so may the PA who made it, their own,
          within 24 hours, while they hold the case and before it went to DCF. Who may is the server's answer on the row
          (can_undo), never worked out here; the server decides again when it is pressed. `canUndo` is only the write gate.
        */}
        {canUndo && doc.misfiled && doc.can_undo === true && (
          <UndoNotThisResidents tone="pa"
            onSubmit={(b) => advocateApi.undoNotThisResidents(caseId, doc.id, { reason: b.reason, idempotency_key: b.idempotencyKey })}
            onDone={(m) => { if (onMarked) onMarked(m || 'The mark was undone.'); }} />
        )}
        {canMark && !doc.misfiled && PA_MARKABLE.includes(doc.document_class) && (
          <NotThisResidents tone="pa"
            onSubmit={(b) => advocateApi.markNotThisResidents(caseId, doc.id, { reason: b.reason, correct_patient_id: b.correctPatientId, idempotency_key: b.idempotencyKey })}
            search={async (q) => {
              const r = await advocateApi.searchResidents({ q });
              return r.success && r.data ? r.data.items.filter((h) => h.name).map((h) => ({ id: h.id, name: h.name as string, facility_name: h.facility_name })) : [];
            }}
            onDone={(m) => { if (onMarked) onMarked(m || 'Marked not this resident\'s.'); }} />
        )}
        {playable && (player.media
          ? <button type="button" className="pa-docbtn" onClick={player.close}>Close player</button>
          : <button type="button" className="pa-docbtn primary" disabled={busy} onClick={play} aria-label={`Play ${name}`}>{busy ? 'Loading…' : 'Play'}</button>)}
        <button type="button" className={`pa-docbtn${playable ? '' : ' primary'}`} disabled={busy} onClick={open} aria-label={`Open ${name}`}>Open</button>
      </div>
      <RecordingAudio player={player} name={name} tone="pa" />
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
    </li>
  );
}

/** The open UMED, if any, is the one whose requirements bite now. */
const openUmed = (list: UmedCase[]): UmedCase | null => list.find((u) => u.stage !== 'CLOSED') || list[0] || null;

/**
 * The resident, by sensitivity (addendum §5). DOB is shown. The SSN is a
 * state, never a value: "needed" when the UMED's readiness says so, and
 * otherwise not tracked in eMerge yet — never "Missing" from a column that
 * is populated on no resident (F-4). No Case # until April names which one
 * (§6, O-17).
 */
function Resident({ c, d, umed }: { c: CaseDetail['case']; d: Demographics | null; umed: UmedCase | null }) {
  const ssnNeeded = Boolean(umed && umed.readiness_blockers.includes('SSN_REQUIREMENT'));
  return (
    <Cell title="Resident">
      <dl className="pa-kv">
        <dt>Date of birth</dt><dd>{d && d.dob ? shortDate(d.dob) : <NotRecorded />}</dd>
        <dt>SSN</dt>
        <dd>{ssnNeeded ? <span className="pa-tag attn">Needed for the UMED</span> : <span className="pa-text none">Not tracked in eMerge yet</span>}</dd>
        <dt>Qualification</dt><dd>{c.qualification_status && c.qualification_status.length ? c.qualification_status.join(', ') : <NotRecorded />}</dd>
      </dl>
    </Cell>
  );
}

/**
 * Two parties, two facts, never one (EP-004C §10). Financial responsibility
 * is who pays the facility, read from the demographics record. The
 * decision-maker is who decides for the resident: the lead's responsible-
 * party record, with the demographics "POA" fields beside it when they are
 * filled. Whether either is the healthcare surrogate is April's ruling to
 * make; until then the screen says where each fact came from.
 */
const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const money = (v: string | number) => usd.format(Number(v));

function Financial({ detail, scope, onChanged, fold }: { detail: CaseDetail; scope: Scope | null; onChanged: PanelDone; fold?: { open: boolean } }) {
  const [edit, setEdit] = useState<'finresp' | 'liability' | null>(null);
  const fr = detail.case.financial_responsibility;
  const ev = detail.evidence;
  const d = detail.demographics;
  // The native fact first -- the same one the header shows. The demographics figure is the board's mirror, and says so.
  const native = detail.liability !== undefined ? detail.liability : detail.case.liability;
  const liability = d && d.patient_liability !== null && d.patient_liability !== undefined && d.patient_liability !== '' ? d.patient_liability : null;
  const holder = Boolean(scope && (scope.manager || detail.viewer?.relationship === 'CURRENT' || detail.case.assigned_advocate_user_id === scope.viewing_user_id));
  const writable = holder && !detail.viewer?.read_only && !isClosedLead(detail.case);
  const canResponsibility = Boolean(writable && scope && canDo(scope, 'record_financial_responsibility'));
  const canLiability = Boolean(writable && scope && canDo(scope, 'record_liability'));
  const c = { ...detail.case, liability: native };
  const saved = async (msg: string) => { setEdit(null); await onChanged(msg); };
  return (
    <Cell title="Financial responsibility" fold={fold}>
      <p className="pa-fr">
        {fr.type
          ? <strong>{FINANCIAL_RESPONSIBILITY_LABEL[fr.type]}</strong>
          : fr.raw && !fr.ach_on_file
            ? <><strong>{fr.raw}</strong> <span className="pa-text none">(a word the board uses that eMerge does not know yet)</span></>
            : <NotRecorded />}
        {fr.ach_on_file && <span className="pa-tag ok">ACH on file</span>}
      </p>
      {canResponsibility && edit !== 'finresp' && <button type="button" className="pa-inline-edit" onClick={() => setEdit('finresp')} aria-label="Change financial responsibility">{fr.type ? 'Change' : 'Enter'}</button>}
      {canResponsibility && edit === 'finresp' && <FinRespEditor c={c} onClose={() => setEdit(null)} onSaved={saved} />}
      <p className="pa-sub-line">
        Patient Liability:{' '}
        {native
          ? <LiabilityValue liability={native} />
          : liability !== null
            ? <><strong>{money(liability)}</strong> <span className="pa-text none">from the board</span>{d?.patient_liability_checked_at ? ` \u00b7 checked ${shortDate(d.patient_liability_checked_at)}` : ' \u00b7 never checked'}</>
            : <NotRecorded />}
      </p>
      {canLiability && edit !== 'liability' && <button type="button" className="pa-inline-edit" onClick={() => setEdit('liability')} aria-label="Change Patient Liability">{native ? 'Change' : 'Enter'}</button>}
      {canLiability && edit === 'liability' && <LiabilityEditor c={c} onClose={() => setEdit(null)} onSaved={saved} />}
      {ev && ev.payor_issues !== null && <p className="pa-sub-line">Payor issues: {ev.payor_issues ? 'yes' : 'no'}</p>}
      <p className="pa-text none">Who pays the facility, and so who pays us. Source: Patient Demographics.</p>
    </Cell>
  );
}

function DecisionMaker({ detail }: { detail: CaseDetail }) {
  const ev = detail.evidence;
  const d = detail.demographics;
  const hasPoa = Boolean(d && (d.poa_name || d.poa_phone || d.poa_email));
  return (
    <Cell title="Decision-maker">
      {ev ? (
        <dl className="pa-kv">
          <dt>Name</dt><dd>{ev.responsible_party_name || <NotRecorded />}</dd>
          <dt>Phone</dt><dd>{ev.responsible_party_phone || <NotRecorded />}</dd>
          <dt>Email</dt><dd>{ev.responsible_party_email || <NotRecorded />}</dd>
        </dl>
      ) : <p className="pa-text none">No lead record yet.</p>}
      {hasPoa && d && (
        <dl className="pa-kv" aria-label="Demographics POA record">
          <dt>POA (demographics)</dt><dd>{[d.poa_name, d.poa_phone, d.poa_email].filter(Boolean).join(' · ')}</dd>
        </dl>
      )}
      <p className="pa-text none">Who decides for the resident. Source: the lead's responsible-party record{hasPoa ? ' and the demographics POA fields' : ''}.</p>
    </Cell>
  );
}

/**
 * Enrollment (addendum §4): the decision as recorded. The date is generated
 * by the UMED workflow's transition, which does not exist yet, so it is said
 * to be absent rather than reconstructed; the cost is derived from a pricing
 * rule that is still PROPOSED, so it is not shown at all.
 */
function Enrollment({ c, fold }: { c: CaseDetail['case']; fold?: { open: boolean } }) {
  const enrolled = c.disposition === 'ENROLLED' || c.stage === 'ENROLLED';
  return (
    <Cell title="Enrollment" fold={fold}>
      {enrolled ? (
        <>
          <p className="pa-lead">Enrolled</p>
          <dl className="pa-kv"><dt>Enrollment date</dt><dd className="pa-text none">Not recorded yet — set by the UMED workflow</dd></dl>
        </>
      ) : <p className="pa-lead none">Not enrolled</p>}
    </Cell>
  );
}

/**
 * The one sentence that must never be wrong (EP-004U): a source claim of
 * approval is never rendered as an approval. "Approved" is the UMED
 * operator's act of moving the case to Approved (2026-10-06), which needs
 * FLMMIS reflected on record; FLMMIS alone is evidence. Anything less says
 * what the source says and that it is not yet proven. The same rule as the
 * UMED workspace.
 */
function umedLine(u: UmedCase): { text: string; note: string | null } {
  const proven = Boolean(u.approved_by_act);
  if (u.stage === 'APPROVED' || u.stage === 'HANDED_TO_FINANCE') {
    return proven
      ? { text: u.stage === 'APPROVED' ? 'Approved' : 'Approved · with Finance', note: u.noca_status === 'RECEIVED' ? 'Approved by the UMED team; FLMMIS reflected and NOCA received.' : 'Approved by the UMED team on FLMMIS reflected; NOCA not received.' }
      : { text: UMED_STAGE_LABEL.APPROVED, note: u.flmmis_status === 'REFLECTED' ? 'FLMMIS is reflected; the UMED team has not moved it to Approved yet.' : 'Approval not proven yet: FLMMIS is not on record as reflected.' };
  }
  return { text: UMED_STAGE_LABEL[u.stage], note: null };
}

function UmedCell({ list, fold }: { list: UmedCase[]; fold?: { open: boolean } }) {
  const u = openUmed(list);
  if (!u) return <Cell title="UMED" fold={fold}><p className="pa-lead none">Not started</p></Cell>;
  const line = umedLine(u);
  const earlier = list.length - 1;
  // Whose date it is (final verification P3): the act's when the UMED team approved it here; otherwise Monday's.
  const actOn = u.approved_by_act ? (u.act_approved_on || u.approved_on) : null;
  const approvedOn = actOn ? ` · approved in eMerge ${shortDate(actOn)}` : u.approved_on ? ` · Monday's approval date ${shortDate(u.approved_on)}` : '';
  return (
    <Cell title="UMED" fold={fold}>
      <p className="pa-lead">{line.text}</p>
      <p className="pa-sub-line">{UMED_TYPE_LABEL[u.umed_type]}{u.submitted_on ? ` · submitted ${shortDate(u.submitted_on)}` : ''}{approvedOn}</p>
      {line.note && <p className="pa-text none">{line.note}</p>}
      {earlier > 0 && <p className="pa-sub-line">{earlier} earlier UMED{earlier === 1 ? '' : 's'} on this resident.</p>}
    </Cell>
  );
}

/**
 * The building, brought to the decision: who to call and who the TM is,
 * without leaving the case. Level 1 is the name in the header; this is
 * level 2. The full facility record stays where it is.
 */
function FacilityCell({ f, fold }: { f: FacilityContext | null; fold?: { open: boolean } }) {
  /*
   * D-30 (Kristian Sims and Talhia Zalvidea, 2026-10-02): the people the
   * workforce calls, by role, kept current by whoever learns of a change.
   * The case brings the directory; the facilities route says whether this
   * caller may change it (and returns the fresh directory after each change).
   * Until it answers yes, the cell offers nothing that writes.
   */
  const [live, setLive] = useState<FacilityDirectory | null>(null);
  const [canWrite, setCanWrite] = useState(false);
  const [edit, setEdit] = useState<ContactEdit | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const facilityId = f?.id || null;
  useEffect(() => {
    setLive(null); setCanWrite(false); setEdit(null); setSaved(null);
    setLoadError(null); setRetryable(false);
    if (!facilityId) return undefined;
    let gone = false;
    advocateApi.facilityContacts(facilityId).then((r) => {
      if (gone) return;
      if (!r || !r.success || !r.data) {
        setLoadError('Could not load the current facility directory. The case contacts are shown; editing is unavailable.');
        setRetryable(!r?.status || r.status >= 500);
        return;
      }
      setLive(r.data.directory);
      setCanWrite(Boolean(r.data.can && r.data.can.write));
    }).catch(() => {
      if (gone) return;
      setLoadError('Could not load the current facility directory. The case contacts are shown; editing is unavailable.');
      setRetryable(true);
    });
    return () => { gone = true; };
  }, [facilityId, attempt]);
  /** Someone changed the building first: show what is current now; the form keeps its refusal on screen. */
  const reload = () => {
    if (!facilityId) return;
    advocateApi.facilityContacts(facilityId).then((r) => { if (r && r.success && r.data) setLive(r.data.directory); }).catch(() => {});
  };

  if (!f) return <Cell title="Facility" fold={fold}><p className="pa-text none">Facility not recorded</p></Cell>;
  const main: MainPhone | null = live ? live.main_phone : (f.main_phone || (f.phone ? { name: null, title: null, email: null, phone: f.phone } : null));
  const lines: FacilityContactLine[] = live ? live.contacts : f.contacts;
  const missing: ContactRole[] = live ? live.missing_roles : (f.missing_roles || []);
  const done = (msg: string) => (dir: FacilityDirectory) => { setLive(dir); setEdit(null); setSaved(msg); };
  const isEditing = (kind: ContactEdit['kind'], key: string) => Boolean(edit && edit.kind === kind && edit.key === key);
  const roleOf = (c: FacilityContactLine): ContactRole => c.role || 'OTHER';

  return (
    <Cell title="Facility" fold={fold}>
      <dl className="pa-kv">
        <dt>Phone</dt>
        <dd>
          {main && main.phone ? <span>{main.phone}</span> : <NotRecorded />}
          {main && main.phone && <ContactProvenance c={main} />}
          {main && main.monday_phone && <span className="pa-contact-prov">Monday’s old number (history only): {main.monday_phone}</span>}
          {canWrite && !isEditing('main', 'main') && (
            <button type="button" className="pa-link" onClick={() => { setSaved(null); setEdit({ kind: 'main', key: 'main' }); }}>Correct the number</button>
          )}
          {canWrite && isEditing('main', 'main') && (
            <ContactForm facilityId={f.id} role="MAIN" replaces={main?.id || null} initial={{ phone: main?.phone || '' }} onStale={reload} onCancel={() => setEdit(null)} onDone={done('Main phone recorded. Everyone sees the new number now.')} />
          )}
        </dd>
        <dt>Email</dt><dd>{f.email || <NotRecorded />}</dd>
        <dt>Territory manager</dt><dd>{f.territory_manager || <NotRecorded />}</dd>
        <dt>Region</dt><dd>{[f.region, f.county].filter(Boolean).join(' · ') || <NotRecorded />}</dd>
        {f.provider_next_visit_date && <><dt>Next visit</dt><dd>{shortDate(f.provider_next_visit_date)}</dd></>}
      </dl>
      {loadError && <p className="pa-notice stop" role="alert">{loadError}{retryable && <button type="button" className="pa-link" onClick={() => setAttempt((value) => value + 1)}>Retry facility contacts</button>}</p>}
      {saved && <p className="pa-notice ok" role="status">{saved}</p>}
      {(lines.length > 0 || canWrite) && (
        <ul className="pa-contacts" aria-label="Facility contacts">
          {lines.map((c, i) => {
            const key = c.id || `${c.name || ''}-${i}`;
            const role = roleOf(c);
            return (
              <li key={key}>
                <span className="pa-contact-role">{CONTACT_ROLE_LABEL[role]}</span>
                <strong>{c.name || 'Name not recorded'}</strong>{c.title && c.title !== CONTACT_ROLE_LABEL[role] ? <span> · {c.title}</span> : null}
                <span className="pa-contact-how">{[c.phone, c.email].filter(Boolean).join(' · ') || 'No phone or email recorded'}</span>
                <ContactProvenance c={c} />
                {canWrite && c.id && !isEditing('edit', key) && !isEditing('replace', key) && !isEditing('end', key) && (
                  <span className="pa-contact-acts">
                    {/* Edit corrects the same person (a typo, a new extension) and keeps where the line came from;
                        Replace says someone else now holds the role. */}
                    <button type="button" className="pa-link" aria-label={`Edit ${CONTACT_ROLE_LABEL[role]} ${c.name || ''}`.trim()} onClick={() => { setSaved(null); setEdit({ kind: 'edit', key }); }}>Edit</button>
                    <button type="button" className="pa-link" aria-label={`Replace ${CONTACT_ROLE_LABEL[role]} ${c.name || ''} with someone else`.trim()} onClick={() => { setSaved(null); setEdit({ kind: 'replace', key }); }}>Replace</button>
                    <button type="button" className="pa-link" aria-label={`Ended: ${CONTACT_ROLE_LABEL[role]} ${c.name || ''}`.trim()} onClick={() => { setSaved(null); setEdit({ kind: 'end', key }); }}>Ended</button>
                  </span>
                )}
                {canWrite && c.id && isEditing('edit', key) && (
                  <ContactForm facilityId={f.id} role={role} mode="EDIT" replaces={c.id}
                    initial={{ name: c.name || '', title: c.title || '', phone: c.phone || '', email: c.email || '', ...(c.notes !== undefined ? { notes: c.notes || '' } : {}) }}
                    onStale={reload} onCancel={() => setEdit(null)} onDone={done(`${CONTACT_ROLE_LABEL[role]} updated. The earlier entry is kept in the history.`)} />
                )}
                {canWrite && c.id && isEditing('replace', key) && (
                  <ContactForm facilityId={f.id} role={role} mode="REPLACE" replaces={c.id} initial={{}}
                    onStale={reload} onCancel={() => setEdit(null)} onDone={done(`${CONTACT_ROLE_LABEL[role]} replaced. The earlier entry is kept in the history.`)} />
                )}
                {canWrite && c.id && isEditing('end', key) && (
                  <EndContactForm facilityId={f.id} contactId={c.id} who={c.name || CONTACT_ROLE_LABEL[role]}
                    onStale={reload} onCancel={() => setEdit(null)} onDone={done(`${c.name || CONTACT_ROLE_LABEL[role]} is no longer listed. The entry is kept in the history.`)} />
                )}
              </li>
            );
          })}
          {canWrite && missing.map((role) => (
            <li key={`add-${role}`} className="pa-contact-missing">
              <span className="pa-contact-role">{CONTACT_ROLE_LABEL[role]}</span>
              <span className="pa-text none">Not recorded</span>
              {!isEditing('add', role)
                ? <button type="button" className="pa-link" aria-label={`Add a contact: ${CONTACT_ROLE_LABEL[role]}`} onClick={() => { setSaved(null); setEdit({ kind: 'add', key: role }); }}>Add a contact</button>
                : <ContactForm facilityId={f.id} role={role} initial={{}} onStale={reload} onCancel={() => setEdit(null)} onDone={done(`${CONTACT_ROLE_LABEL[role]} recorded.`)} />}
            </li>
          ))}
          {canWrite && (
            <li key="add-other" className="pa-contact-missing">
              {!isEditing('add', 'any')
                ? <button type="button" className="pa-link" onClick={() => { setSaved(null); setEdit({ kind: 'add', key: 'any' }); }}>Add another contact</button>
                : <ContactForm facilityId={f.id} role="OTHER" choices={['OTHER', ...missing]} initial={{}} onStale={reload} onCancel={() => setEdit(null)} onDone={done('Contact recorded.')} />}
            </li>
          )}
        </ul>
      )}
      {!canWrite && missing.length > 0 && lines.length > 0 && (
        <p className="pa-sub-line">Not recorded: {missing.map((r) => CONTACT_ROLE_LABEL[r]).join(', ')}</p>
      )}
    </Cell>
  );
}

type ContactEdit = { kind: 'main' | 'edit' | 'replace' | 'end' | 'add'; key: string };
/** Refusals that mean "someone changed this building first": the cell re-reads what is current. */
const STALE_CODES: ReadonlySet<string> = new Set(['STALE', 'ALREADY_ENDED', 'ROLE_HAS_CONTACT', 'ROLE_MISMATCH']);
/** A contact refusal in the server's own words: its STALE is about the contact, not the case. */
const contactErrText = (r: { code?: string; message?: string; error?: string; status?: number }): string => (
  r.code === 'STALE' && (r.message || r.error) ? String(r.message || r.error) : errText(r as Parameters<typeof errText>[0])
);

/** Quietly, under each line: where it came from. */
function ContactProvenance({ c }: { c: FacilityContactLine }) {
  if (c.source === 'EMERGE') {
    return (
      <span className="pa-contact-prov">
        {c.origin_system === 'MONDAY' ? 'Adopted from Monday · kept by ' : 'Recorded by '}{c.recorded_by_name || 'someone in eMerge'}{c.effective_from ? ` on ${shortDateTime(c.effective_from)}` : ''}{c.reason ? ` · ${c.reason}` : ''}
      </span>
    );
  }
  // 2026-10-05: Monday no longer changes it; changing it here takes it over.
  if (c.source === 'MONDAY') return <span className="pa-contact-prov">From Monday</span>;
  // A number on a building with no Monday item: not Monday's; correcting it keeps it in eMerge.
  if (c.source === 'LEGACY') return <span className="pa-contact-prov">On the building record before eMerge kept contacts</span>;
  return null;
}

/** Add, edit (same person), replace (someone else), or correct the main number. Every change says why, or how the person learned of it. */
function ContactForm({ facilityId, role: initialRole, mode, replaces = null, initial, choices = null, onCancel, onDone, onStale }: {
  facilityId: string; role: ContactRole; replaces?: string | null;
  /** EDIT keeps the line (and its Monday provenance); REPLACE ends it for someone else; omitted for an add or the main number. */
  mode?: 'EDIT' | 'REPLACE';
  /** The roles a new contact may take: OTHER and the roles nobody holds. A held role is changed, not added to. */
  choices?: ContactRole[] | null;
  /** notes: present when the form was shown the line's notes. An Edit that was not, and is not typed into, leaves them as they are. */
  initial: { name?: string; title?: string; phone?: string; email?: string; notes?: string };
  onCancel: () => void; onDone: (dir: FacilityDirectory) => void; onStale?: () => void;
}) {
  const whyContactId = useWhyId('why-contact');
  const [role, setRole] = useState<ContactRole>(initialRole);
  const [name, setName] = useState(initial.name || '');
  const [title, setTitle] = useState(initial.title || '');
  const [phone, setPhone] = useState(initial.phone || '');
  const [email, setEmail] = useState(initial.email || '');
  const [notes, setNotes] = useState(initial.notes || '');
  const [notesTouched, setNotesTouched] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [key] = useState(freshKey);
  const isMain = role === 'MAIN';
  // An Edit never clears notes it was not shown: the server keeps a field the request leaves out.
  const sendNotes = !isMain && (mode !== 'EDIT' || initial.notes !== undefined || notesTouched);
  const label = isMain ? 'Correct the main number' : mode === 'EDIT' ? `Edit the ${CONTACT_ROLE_LABEL[role]}` : replaces ? `Replace the ${CONTACT_ROLE_LABEL[role]}` : `Add a contact: ${CONTACT_ROLE_LABEL[role]}`;
  const ready = reason.trim().length >= 3 && (isMain ? phone.trim() !== '' : name.trim() !== '');
  return (
    <form className="pa-form pa-contact-form" aria-label={label} onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setErr(null);
      try {
        const r = await advocateApi.recordFacilityContact(facilityId, {
          role, mode: isMain ? undefined : (mode || (replaces ? 'REPLACE' : 'ADD')), name: isMain ? null : name.trim(), title: isMain ? null : (title.trim() || null), phone: phone.trim() || null,
          email: isMain ? null : (email.trim() || null), ...(sendNotes ? { notes: notes.trim() || null } : {}), reason: reason.trim(), replaces_contact_id: replaces, idempotency_key: key,
        });
        if (!r.success || !r.data) { setErr(contactErrText(r)); if (STALE_CODES.has(String(r.code))) onStale?.(); return; }
        onDone(r.data.directory);
      } catch { setErr('Could not confirm this with the server. Try again: a retry will not record it twice.'); } finally { setBusy(false); }
    }}>
      {choices && (
        <label><span>Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as ContactRole)} aria-label="Contact role">
            {choices.map((r) => <option key={r} value={r}>{CONTACT_ROLE_LABEL[r]}</option>)}
          </select>
        </label>
      )}
      {!isMain && <label><span>Name</span><input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} aria-label="Contact name" /></label>}
      {!isMain && <label><span>Title (optional)</span><input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} aria-label="Contact title" placeholder={role === 'OTHER' ? 'e.g. Receptionist' : CONTACT_ROLE_LABEL[role]} /></label>}
      <label><span>{isMain ? 'Main number' : 'Phone'}</span><input value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} inputMode="tel" aria-label={isMain ? 'Main number' : 'Contact phone'} placeholder="813-555-0100" /></label>
      {!isMain && <label><span>Email</span><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254} aria-label="Contact email" /></label>}
      {!isMain && <label><span>Notes (optional)</span><textarea value={notes} onChange={(e) => { setNotes(e.target.value); setNotesTouched(true); }} maxLength={1000} rows={2} /></label>}
      <label><span>Why / how you learned of it</span><input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} aria-label="Why or how you learned of it" placeholder="e.g. The front desk told me on 10/2" /></label>
      <div className="btns">
        <button type="submit" className="pa-open pa-primary" disabled={busy || !ready} aria-describedby={!ready ? whyContactId : undefined}>{busy ? 'Recording…' : 'Record'}</button>
        <Why id={whyContactId} reason={!ready && firstReason([isMain ? !phone.trim() : !name.trim(), isMain ? 'Enter the phone number first.' : 'Enter the name first.'], [reason.trim().length < 3, 'Say where it came from (at least 3 characters) first.'])} />
        <button type="button" className="pa-link" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
    </form>
  );
}

/** "No longer there." The entry stays in the history; the reason is required. */
function EndContactForm({ facilityId, contactId, who, onCancel, onDone, onStale }: {
  facilityId: string; contactId: string; who: string; onCancel: () => void; onDone: (dir: FacilityDirectory) => void; onStale?: () => void;
}) {
  const whyContactEndedId = useWhyId('why-contact-ended');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [key] = useState(freshKey);
  return (
    <form className="pa-form pa-contact-form" aria-label={`${who} is no longer there`} onSubmit={async (e) => {
      e.preventDefault(); setBusy(true); setErr(null);
      try {
        const r = await advocateApi.endFacilityContact(facilityId, contactId, reason.trim(), key);
        if (!r.success || !r.data) { setErr(contactErrText(r)); if (STALE_CODES.has(String(r.code))) onStale?.(); return; }
        onDone(r.data.directory);
      } catch { setErr('Could not confirm this with the server. Try again: a retry will not record it twice.'); } finally { setBusy(false); }
    }}>
      <label><span>Why is {who} no longer the contact?</span><input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="e.g. Left the building in September" /></label>
      <div className="btns">
        <button type="submit" className="pa-open pa-primary" disabled={busy || reason.trim().length < 3} aria-describedby={reason.trim().length < 3 ? whyContactEndedId : undefined}>{busy ? 'Recording…' : 'Record as ended'}</button>
        <Why id={whyContactEndedId} reason={reason.trim().length < 3 && 'Say why (at least 3 characters) first.'} />
        <button type="button" className="pa-link" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
    </form>
  );
}

/**
 * The notes, in brief, on the Work tab (2026-10-05). This cell used to show
 * ONLY Monday's text, so "Note recorded." appeared and the cell a PA was
 * looking at did not change. Now it shows the newest eMerge note and opens the
 * resident's whole thread; Monday's text stays, labelled as history.
 */
function Notes({ ev, latest, fold, onOpenNotes }: { ev: LeadEvidence | null; latest: WorkCase['latest_note'] | null; fold?: { open: boolean }; onOpenNotes: () => void }) {
  return (
    <Cell title="Notes" fold={fold}>
      {latest
        ? (
          <div className="pa-notes-peek">
            <p className="pa-text">{latest.preview}{latest.truncated ? '…' : ''}</p>
            <p className="pa-sub-line">{latest.kind === 'VETTING' ? 'Vetting' : 'Note'} · {shortDate(latest.recorded_at)}{latest.recorded_by_name ? ` · ${latest.recorded_by_name}` : ''}</p>
          </div>
        )
        : <p className="pa-text none">No eMerge note on this case yet.</p>}
      <button type="button" className="pa-open pa-notes-open" onClick={onOpenNotes}>Open every note for this resident</button>
      {ev && (ev.notes || ev.reason_for_dnq || ev.reason_for_nitwl) && (
        <div className="pa-notes-monday">
          <span className="pa-sub-line">From Monday (history)</span>
          {ev.notes && <p className="pa-text">{ev.notes}</p>}
          {ev.reason_for_dnq && <p className="pa-text"><strong>DNQ reason:</strong> {ev.reason_for_dnq}</p>}
          {ev.reason_for_nitwl && <p className="pa-text"><strong>Not-interested reason:</strong> {ev.reason_for_nitwl}</p>}
        </div>
      )}
    </Cell>
  );
}

/** The story is what happened to the case, not the projector's bookkeeping. */
const STORY_EVENT_LABEL: Record<string, string> = {
  CASE_OPENED: 'Case opened', ASSIGNED: 'Assigned', ASSIGNMENT_CHANGED: 'Reassigned', STAGE_CHANGED: 'Stage changed',
  DISPOSITION_CHANGED: 'Decision recorded', FOLLOW_UP_RECORDED: 'Follow-up recorded', FOLLOW_UP_SCHEDULED: 'Follow-up scheduled',
  VETTING_UPDATED: 'Vetting updated', DOCUMENTATION_REQUESTED: 'Documentation requested', UMED_REQUESTED: 'UMED requested',
  UMED_STATUS_RECEIVED: 'UMED status received', REVERSAL_RECEIVED: 'Reversal received', CASE_CLOSED: 'Case closed', CASE_REOPENED: 'Case reopened',
  PRIVATE_PAY_ACH_FORM_SEND_CLAIMED: 'ACH form send started', PRIVATE_PAY_ACH_FORM_SENT: 'ACH form sent to the payer',
  PRIVATE_PAY_ACH_FORM_SEND_FAILED: 'ACH form not sent', PRIVATE_PAY_ACH_FORM_HANDLED_OUTSIDE: 'ACH form handled outside eMerge',
};
const STORY_NOISE = new Set(['ACTION_OPENED', 'ACTION_RESOLVED', 'SOURCE_UPDATED']);
const storyEvents = (events: CaseEvent[]): CaseEvent[] => events.filter((e) => !STORY_NOISE.has(e.event_type));

function Story({ events }: { events: CaseEvent[] }) {
  const told = storyEvents(events);
  return (
    <Cell title="Story" wide>
      {told.length === 0 ? <p className="pa-text none">Nothing recorded yet</p> : (
        <ol className="pa-story">
          {told.map((e) => (
            <li key={e.id}>
              <span className="when">{shortDate(e.occurred_at)}</span>
              <span className="what">
                <strong>{STORY_EVENT_LABEL[e.event_type] || e.event_type.toLowerCase().replace(/_/g, ' ')}</strong>
                <span> · {e.source_system === 'MONDAY' ? 'from the board' : e.actor_name || 'eMerge'}</span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </Cell>
  );
}

/**
 * Where a fact came from, for a reviewer: the board's word and file word,
 * its raw dates, the transfer note, the UMED request rows. Progressive
 * disclosure, the same as the UMED workspace's; rendered only when the
 * server sent it.
 */
function SourceDetails({ p }: { p: Provenance }) {
  const [open, setOpen] = useState(false);
  const b = p.board;
  return (
    <Cell title="Source details" wide>
      <button type="button" className="pa-link" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide' : 'Show'} where this came from
      </button>
      {open && (
        <>
          <dl className="pa-kv">
            <dt>Source</dt><dd>{p.source_system} · {p.source_record_type}</dd>
            <dt>Vocabulary</dt><dd>v{p.vocabulary_version} (provisional)</dd>
            <dt>Board status</dt><dd>{p.source_status_raw || <NotRecorded />}</dd>
            <dt>Board file status</dt><dd>{p.source_file_status_raw || (b && b.file_status) || <NotRecorded />}</dd>
            {b && (
              <>
                <dt>Board dates</dt>
                <dd>assigned {shortDate(b.date_assigned)} · last follow-up {shortDate(b.last_follow_up_date)} · POA follow-up {shortDate(b.poa_follow_up_date)}{b.date_closed ? ` · closed ${shortDate(b.date_closed)}` : ''}</dd>
                <dt>Reversal notification</dt><dd>{b.reversal_notification_status || <NotRecorded />}</dd>
                <dt>SFS rep</dt><dd>{b.sfs_rep || <NotRecorded />}</dd>
              </>
            )}
            <dt>Assignment</dt>
            <dd>{p.assignment_source === 'MONDAY' ? 'still mirroring the board' : 'eMerge-owned'}{p.assignment_note ? ` · ${p.assignment_note}` : ''}{p.assignment_transferred_at ? ` · transferred ${shortDateTime(p.assignment_transferred_at)}` : ''}</dd>
          </dl>
          {p.umed_requests.length > 0 && (
            <dl className="pa-kv" aria-label="UMED request rows on the board">
              {p.umed_requests.map((u) => (
                <React.Fragment key={u.id}>
                  <dt>{u.status || 'No status'}</dt>
                  <dd>{u.submitted_date ? `submitted ${shortDate(u.submitted_date)}` : 'not submitted'}{u.sent_to_dcf_date ? ` · to DCF ${shortDate(u.sent_to_dcf_date)}` : ''}{u.approval_date ? ` · approved ${shortDate(u.approval_date)}` : ''}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}
        </>
      )}
    </Cell>
  );
}

/** What an assignment came to: `message` is shown by the control itself when it stays on screen (a refusal). */
export interface AssignOutcome { ok: boolean; message: string | null }
/** An assign handler: the shell's (an outcome) or an older one that answers only true / false (the Manifest desk). */
export type AssignHandler = (advocateUserId: string | null, note: string, expectedFrom: string | null) => Promise<AssignOutcome | boolean>;

/**
 * The assign refusals, in words (2026-10-30). CASE_CLOSED is also caught before the press: a closed lead shows
 * "reopen it first" instead of a button that can only be refused.
 */
export function assignRefusalWords(r: { code?: string; status?: number; error?: string; message?: string }): string {
  switch (r.code) {
    case 'CASE_CLOSED': return 'This lead is closed: reopen it first, then assign it.';
    case 'ACCOUNT_DEACTIVATED': return 'That account is deactivated. Pick an active Patient Advocate.';
    case 'NOT_AN_ADVOCATE': return 'That account is not a Patient Advocate yet, so it cannot hold a case.';
    case 'RESIDENT_HAS_ANOTHER_PA': return r.error || r.message || "This resident's other open case is with another PA, so it was not moved.";
    case 'FIELD_INVALID': return 'Pick the advocate again from the list.';
    case 'NOT_FOUND': return 'That case or account no longer exists. Reload the case.';
    case 'UNAUTHENTICATED': return 'Your sign-in has ended. Sign in again; nothing was changed.';
    default: break;
  }
  if (r.status === 403 || (r.code && /DENY/.test(r.code))) return 'Your account may not assign this case.';
  if (r.status && r.status >= 500) return 'The server could not finish this. Nothing was changed; try again in a minute.';
  return r.error || r.message || 'The server refused this assignment. Nothing was changed.';
}

function AssignControl({ detail, advocates, onAssign, onReopen }: { detail: CaseDetail; advocates: Advocate[]; onAssign: AssignHandler; onReopen?: () => void }) {
  // Fresh state per case and per current holder: the parent keys this
  // component on both, so a change remounts it rather than an effect
  // resetting state after the manager has already touched the controls.
  const current = detail.case.assigned_advocate_user_id || '';
  const [to, setTo] = useState(current);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  // A refusal the control can show where the manager is looking (fix round 5: on a phone the shell's
  // notice was behind the full-screen panel). A success or a conflict re-reads the case, which
  // remounts this control; the panel's own notice says those.
  const [said, setSaid] = useState<string | null>(null);
  const changed = to !== current;
  // The server refuses to assign a CLOSED case (CASE_CLOSED); say so before the press, with the way out.
  const closed = detail.case.stage === 'CLOSED';
  const whyId = `why-assign-${detail.case.id}`;
  const why = changed ? null : current ? 'Pick someone other than the current holder to assign.' : 'Pick an advocate to assign this case to.';
  return (
    <div className="pa-section pa-assign">
      <h3>Assignment</h3>
      <p className="pa-text">{detail.case.assignee_name ? `Held by ${detail.case.assignee_name}` : 'Nobody holds this case'}{detail.case.assignment_source === 'MONDAY' ? ' · still mirroring the board' : ''}</p>
      {detail.case.assignment_note && <p className="pa-text none">{detail.case.assignment_note}</p>}
      {closed ? (
        <p className="pa-text" role="status">
          This lead is closed: reopen it first.{' '}
          {onReopen ? <button type="button" className="pa-link" onClick={onReopen}>Go to Reopen this lead</button> : 'A PA with status rights reopens it from Work.'}
        </p>
      ) : (
        <>
          <AdvocateSelect advocates={advocates} value={to} onChange={(v) => { setTo(v); setSaid(null); }} allowNone label="Assign this case to" />
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why (optional, kept on the record)" aria-label="Assignment note" maxLength={2000} />
          {/* aria-disabled, not disabled, while it saves: a disabled button drops the keyboard's focus to the page. */}
          <button type="button" className="pa-open pa-primary" disabled={!changed} aria-disabled={busy || undefined} aria-describedby={why ? whyId : undefined}
            onClick={async () => {
              if (busy) return;
              setBusy(true); setSaid(null);
              const got = await onAssign(to || null, note, current || null);
              const r: AssignOutcome = typeof got === 'boolean' ? { ok: got, message: null } : got;
              setBusy(false);
              if (r.ok) setNote(''); else setSaid(r.message);
            }}>
            {busy ? 'Saving…' : to ? 'Assign' : 'Take back'}
          </button>
          <Why id={whyId} reason={why} />
        </>
      )}
      {said && <p className="pa-notice stop" role="alert">{said}</p>}
      {detail.case.assigned_at && <p className="pa-text none">Assigned {shortDateTime(detail.case.assigned_at)}</p>}
    </div>
  );
}

/* ── Monthly sales (EP-009) ───────────────────────────────────────────── */

function MonthNav({ month, onMonth }: { month: string; onMonth: (m: string) => void }) {
  const now = currentMonth();
  const future = month >= now;
  return (
    <div className="pa-monthnav" role="group" aria-label="Month">
      <button type="button" className="pa-open" onClick={() => onMonth(shiftMonth(month, -1))} aria-label="Previous month">‹ {monthName(shiftMonth(month, -1))}</button>
      <strong aria-live="polite">{monthName(month)}</strong>
      {month !== now && <button type="button" className="pa-open" onClick={() => onMonth(now)}>Current month</button>}
      <button type="button" className="pa-open" disabled={future} onClick={() => onMonth(shiftMonth(month, 1))} aria-label="Next month">{monthName(shiftMonth(month, 1))} ›</button>
    </div>
  );
}

// Prior-month is the fact the business asked to see, so it carries the attention tone; the two others are plain.
const ORIGIN_TONE: Record<SalesRow['assignment_origin'], string> = { PRIOR_MONTH: 'pa-tag attn', SAME_MONTH: 'pa-tag ok', UNKNOWN: 'pa-tag' };

/**
 * Enrolment on a sale row, and what it rests on.
 *
 * `e` is optional because a server one deploy behind sends rows without it,
 * and a missing enrolment must read as "not enrolled" rather than take the
 * whole month's sales down. This is the shape of bug that turns a deploy-order
 * mistake into a blank page for an entire team.
 */
function EnrollmentCell({ e }: { e?: SalesRow['enrollment'] }) {
  const state = e?.state || 'NOT_ENROLLED';
  if (state !== 'ENROLLED') return <span className={state === 'NOT_ENROLLED_FINAL' ? 'pa-tag stop' : 'pa-tag'}>{ENROLLMENT_LABEL[state]}</span>;
  const how = e?.basis === 'VA_CONFIRMED' ? ' · confirmed by a VA' : e?.basis === 'FLMMIS_REFLECTED' ? ' · FLMMIS reflected' : '';
  return (
    <>
      <span className="pa-tag ok">Enrolled</span>
      {e?.confirmed_at && <span className="pa-sale-sub">{shortDate(e.confirmed_at)}{how}</span>}
    </>
  );
}

function SalesView({ data, selected, onMonth, onOpen, whose }: { data: SalesPage; selected: string | null; onMonth: (m: string) => void; onOpen: (id: string) => void; whose: string }) {
  const s = data.summary;
  const monthLabel = monthName(data.month);
  return (
    <section className="pa-card" aria-label="My sales">
      <div className="pa-card-head">
        <div><h2>My sales</h2><p className="why">Sales closed in {monthLabel}, credited to {whose === 'your' ? 'you' : whose.replace(/'s$/, '')} · credited to whoever held the case when it closed (PA-SALES-R1)</p></div>
        <MonthNav month={data.month} onMonth={onMonth} />
      </div>
      <dl className="pa-kpis" aria-label="Summary">
        <div><dt>Sales closed</dt><dd>{s.sales_closed}</dd></div>
        <div><dt>Sale value</dt><dd>{money(s.sale_value)}{s.amount_pending ? <span className="pa-kpi-note">{s.amount_pending} amount{s.amount_pending === 1 ? '' : 's'} pending</span> : null}</dd></div>
        {/* EP-010 §8: enrolment is a state on these same sales, never an
            addition to them, so it is a subset of Sales closed and is shown
            that way rather than as its own total. */}
        <div><dt>Enrolled</dt><dd>{s.enrolled ?? 0}<span className="pa-kpi-note">of {s.sales_closed}</span></dd></div>
        <div><dt>Prior-month assignments</dt><dd>{s.prior_month_assignments}{s.origin_unknown ? <span className="pa-kpi-note">{s.origin_unknown} unknown</span> : null}</dd></div>
      </dl>
      {Boolean(s.pricing_rule_required) && (
        <div className="pa-note">
          <span>
            <strong>{s.pricing_rule_required} sale{s.pricing_rule_required === 1 ? ' has' : 's have'} no amount because no price catalog was approved when {s.pricing_rule_required === 1 ? 'it' : 'they'} closed.</strong>{' '}
            The sale is recorded and credited; the figure is not invented. It appears once a pricing version is in effect and the amount is recorded against it.
          </span>
        </div>
      )}
      {data.items.length === 0 ? (
        <Empty>No sales closed in {monthLabel} yet.</Empty>
      ) : (
        <div className="pa-tablewrap">
          <table className="pa-table" aria-label={`Sales closed in ${monthLabel}`}>
            <thead><tr><th>Resident</th><th>Facility</th><th>Date assigned</th><th>Date closed</th><th>Sale amount</th><th>Units &amp; terms</th><th>Enrollment</th><th>Assignment origin</th><th>Current state</th><th><span className="sr-only">Open</span></th></tr></thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.id} className={selected === r.case_id ? 'on' : ''}>
                  <td><strong>{r.resident || 'Resident (name not on file)'}</strong>{r.reversed_at ? <span className="pa-tag stop">Reversed</span> : null}</td>
                  <td>{r.facility_name || <NotRecorded />}</td>
                  <td>{r.first_assigned_at ? <>{shortDate(r.first_assigned_at)}{r.first_assigned_confidence === 'SOURCE_REPORTED' ? <span className="pa-kpi-note" title="From the board's Date Assigned">board</span> : null}</> : <span className="pa-text none">Unavailable</span>}</td>
                  <td>{shortDate(r.sale_closed_at)}{r.sale_close_confidence === 'SOURCE_REPORTED' ? <span className="pa-kpi-note" title="From the board's Date Closed">board</span> : null}</td>
                  {/* EP-010: the amount says where it came from. A native
                      figure is the approved catalog's and carries its version;
                      a projected one is still the board's Closed Lead Total
                      and still says so. */}
                  <td>
                    {r.sale_amount === null
                      ? <span className="pa-text none" title={r.amount_pending_reason === 'PRICING_RULE_REQUIRED' ? 'No price catalog was approved when this closed. The figure is not invented.' : undefined}>Amount pending</span>
                      : <>{money(r.sale_amount)}<span className="pa-kpi-note" title={r.amount_source === 'NATIVE_PRICING_CATALOG' ? `Derived from ${r.pricing_version}` : r.amount_source === 'APPROVED_OVERRIDE' ? 'An approved override, with a reason on the record' : 'Closed Lead Total on the board'}>{r.amount_source === 'NATIVE_PRICING_CATALOG' ? 'catalog' : r.amount_source === 'APPROVED_OVERRIDE' ? 'override' : 'board'}</span></>}
                  </td>
                  <td>
                    {r.product ? r.product.label : <span className="pa-text none">Not recorded</span>}
                    {r.term && r.term.status === 'RESOLVED' && r.term.months !== undefined
                      ? <span className="pa-sale-sub">{r.term.months} mo at {money((r.term.monthly_cents || 0) / 100)}</span>
                      : r.term ? <span className="pa-sale-sub">Term not resolved</span> : null}
                  </td>
                  <td><EnrollmentCell e={r.enrollment} /></td>
                  <td><span className={ORIGIN_TONE[r.assignment_origin]}>{ORIGIN_LABEL[r.assignment_origin]}</span></td>
                  <td>{stateLine(r)}{r.flmmis_status === 'REFLECTED' ? ' · FLMMIS reflected' : r.umed_stage ? ` · UMED ${r.umed_stage.toLowerCase().replace(/_/g, ' ')}` : ''}</td>
                  <td><button type="button" className="pa-open" aria-pressed={selected === r.case_id} onClick={() => onOpen(r.case_id)} aria-label={`Open ${r.resident || 'case'}`}>Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ── All cases (EP-009) ───────────────────────────────────────────────── */

function HistoryList({ data, selected, q, onFilter, onOpen, onPage, onClearSearch }: {
  data: HistoryPage; selected: string | null; q: string; onFilter: (f: HistoryFilter) => void; onOpen: (id: string) => void; onPage: (p: number) => void; onClearSearch: () => void;
}) {
  const span = (c: HistoryRow) => {
    const from = c.my_first_assigned_at ? shortDate(c.my_first_assigned_at) : 'date unavailable';
    return c.relationship === 'CURRENT' ? `Current owner · since ${from}` : `Former assignment · ${from} – ${c.my_last_unassigned_at ? shortDate(c.my_last_unassigned_at) : 'unknown'}`;
  };
  return (
    <section className="pa-card" aria-label="All cases">
      <div className="pa-card-head">
        <div><h2>All cases</h2><p className="why">Every case you have ever held — current or former, open or closed.</p></div>
        <span className="pa-count">{data.ever_held}</span>
      </div>
      <div className="pa-lenses" role="group" aria-label="Show">
        {data.filters.map((f) => (
          <button key={f.key} type="button" className={`pa-lens${data.filter === f.key ? ' on' : ''}`} aria-pressed={data.filter === f.key} onClick={() => onFilter(f.key)}>{f.label}<span className="pa-count">{f.count}</span></button>
        ))}
      </div>
      {data.items.length === 0 ? (
        <div className="pa-empty">
          <p>{data.ever_held === 0 ? 'No cases have been assigned to you yet.' : q ? 'No case you have held matches this search.' : 'No case you have held matches this filter.'}</p>
          <div className="btns">
            {q && <button type="button" className="pa-open" onClick={onClearSearch}>Clear search</button>}
            {data.filter !== 'all' && <button type="button" className="pa-open" onClick={() => onFilter('all')}>Show all</button>}
          </div>
        </div>
      ) : (
        <ul className="pa-rows" aria-label="Cases you have held">
          {data.items.map((c) => (
            <li key={c.id} className={`pa-row${selected === c.id ? ' on' : ''}`}>
              <div className="who">
                <strong>{personName(c)}</strong>
                <span><BuildingOrClinic row={c} fallback="Facility not recorded" /></span>
              </div>
              <div className="state"><strong>{stateLine(c)}</strong><span className={c.relationship === 'FORMER' ? 'pa-former' : ''}>{span(c)}</span></div>
              <div className="next">
                <strong>{c.last_event ? c.last_event.type.toLowerCase().replace(/_/g, ' ') : 'No activity recorded'}</strong>
                <span>{c.last_event ? shortDateTime(c.last_event.at) : c.twl_effective_at ? `TWL ${shortDate(c.twl_effective_at)}` : c.intake_observed_at ? `TWL first seen in CounselEAR ${shortDate(c.intake_observed_at)}` : ''}</span>
              </div>
              <button type="button" className="pa-open" aria-pressed={selected === c.id} onClick={() => onOpen(c.id)}>Open</button>
            </li>
          ))}
        </ul>
      )}
      <Pager page={data.page} limit={data.limit} total={data.total} onPage={onPage} />
    </section>
  );
}

/**
 * EP-010 §4.5. Work is where a PA lives; the other three are where they go
 * when the routine act is not the one they need. Ordered by how often a PA
 * opens them, not by how the data is modelled.
 */
type DrawerSection = 'work' | 'notes' | 'documents' | 'identity' | 'history';
const DRAWER_SECTIONS: ReadonlyArray<{ key: DrawerSection; label: string }> = [
  { key: 'work', label: 'Work' },
  // 2026-10-05: the resident's whole note thread, every case and episode, second only to the work.
  { key: 'notes', label: 'Notes' },
  { key: 'documents', label: 'Documents' },
  { key: 'identity', label: 'Identity' },
  { key: 'history', label: 'History' },
];

/**
 * Three facts a PA needs before they touch anything, under the name: what the
 * resident can carry, who manages the funds, and what is due next. The
 * liability is the native fact (never the board's mirror) and says whether
 * Finance has verified it; absent is "Not checked", which is not zero.
 */
function LiabilityValue({ liability }: { liability: LiabilityFact }) {
  return (
    <>
      <strong className="pa-money">{cents(liability.amount_cents)}</strong>
      {liability.classification && <span> · {LIABILITY_CLASS_LABEL[liability.classification]}</span>}
      {liability.verification_status === 'VERIFIED' && <span className="pa-tag ok">Verified</span>}
      {liability.verification_status === 'CORRECTION_PENDING' && <span className="pa-tag attn">Correction pending</span>}
    </>
  );
}

function CaseFacts({ c, liability }: { c: CaseDetail['case']; liability?: LiabilityFact | null }) {
  const fr = c.financial_responsibility;
  const due = dueWords(c.next_action);
  return (
    <dl className="pa-head-facts" aria-label="Case summary">
      <div>
        <dt>Patient Liability</dt>
        <dd>
          {liability
            ? <LiabilityValue liability={liability} />
            : <span className="pa-text none">Not checked</span>}
        </dd>
      </div>
      <div>
        <dt>Financial responsibility</dt>
        <dd>
          {fr && fr.type
            ? <strong>{FINANCIAL_RESPONSIBILITY_LABEL[fr.type]}</strong>
            : fr && fr.raw && !fr.ach_on_file
              ? <strong>{fr.raw}</strong>
              : <span className="pa-text none">Not recorded</span>}
          {fr && fr.ach_on_file && <span className="pa-tag ok">ACH on file</span>}
        </dd>
      </div>
      <div>
        <dt>Next action</dt>
        <dd>
          {c.next_action
            // The action and its date are two items of the row (its gap separates them), so no separator dot
            // is ever inside the past-due pill or at the start of a wrapped line (fix round 7).
            ? <><strong>{ACTION_LABEL[c.next_action.action_type]}</strong><span className="pa-sr-only">, due </span><DueWords due={due} /></>
            : <span className="pa-text none">No open action</span>}
        </dd>
      </div>
    </dl>
  );
}

/**
 * The signed send-back (docs/record-flow/send-back-design.md §3.1), from the
 * case. "Send back" is offered only when the server's scope says this caller
 * may (`scope.can.send_back`: capability held, gate open, key configured) and
 * the case is theirs; the dialog's preview and the server decide again.
 * "Mark handled" answers a RETURNED_TO_YOU task sent to this PA: it is the
 * case's own open action, so its id is read from `detail.actions`.
 */
function sendBackState(detail: CaseDetail, scope: Scope | null) {
  const returned = detail.actions.filter((a) => a.status === 'OPEN' && a.action_type === 'RETURNED_TO_YOU');
  const canSend = Boolean(scope?.can?.send_back) && !detail.viewer?.read_only && detail.case.stage !== 'CLOSED';
  return { returned, canSend };
}

/**
 * 2026-10-28 (Talhia Zalvidea, Kristian): on the Notes tab the contact touches, a provider's follow-up
 * requests and Send back sat above the thread at full size and left the notes a two-line sliver. There
 * they fold into one row that says where things stand; one click opens them. Open by default only
 * where the panel is tall enough for them AND half its height of notes; the PA's choice is kept for
 * the browser session. The Work tab shows them in full, as before.
 */
export const CASE_ACTS_OPEN_QUERY = '(min-height: 1100px)';
export const CASE_ACTS_KEY = 'emerge.pa.caseActs.open';
function readCaseActsPref(): boolean | null {
  try {
    const v = window.sessionStorage.getItem(CASE_ACTS_KEY);
    return v === '1' ? true : v === '0' ? false : null;
  } catch { return null; }
}
export function useCaseActsOpen(): [boolean, (open: boolean) => void] {
  const tall = useMediaMatch(CASE_ACTS_OPEN_QUERY);
  const [pref, setPref] = useState<boolean | null>(readCaseActsPref);
  const choose = (open: boolean) => {
    setPref(open);
    try { window.sessionStorage.setItem(CASE_ACTS_KEY, open ? '1' : '0'); } catch { /* kept for this panel only */ }
  };
  return [pref === null ? tall : pref, choose];
}

/** The folded row's words: where the touches stand, and what else is behind it. */
function caseActsSummary(detail: CaseDetail, scope: Scope | null) {
  const c = detail.case;
  const hasTouches = Array.isArray(detail.touch_dispositions) && detail.touch_dispositions.length > 0;
  const reminders = Array.isArray(detail.provider_reminders) ? detail.provider_reminders : [];
  const openRequests = reminders.filter((r) => r.open).length;
  const { returned, canSend } = sendBackState(detail, scope);
  const a = c.contact_attempt || null;
  const touch = !hasTouches ? null
    : a && !a.complete ? `Touch ${a.touches} of ${a.of || 3}${a.last_label ? ` · last: ${a.last_label}` : ''}`
      : a && a.complete ? `Contact attempt ${a.attempt} complete` : 'No touch recorded yet';
  const parts = [touch, canSend && (hasTouches || reminders.length > 0) ? 'Send back' : null].filter(Boolean) as string[];
  return {
    any: hasTouches || reminders.length > 0 || canSend || returned.length > 0,
    title: hasTouches ? 'Contact touches' : reminders.length ? 'Provider follow-up' : 'Send back',
    text: parts.join(' · '),
    openRequests,
    returned: returned.length,
  };
}

function SendBackBar({ detail, scope, onChanged }: { detail: CaseDetail; scope: Scope | null; onChanged: PanelDone }) {
  const c = detail.case;
  const { returned, canSend } = sendBackState(detail, scope);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  if (!canSend && returned.length === 0) return null;
  const handle = async (a: CaseAction) => {
    setBusy(a.id); setErr(null);
    const r = await sendBackApi.markHandled(c.id, a.id);
    setBusy(null);
    if (!r.success) { const m = r.message || r.error || 'eMerge did not record this. Nothing was changed.'; setErr(m); void onChanged(m, { stop: true }); return; }
    await onChanged('Marked handled.');
  };
  return (
    <div className="pa-section" aria-label="Send back">
      {returned.map((a) => (
        <div key={a.id} className="pa-notice" role="status">
          <strong>Returned to you.</strong> {a.reason || 'No reason was recorded.'}{' '}
          <button type="button" className="pa-open" disabled={busy === a.id} onClick={() => handle(a)}>{busy === a.id ? 'Saving…' : 'Mark handled'}</button>
        </div>
      ))}
      {err && <p className="pa-notice stop" role="alert">{err}</p>}
      {canSend && (
        <SendBackAction
          path={sendBackPath.pa(c.id)} recordLabel={personName(c)} className="pa-open" label="Send back…"
          onClosed={(done) => { if (done) void onChanged(`Sent back to ${done.to.name}.`); }}
        />
      )}
    </div>
  );
}

export function CaseContext({ caseId, detail, refusal, loading, manager, advocates, onAssign, scope, onChanged, request, onNoteAdded, docked = false }: {
  /** The case the caller ASKED for. Not `detail.case.id`: see the section effect. */
  caseId: string | null;
  detail: CaseDetail | null; refusal: Refusal | null; loading: boolean; manager: boolean; advocates: Advocate[];
  onAssign: AssignHandler;
  /**
   * A write in the case recorded something. `forCaseId` is the case the write was ABOUT (the detail the
   * form wrote), which is not always the case on screen when the answer arrives: Next may have been
   * pressed while it was in flight (fix round 7).
   */
  scope: Scope | null; onChanged: (msg: string, forCaseId?: string, opts?: { stop?: boolean }) => Promise<void>;
  /** A section asked for from outside (the row's "Add note"): honoured each time `nonce` changes. */
  request?: { section: DrawerSection; compose?: boolean; nonce: number } | null;
  /** A note landed in the thread: the shell patches the row in place, with no reload. */
  onNoteAdded?: (caseId: string, note: { kind: NoteKind; preview: string; recorded_at: string }) => void;
  /** In the docked panel: its dark bar already names the resident and the building, so the card does not repeat them. */
  docked?: boolean;
}) {
  const [section, setSection] = useState<DrawerSection>(request ? request.section : 'work');
  const [compose, setCompose] = useState(Boolean(request && request.compose));
  const seenRequest = React.useRef(request ? request.nonce : 0);
  useEffect(() => {
    if (!request || request.nonce === seenRequest.current) return;
    seenRequest.current = request.nonce;
    setSection(request.section);
    setCompose(Boolean(request.compose));
  }, [request]);
  // A different case starts on Work: the section a PA chose for one resident is
  // not a statement about the next one.
  //
  // Two things this deliberately is not:
  //
  //  - It is not keyed on `detail.case.id`. That looked equivalent and was not:
  //    every refresh sets `detail` to null and back, so the id went
  //    CASE -> null -> CASE and the reset fired again. A PA reading Identity
  //    while any background reload landed — a note saved, the scope refreshed —
  //    was silently returned to Work.
  //
  //  - It is not a useEffect. An effect also runs on MOUNT, and once the case
  //    opens under its row instead of in the side pane, the mount no longer
  //    happens on the shell's first render — it happens when a row is opened.
  //    The pending mount effect then flushed on top of the PA's first click, so
  //    choosing Documents put them straight back on Work. Comparing the id the
  //    caller asked for against the one we are showing has no such timing: it
  //    resets on a real change and never on a mount or a re-render.
  const showingFor = React.useRef(caseId);
  if (showingFor.current !== caseId) {
    showingFor.current = caseId;
    // A different case starts on Work -- unless the same click asked for its Notes.
    const asked = request && request.nonce !== seenRequest.current ? request : null;
    setSection(asked ? asked.section : 'work');
    setCompose(Boolean(asked && asked.compose));
    if (asked) seenRequest.current = asked.nonce;
  }
  const openNotes = () => { setSection('notes'); setCompose(true); };
  const [actsOpen, setActsOpen] = useCaseActsOpen();

  if (loading && !detail) return <Skeleton announce={false} />;
  if (refusal) return <section className="pa-card"><StateBlock refusal={refusal} /></section>;
  if (!detail) return <section className="pa-card"><p className="pa-empty">Select a case to see its work, the resident, the parties and the story.</p></section>;
  // Another case's detail is never shown under this one (fix round 7): until the asked-for case is read,
  // the panel is loading, not the last resident's notes, composer and forms.
  if (caseId && detail.case.id !== caseId) return <Skeleton announce={false} />;

  const c = detail.case;
  // Every result a form here reports names the case it was written about: the case of THIS render, which
  // the form's own closure keeps even if the panel has moved to another resident by the time it answers.
  const changed: PanelDone = (msg, opts) => onChanged(msg, c.id, opts);
  const umed = openUmed(detail.umed_cases);
  const writable = Boolean(scope && scope.writes === 'NATIVE' && !detail.viewer?.read_only);
  // Notes in the docked panel: the touches, the provider's requests and Send back fold into one row.
  const notesMode = docked && section === 'notes';
  const acts = caseActsSummary(detail, scope);
  const folded = notesMode && acts.any && !actsOpen;
  return (
    <section className={`pa-card${notesMode ? ' notes-mode' : ''}`} aria-label="Case context">
      {/*
        The summary. In the docked panel's Notes tab it is a capped, scrollable
        strip, so the thread and its composer own the rest of the panel's height.
      */}
      <div className="pa-card-summary">
      <div className="pa-card-head">
        <div>
          <h2 className={docked ? 'pa-sr-only' : undefined}>{personName(c)}</h2>
          <p className="pa-head-line">{docked ? null : <>{c.facility_name ? facilityDisplayName(c.facility_name) : <BuildingOrClinic row={c} fallback="Facility not recorded" />} · </>}<strong>{stateLine(c)}</strong></p>
          <p className="why">{replacementTag(c) || INTAKE_LABEL[c.intake_kind]} · {c.assignee_name ? `held by ${c.assignee_name}` : 'unassigned'}</p>
          <CaseFacts c={c} liability={detail.liability !== undefined ? detail.liability : c.liability} />
        </div>
        <AttentionTags items={c.attention} />
      </div>

      {notesMode && acts.any && (
        <div className="pa-case-acts-row">
          <button type="button" className="pa-case-acts-toggle" aria-expanded={!folded} aria-controls="case-acts-touches case-acts-sendback"
            onClick={() => setActsOpen(folded)}>
            <span className="pa-case-acts-caret" aria-hidden="true">▸</span>
            <span className="pa-case-acts-title">{acts.title}</span>
            {acts.text && <span className="pa-case-acts-sum">{acts.text}</span>}
            {acts.returned > 0 && <span className="pa-tag stop">Returned to you</span>}
            {acts.openRequests > 0 && <span className="pa-tag attn">{acts.openRequests === 1 ? 'Provider asked' : `${acts.openRequests} provider requests`}</span>}
            <span className="pa-case-acts-more">{folded ? 'Show' : 'Hide'}</span>
          </button>
        </div>
      )}
      {/* The same elements on every tab (only hidden while folded), so a half-written touch survives a fold or a tab change. */}
      <div id="case-acts-touches" className="pa-case-acts-body" hidden={folded}>
        <ProviderFollowUps items={detail.provider_reminders} />
        <ContactTouches caseId={c.id} attempt={c.contact_attempt} touches={detail.touches} dispositions={detail.touch_dispositions}
          canWrite={Boolean(writable && scope && canDo(scope, 'add_note') && c.stage !== 'CLOSED')} onDone={(msg) => changed(msg)} />
      </div>

      {!detail.case.assigned_advocate_user_id && detail.case.stage !== 'CLOSED' && (
        <div className="pa-section pa-why">
          <h3>Why this case is unassigned</h3>
          <p className="pa-text">{detail.case.intake_reason ? INTAKE_REASON_LABEL[detail.case.intake_reason] : INTAKE_LABEL[detail.case.intake_kind]}{detail.case.source_record_type === 'patients' ? ' · from the resident record: Active, with a TWL, and no open PA case' : detail.case.source_record_type === 'unassigned_twls' ? ' · a TWL with no lead yet' : detail.case.assignee_resolution === 'UNRESOLVED_MONDAY_USER' ? ' · the board named an account that is not a current advocate' : ' · nobody was assigned on the board'}</p>
          <dl className="pa-kv">
            <dt>TWL date</dt><dd>{detail.case.twl_effective_at ? shortDate(detail.case.twl_effective_at) : detail.case.intake_observed_at ? <span title="The source recorded no TWL date. This is the day eMerge first saw the TWL, not a Monday TWL date.">{shortDate(detail.case.intake_observed_at)} · first seen in CounselEAR</span> : <span className="pa-text none">Not recorded</span>}</dd>
            <dt>Seen by eMerge</dt><dd>{detail.case.intake_observed_at ? shortDateTime(detail.case.intake_observed_at) : <NotRecorded />}</dd>
            <dt>Building</dt><dd><BuildingOrClinic row={detail.case} fallback={<NotRecorded />} /></dd>
          </dl>
        </div>
      )}
      {detail.viewer?.read_only && (
        <div className="pa-notice pa-formerbanner" role="status">
          <strong>Former assignment{detail.viewer.intervals.length ? ` · ${detail.viewer.intervals.map((i) => `${shortDate(i.assigned_at)} – ${i.unassigned_at ? shortDate(i.unassigned_at) : 'now'}`).join(', ')}` : ''}.</strong> You no longer hold this case, so it is read-only here. Ask a manager to reassign it to you if you need to work it.
        </div>
      )}
      <div id="case-acts-sendback" className="pa-case-acts-body" hidden={folded}>
        <SendBackBar key={`sb:${detail.case.id}`} detail={detail} scope={scope} onChanged={changed} />
      </div>
      {manager && <AssignControl key={`${detail.case.id}:${detail.case.assigned_advocate_user_id || ''}`} detail={detail} advocates={advocates} onAssign={onAssign}
        onReopen={scope && canDo(scope, 'change_status') && !detail.viewer?.read_only ? () => {
          setSection('work');
          window.setTimeout(() => { const el = document.getElementById(`reopen-lead-${detail.case.id}`); if (el) { el.focus(); if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' }); } }, 0);
        } : undefined} />}
      </div>

      {/*
        EP-010 §4.5: four sections, Work first, instead of eleven cells stacked
        down a page. The business's complaint was navigation, and the old pane
        made the PA scroll past the resident's story to reach the next action.
        Documents, Identity and History are still one click away and still
        carry everything they carried.
      */}
      <div className="pa-drawer" role="group" aria-label="Case sections">
        <div className="pa-drawer-tabs" role="tablist" aria-label="Case sections" onFocus={revealInStrip}>
          {(DRAWER_SECTIONS).map((s) => (
            <button key={s.key} type="button" role="tab" id={`dtab-${s.key}`} aria-controls={`dpanel-${s.key}`} aria-selected={section === s.key}
              className={`pa-drawer-tab${section === s.key ? ' on' : ''}`} onClick={() => setSection(s.key)}>
              {s.label}
            </button>
          ))}
        </div>

        <div className="pa-drawer-panel" role="tabpanel" id={`dpanel-${section}`} aria-labelledby={`dtab-${section}`}>
          {section === 'work' && (
            <>
              {writable
                ? <WorkTheCase key={`work:${detail.case.id}:${detail.native?.version ?? 0}`} detail={detail} scope={scope!} onChanged={changed} only="work" manager={manager} lead={<CurrentWork c={c} actions={detail.actions} />} onOpenNotes={openNotes} />
                : <div className="pa-panel"><CurrentWork c={c} actions={detail.actions} /></div>}
              {/* The reference cells, folded: Financial and the notes are read most, so they open. */}
              <div className="pa-folds">
                <Financial key={`financial:${c.id}`} detail={detail} scope={scope} onChanged={changed} fold={{ open: true }} />
                <Enrollment c={c} fold={{ open: false }} />
                <UmedCell list={detail.umed_cases} fold={{ open: false }} />
                <FacilityCell f={detail.facility} fold={{ open: false }} />
                <Notes ev={detail.evidence} latest={c.latest_note || null} fold={{ open: true }} onOpenNotes={() => setSection('notes')} />
              </div>
            </>
          )}

          {/*
            The resident's notes (2026-10-05): one thread across every case and
            episode, the composer at the bottom. Keyed on the RESIDENT, so a
            status change (a version bump) never clears a half-written note.
          */}
          {section === 'notes' && (
            <ResidentNotesThread key={`thread:${c.patient_id || c.id}`} patientId={c.patient_id || null} actingCaseId={c.id}
              canWrite={Boolean(scope && canDo(scope, 'add_note') && !detail.viewer?.read_only)} focusComposer={compose}
              onNoteAdded={onNoteAdded} />
          )}

          {/*
            What is on file, then the files, then the way to add one. The
            upload used to come first and push the files below the fold, which
            is backwards for the question a PA opens this section to answer.
          */}
          {section === 'documents' && (
            <>
              <DocumentsPanel caseId={c.id} docs={detail.documents || []}
                canMark={Boolean(writable && scope && canDo(scope, 'add_document') && !isClosedLead(c) && (scope.manager || detail.viewer?.relationship === 'CURRENT' || c.assigned_advocate_user_id === scope.viewing_user_id))}
                canUndo={Boolean(writable && scope && canDo(scope, 'add_document'))}
                onMarked={(m) => { void changed(m); }} />
              {writable && <div className="pa-docadd"><WorkTheCase key={`docs:${detail.case.id}:${detail.documents?.length ?? 0}`} detail={detail} scope={scope!} onChanged={changed} only="documents" /></div>}
              {detail.evidence && (
                <div className="pa-panel">
                  <Cell title="Vetting notes" wide><Text value={detail.evidence.vetting_notes} /></Cell>
                </div>
              )}
            </>
          )}

          {section === 'identity' && (
            <>
              <div className="pa-panel"><FacilityRecord detail={detail} /></div>
              {writable && <WorkTheCase key={`id:${detail.case.id}:${detail.native?.version ?? 0}`} detail={detail} scope={scope!} onChanged={changed} only="identity" />}
              <div className="pa-panel">
                <Resident c={c} d={detail.demographics} umed={umed} />
                <DecisionMaker detail={detail} />
              </div>
            </>
          )}

          {section === 'history' && (
            <div className="pa-panel">
              <Chronology detail={detail} />
              <Story events={detail.events} />
              {detail.provenance && <SourceDetails p={detail.provenance} />}
            </div>
          )}
        </div>
      </div>

      {(!scope || scope.writes !== 'NATIVE') && (
        <div className="pa-readonly">
          <strong>{manager ? 'Assignment is the only change recorded here.' : 'Read-only.'}</strong> The Patient Advocate writes are not open on this server yet.
        </div>
      )}
    </section>
  );
}

/* ── Shell ─────────────────────────────────────────────────────────────── */

export default function AdvocateShell() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawView = searchParams.get('view');
  // An old ?view=new link is the New chip on My work: it is read as
  // that on the first render, and the URL is rewritten to match below.
  const legacyNew = rawView === 'new';
  const view: View = legacyNew ? 'work' : isView(rawView) ? rawView : 'work';
  const caseId = searchParams.get('case');
  const as = searchParams.get('as');
  const q = searchParams.get('q') || '';
  const rawLens = searchParams.get('lens');
  const lens: Lens = legacyNew ? 'new' : (lensBelongs(view, rawLens) && isLens(rawLens) ? rawLens : 'active');
  // The filter bar's state: only the views that have the bar carry it, and a
  // value the bar does not offer is dropped rather than sent to be refused.
  const refine: Refine = view === 'work' || view === 'all' ? readRefine(view, (k) => searchParams.get(k)) : NO_REFINE;
  const { facility: rFacility, status: rStatus, fr: rFr, liability: rLiability, docs: rDocs, sort: rSort } = refine;
  const pageNo = Math.max(parseInt(searchParams.get('page') || '1', 10) || 1, 1);
  const rawMonth = searchParams.get('month');
  const month: string | null = view === 'sales' && rawMonth && MONTH_RE.test(rawMonth) ? rawMonth : null;
  const rawFilter = searchParams.get('filter');
  const filter: HistoryFilter = view === 'history' && isHistoryFilter(rawFilter) ? rawFilter : 'all';
  const loc: Loc = { view, caseId, as, q, page: pageNo, lens, refine, month, filter };
  const go = (patch: Partial<Loc>, replace = false) => navigate(path({ ...loc, ...patch }), { replace });

  // Fix round 12: note words held in this page's memory only (storage full) outlive the notes thread closing
  // in-app; when the workspace itself goes and her session is gone (a sign-out), they go with it.
  useEffect(() => () => forgetMemoryWithoutSession(), []);

  const [scope, setScope] = useState<Scope | null>(null);
  const [scopeRefusal, setScopeRefusal] = useState<Refusal | null>(null);
  const [scopeLoading, setScopeLoading] = useState(true);
  const [advocates, setAdvocates] = useState<Advocate[]>([]);
  // T2: what the caller may do on the residents routes. Absent (refused) hides the tab; the server refuses again on every call.
  const [identity, setIdentity] = useState<IdentityScope | null>(null);

  const [work, setWork] = useState<WorkPage | null>(null);
  const [followUps, setFollowUps] = useState<FollowUpPage | null>(null);
  const [all, setAll] = useState<AllCasesPage | null>(null);
  const [sales, setSales] = useState<SalesPage | null>(null);
  const [history, setHistory] = useState<HistoryPage | null>(null);
  const [viewRefusal, setViewRefusal] = useState<Refusal | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  // A 400 INVALID_FILTER is said under the bar and the rows already shown stay: it is not a refusal of the view.
  const [filterError, setFilterError] = useState<string | null>(null);
  const [filterNotice, setFilterNotice] = useState<string | null>(null);
  // Set each render, so a read that finishes later drops the parameter from the CURRENT url, not the one it started with.
  const dropRefine = useRef<(k: keyof Refine) => void>(() => undefined);

  const [rowEdit, setRowEdit] = useState<{ id: string; what: RowEdit } | null>(null);
  const [detail, setDetail] = useState<CaseDetail | null>(null);
  const [detailRefusal, setDetailRefusal] = useState<Refusal | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Where a write's result is said (fix round 5): the panel's own acts (assign, a status, a note on
  // Work) inside the open panel, so on a phone or tablet -- where the panel is the whole screen and
  // the list behind it is inert -- the result is seen and heard; the list's own acts above the list.
  // Fix round 6: a panel result belongs to the case it was written about (`caseId`): it is shown only
  // while the panel is on THAT case, it goes when the panel moves to another resident (Prev, Next,
  // another row), and it never moves into the list when the panel closes.
  const [noticeAt, setNoticeAt] = useState<{ text: string; stop: boolean; at: 'dock' | 'list'; caseId: string | null } | null>(null);
  const notice = noticeAt ? noticeAt.text : null;
  const caseIdNow = useRef<string | null>(caseId);
  caseIdNow.current = caseId;
  // Fix round 9: a REFUSAL of a panel write waits with its case, however many moves later the PA comes back:
  // one per case, kept until it is shown and dismissed, or until a later act on that case succeeds (the case
  // is then re-read, and the refusal is history). A success still holds the one slot above and goes on a move.
  const [refused, setRefused] = useState<Record<string, string>>({});
  const waitRefusal = useCallback((forCaseId: string, text: string) => setRefused((m) => ({ ...m, [forCaseId]: text })), []);
  const dropRefusal = useCallback((forCaseId: string | null | undefined) => {
    if (forCaseId) setRefused((m) => { if (!(forCaseId in m)) return m; const next = { ...m }; delete next[forCaseId]; return next; });
  }, []);
  const setNotice = useCallback((text: string | null, opts: { stop?: boolean; at?: 'dock' | 'list'; caseId?: string | null } = {}) => {
    const about = opts.caseId !== undefined ? opts.caseId : caseIdNow.current;
    if (text && opts.stop && opts.at === 'dock' && about) { waitRefusal(about, text); return; }
    setNoticeAt(text ? { text, stop: Boolean(opts.stop), at: opts.at || 'list', caseId: about } : null);
  }, [waitRefusal]);
  useEffect(() => {
    setNoticeAt((n) => (n && n.at === 'dock' && n.caseId !== caseId ? null : n));
  }, [caseId]);
  const dockNoticeRef = useRef<HTMLDivElement | null>(null);
  // The words the panel's two live regions say. The regions are ALWAYS in the panel, empty until there
  // is something to say; each new result clears them first, so even a second identical "Assigned." is a
  // change a screen reader announces (a region that arrives already holding its words often is not).
  const [dockSaid, setDockSaid] = useState<{ status: string; alert: string }>({ status: '', alert: '' });
  const refusedHere = caseId ? refused[caseId] : undefined;
  const refusalNotice = useMemo(() => (refusedHere && caseId ? { text: refusedHere, stop: true, at: 'dock' as const, caseId } : null), [refusedHere, caseId]);
  const dockNotice = refusalNotice || (noticeAt && noticeAt.at === 'dock' && noticeAt.caseId === caseId ? noticeAt : null);
  const dockNoticeNow = useRef(dockNotice);
  dockNoticeNow.current = dockNotice;
  useEffect(() => {
    setDockSaid({ status: '', alert: '' });
    if (!dockNotice) return undefined;
    const t = window.setTimeout(() => setDockSaid(dockNotice.stop ? { status: '', alert: dockNotice.text } : { status: dockNotice.text, alert: '' }), 60);
    return () => window.clearTimeout(t);
  }, [dockNotice]);
  // Where focus goes when a panel closes and its case has no row in this list (a deep link to a case in
  // another lens or page): the list's own title, never the page.
  const pageTitleRef = useRef<HTMLHeadingElement | null>(null);
  // The section the docked panel was asked to open on (the row's "Add note" opens Notes).
  const [panelRequest, setPanelRequest] = useState<{ section: DrawerSection; compose?: boolean; nonce: number } | null>(null);

  /**
   * Close the docked panel and put focus back on the row it was opened from
   * (the board rule: Escape closes, focus returns to the row). Escape inside a
   * field is that field's -- a PA clearing a search must not lose the case.
   */
  const closePanel = useCallback((id: string | null) => {
    navigate(path({ ...loc, caseId: null }));
    // Back to the row it was opened from; a case opened from a link that is not in this list (another
    // lens, another page) has no row, so focus goes to the list's title (fix round 6: it fell to the page).
    if (id) window.setTimeout(() => { ((document.querySelector(`[data-case-open="${id}"]`) as HTMLElement | null) || pageTitleRef.current)?.focus(); }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, searchParams]);
  useEffect(() => {
    if (!caseId || (view !== 'work' && view !== 'all')) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (t && t.closest('input, textarea, select, [contenteditable="true"]')) return;
      closePanel(caseId);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [caseId, view, closePanel]);

  const [fbOpen, setFbOpen] = useState(false);
  const [fbSource, setFbSource] = useState<PromptSource>('DRAWER');
  const openFeedback = useCallback((s: PromptSource) => { setFbSource(s); setFbOpen(true); }, []);
  const closeFeedback = useCallback(() => setFbOpen(false), []);

  const manager = Boolean(scope?.manager);
  // The signed send-backs addressed to this person. Absent (404) hides the tab: the gate is closed on that server.
  const returned = useReturned();

  const loadScope = useCallback(async (quiet = false) => {
    if (!quiet) setScopeLoading(true);
    setScopeRefusal(null);
    try {
      const res = await advocateApi.scope(as);
      if (res.success && res.data) {
        setScope(res.data);
        if (res.data.manager) {
          const list = await advocateApi.advocates();
          if (list.success && list.data) setAdvocates(list.data);
        }
        try { const id = await residentsApi.scope(); setIdentity(id.success && id.data ? id.data : null); } catch { setIdentity(null); }
      } else setScopeRefusal(refusalFor(res.code, res.status, res.message));
    } catch { setScopeRefusal(OFFLINE); } finally { setScopeLoading(false); }
  }, [as]);
  useEffect(() => { loadScope(); }, [loadScope]);

  // Unassigned moved to Manifest Review (2026-09-30). An old link to it is
  // answered below, once the scope says who is asking.
  const movedAway = rawView === 'unassigned';
  useEffect(() => {
    if (movedAway) return;
    if (rawView === 'new') { go({ view: 'work', lens: 'new' }, true); return; }
    if (rawView !== null && !isView(rawView)) { go({ view: 'work' }, true); return; }
    // A lens that belongs to another workspace is not this queue's state: the URL is canonicalised, never silently intersected.
    if (!lensBelongs(view, rawLens)) go({ lens: 'active' }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawView, rawLens, view]);

  const loadView = useCallback(async () => {
    setViewLoading(true); setViewRefusal(null); setFilterError(null);
    const l = { as, q, page: pageNo, lens, facility: rFacility, status: rStatus, fr: rFr, liability: rLiability, docs: rDocs, sort: rSort };
    const invalid = (r: { code?: string; message?: string; error?: string; detail?: unknown }): boolean => {
      if (r.code !== 'INVALID_FILTER') return false;
      // A stale or hand-edited link: when the server names the parameter, clear just that one and read again.
      const d = (r.detail && typeof r.detail === 'object' ? r.detail : {}) as { parameter?: unknown };
      const named = typeof d.parameter === 'string' && (REFINE_KEYS[view === 'all' ? 'all' : 'work'] as readonly string[]).includes(d.parameter) ? d.parameter as keyof Refine : null;
      if (named && l[named]) {
        setFilterNotice(`The ${named} filter in this link was not recognised and was cleared.`);
        dropRefine.current(named);
        return true;
      }
      setFilterError(r.message || r.error || 'That filter was not accepted. The list below is unchanged.');
      return true;
    };
    try {
      if (view === 'work') { const r = await advocateApi.work(l); if (r.success && r.data) setWork(r.data); else if (!invalid(r)) setViewRefusal(refusalFor(r.code, r.status, r.message)); }
      if (view === 'follow-ups') { const r = await advocateApi.followUps({ as, q, page: pageNo }); if (r.success && r.data) setFollowUps(r.data); else setViewRefusal(refusalFor(r.code, r.status, r.message)); }
      // EP-007: the manager's view is not requested at all without the authority
      // for it. The server refuses it as well; this keeps a refusal off the screen
      // for someone who never asked to be there.
      if (view === 'all' && manager) { const r = await advocateApi.all(l); if (r.success && r.data) setAll(r.data); else if (!invalid(r)) setViewRefusal(refusalFor(r.code, r.status, r.message)); }
      // EP-009: the close month (this month unless the URL says another); every case ever held.
      if (view === 'sales') { const r = await advocateApi.sales({ as, month: month || currentMonth() }); if (r.success && r.data) setSales(r.data); else setViewRefusal(refusalFor(r.code, r.status, r.message)); }
      if (view === 'history') { const r = await advocateApi.history({ as, q, filter, page: pageNo }); if (r.success && r.data) setHistory(r.data); else setViewRefusal(refusalFor(r.code, r.status, r.message)); }
    } catch { setViewRefusal(OFFLINE); } finally { setViewLoading(false); }
  }, [view, as, q, pageNo, lens, rFacility, rStatus, rFr, rLiability, rDocs, rSort, manager, month, filter]);
  useEffect(() => { setWork(null); setFollowUps(null); setAll(null); setSales(null); setHistory(null); setFilterError(null); setFilterNotice(null); }, [view, as]);
  // A hand-typed ?view=all is not an error to explain, it is a view they do not
  // have: send them to their own work once we know which they are.
  useEffect(() => {
    if (scope && !manager && (view === 'all' || view === 'monday-moves')) go({ view: 'work' }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, manager, view]);
  // An old ?view=unassigned link — a bookmark, an email — lands a manager on
  // the queue in Manifest Review, with the search and the case it named. An
  // advocate never had the view, and goes to their own work, as before.
  useEffect(() => {
    if (!movedAway || !scope) return;
    if (!manager) { go({ view: 'work' }, true); return; }
    const next = new URLSearchParams({ view: 'unassigned' });
    ['q', 'lens', 'case'].forEach((k) => { const v = searchParams.get(k); if (v) next.set(k, v); });
    navigate(`/portal/manifest?${next.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movedAway, scope, manager]);
  useEffect(() => { if (scope && !movedAway) loadView(); }, [scope, movedAway, loadView]);

  /**
   * A lead was placed with this advocate: re-read the queue and the counts.
   *
   * Isaiah Cotto, 2026-09-23: "We should be able to refresh the tables to see
   * the assignments after receiving the assignment notification." The signal
   * changes when a notification arrives, so the table catches up because the
   * work moved, not because a timer fired. The first value is skipped — it
   * is only the poll reporting what was already on the screen.
   */
  const handoff = useHandoffSignal();
  const lastHandoff = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (lastHandoff.current === undefined) { lastHandoff.current = handoff; return; }
    if (lastHandoff.current === handoff) return;
    lastHandoff.current = handoff;
    if (!scope) return;
    loadView();
    loadScope(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoff]);

  // Fix round 7: an answer for a case the panel has left is dropped. A write's re-read that lands after
  // Next used to put resident A's whole panel -- notes thread and composer included -- under B's name.
  const loadDetail = useCallback(async (id: string) => {
    const current = () => caseIdNow.current === id;
    if (!current()) return;
    setDetailLoading(true); setDetailRefusal(null);
    try {
      const r = await advocateApi.case(id);
      if (!current()) return;
      if (r.success && r.data) setDetail(r.data);
      else {
        setDetail(null);
        setDetailRefusal(r.code === 'NOT_FOUND'
          ? { title: 'No such case', body: 'This case does not exist or was closed and removed from the queue.' }
          : failureFor(r) || { title: 'This case is not in your authorized scope', body: 'It is assigned to you but no authorization row exists for it. Ask a PA manager to reassign it, which writes one.', detail: `Decision: ${r.code || 'DENIED'}` });
      }
    } catch { if (current()) { setDetail(null); setDetailRefusal(OFFLINE); } } finally { if (current()) setDetailLoading(false); }
  }, []);
  useEffect(() => { setDetail(null); setDetailRefusal(null); }, [caseId]);
  useEffect(() => {
    if (!scope || !caseId || movedAway) return;
    loadDetail(caseId);
  }, [scope, caseId, movedAway, loadDetail]);

  /**
   * A write in the panel re-reads the case, which can remount the control that was pressed: focus must
   * not fall to the page (fix round 5). If it did, it goes to the panel's notice (which says what
   * happened), or to the panel itself.
   */
  const keepFocusInDock = () => {
    window.requestAnimationFrame(() => {
      const a = document.activeElement;
      const dock = dockRef.current;
      if (!dock || (a && a !== document.body && a.isConnected)) return;
      // The message box is always in the panel, but it is focusable only while it holds a message: with
      // none, focus goes to the panel itself (fix round 7). Read from a ref, not this closure's render,
      // which predates the message this write just set.
      (dockNoticeNow.current ? dockNoticeRef.current : null)?.focus();
      if (document.activeElement === document.body || !document.activeElement) dock.focus();
    });
  };
  const onAssign = async (advocateUserId: string | null, note: string, expectedFrom: string | null): Promise<AssignOutcome> => {
    if (!caseId) return { ok: false, message: null };
    setNotice(null);
    try {
      const r = await advocateApi.assign(caseId, advocateUserId, note || undefined, expectedFrom, freshKey());
      if (!r.success) {
        if (r.code === 'ASSIGNMENT_CONFLICT') {
          // The case is re-read (the control remounts), so the panel says it.
          setNotice('Someone else placed this case since you opened it. It was not overwritten — reload to see who holds it.', { stop: true, at: 'dock', caseId });
          await loadDetail(caseId);
          keepFocusInDock();
          return { ok: false, message: null };
        }
        return { ok: false, message: assignRefusalWords(r) };
      }
      dropRefusal(caseId);
      setNotice(advocateUserId ? 'Assigned.' : 'Taken back.', { at: 'dock', caseId });
      await Promise.all([loadDetail(caseId), loadView(), loadScope(true)]);
      keepFocusInDock();
      return { ok: true, message: null };
    } catch { return { ok: false, message: 'Could not reach the server. Nothing was changed.' }; }
  };

  const feedback = (
    <FeedbackDrawer open={fbOpen} onClose={closeFeedback} promptSource={fbSource}
      workspace="PATIENT_ADVOCATE" routeTemplate={BASE} viewName={view} facilityId={detail?.case.facility_id ?? null} />
  );
  const door = <div className="pa-door"><FeedbackButton onClick={() => openFeedback('DRAWER')} /></div>;

  const barRef = useRef<HTMLElement | null>(null);
  // A div, not an aside: the same element is the complementary side panel on a wide screen and a dialog
  // below 901px (an aside may not be a dialog), so crossing the width never remounts the case.
  const dockRef = useRef<HTMLDivElement | null>(null);
  // The dock element itself, as state, so the dialog effect runs when it MOUNTS (fix round 5): a case
  // opened from the URL (a deep link, a reload, back/forward) mounts the dock after the scope
  // skeleton, when neither `modal` nor `caseId` changes any more.
  const [dockEl, setDockEl] = useState<HTMLDivElement | null>(null);
  const dockMount = useCallback((el: HTMLDivElement | null) => { dockRef.current = el; setDockEl(el); }, []);
  const dockedNow = (view === 'work' || view === 'all') && Boolean(caseId);
  // Below 901px the open panel is the whole screen (paDock.css), so it is a MODAL DIALOG: named by the
  // resident, focus moved into it and held there, the page behind it inert, Escape closes it (the
  // board rule's handler above) and focus goes back to the row it was opened from (closePanel).
  const sheet = useMediaMatch(SHEET_QUERY);
  const modal = sheet && dockedNow;
  useEffect(() => {
    const bar = barRef.current;
    const root = bar ? bar.parentElement : null;
    const dock = dockEl;
    if (!modal || !root || !dock) return undefined;
    const main = dock.parentElement;
    // Everything on the page but the panel: the workspace bar, the header, and the list beside the panel.
    // A dialog of the page's own (the feedback drawer) is left alone.
    const behind = [
      ...Array.from(root.children).filter((el) => el !== main && !el.matches('[role="dialog"], [aria-modal="true"]') && !el.querySelector('[role="dialog"], [aria-modal="true"]')),
      ...(main ? Array.from(main.children).filter((el) => el !== dock) : []),
      // The workspace sidebar's rail and the portal's top bar sit outside .pa (WorkspaceLayout): behind the panel too.
      ...Array.from(document.querySelectorAll('.wsb-layout > .wsb, nav[aria-label="Workspaces"]')).filter((el) => !el.contains(dock)),
    ] as HTMLElement[];
    const before = behind.map((el) => ({ el, inert: el.hasAttribute('inert'), hidden: el.getAttribute('aria-hidden') }));
    behind.forEach((el) => { el.setAttribute('inert', ''); el.setAttribute('aria-hidden', 'true'); });
    if (!dock.contains(document.activeElement)) dock.focus();
    return () => {
      before.forEach(({ el, inert, hidden }) => {
        if (!inert) el.removeAttribute('inert');
        if (hidden === null) el.removeAttribute('aria-hidden'); else el.setAttribute('aria-hidden', hidden);
      });
    };
  }, [modal, caseId, dockEl]);
  /** Tab and Shift+Tab stay inside the dialog: from the last control to the first, and back. */
  const holdFocus = (e: React.KeyboardEvent<HTMLElement>) => {
    if (!modal || e.key !== 'Tab') return;
    const dock = dockRef.current;
    if (!dock) return;
    const all = Array.from(dock.querySelectorAll<HTMLElement>('a[href], button, input, select, textarea, [tabindex]'))
      .filter((el) => el.tabIndex >= 0 && !(el as HTMLButtonElement).disabled && !el.closest('[hidden], [inert]')
        // Laid out (a real browser): only what is drawn. jsdom lays nothing out, so there the attributes decide.
        && (document.documentElement.clientWidth === 0 || el.getClientRects().length > 0));
    if (!all.length) { e.preventDefault(); return; }
    const first = all[0];
    const last = all[all.length - 1];
    const here = document.activeElement;
    if (e.shiftKey && (here === first || here === dock || !dock.contains(here))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (here === last || !dock.contains(here))) { e.preventDefault(); first.focus(); }
  };
  // The docked panel fits the screen exactly: it starts below the sticky
  // workspace header (whatever its height today) and ends 12px above the
  // bottom of the window, so the composer and the panel's own controls are
  // always on screen and only one region scrolls at a time.
  useLayoutEffect(() => {
    const bar = barRef.current;
    const root = bar ? bar.parentElement : null;
    if (!bar || !root) return undefined;
    const setBar = () => root.style.setProperty('--pa-bar-h', `${bar.offsetHeight}px`);
    setBar();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(setBar);
    ro.observe(bar);
    return () => ro.disconnect();
    // The header is not drawn while the scope loads (the skeleton): measure once it is.
  }, [scopeLoading, movedAway]);
  useLayoutEffect(() => {
    const el = dockRef.current;
    if (!dockedNow || !el) return undefined;
    let raf = 0;
    const fit = () => {
      raf = 0;
      // Sized from where the panel STICKS (12px under the measured header), or from where it sits
      // while the page is still above that -- never from a position it was pushed to. The old rule
      // read the pushed-up top at the end of the list, made the panel taller, which pushed it up
      // further, until its bar slid under the sticky header (fix round 4). It also ends at the list's
      // end when that is above the window's bottom, so it is never taller than its column allows.
      const barH = barRef.current ? barRef.current.offsetHeight : 0;
      const stuck = barH + 12;
      const top = Math.max(stuck, el.getBoundingClientRect().top);
      const list = el.parentElement ? el.parentElement.querySelector(':scope > .pa-list') : null;
      const listEnd = list ? list.getBoundingClientRect().bottom : Number.POSITIVE_INFINITY;
      let end = window.innerHeight - 12;
      if (listEnd - top >= 360) end = Math.min(end, listEnd);
      el.style.setProperty('--pa-dock-h', `${Math.max(240, Math.floor(end - top))}px`);
      // The panel's column, for the side sheet a short laptop screen gets with Notes open
      // (paDock.css): the same left edge and width as the column it leaves.
      const main = el.parentElement;
      if (main) {
        const cs = window.getComputedStyle(main);
        const cols = cs.gridTemplateColumns.split(' ').map((v) => parseFloat(v)).filter((v) => Number.isFinite(v));
        const w = cols.length >= 2 ? cols[cols.length - 1] : NaN;
        if (Number.isFinite(w) && w > 0) {
          const mr = main.getBoundingClientRect();
          const right = mr.right - (parseFloat(cs.paddingRight) || 0);
          el.style.setProperty('--pa-dock-w', `${Math.round(w)}px`);
          el.style.setProperty('--pa-dock-left', `${Math.round(right - w)}px`);
          // What the side sheet takes from the right of the window: the header lays itself out in the
          // rest, so no header control is ever under the sheet (paDock.css).
          const root = main.parentElement;
          if (root) root.style.setProperty('--pa-sheet-room', `${Math.max(0, Math.round(document.documentElement.clientWidth - (right - w) + 12))}px`);
        }
      }
    };
    const later = () => { if (!raf) raf = window.requestAnimationFrame(fit); };
    fit();
    window.addEventListener('scroll', later, { passive: true });
    window.addEventListener('resize', later);
    return () => {
      window.removeEventListener('scroll', later);
      window.removeEventListener('resize', later);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [dockedNow, caseId, scopeLoading]);

  // A button that turns itself off under the keyboard drops focus to the page (fix round 8). When Prev or
  // Next was pressed and the move left it disabled (the first or last resident), focus goes to the other
  // nav button, or to the resident's name when neither can take it.
  const prevBtnRef = useRef<HTMLButtonElement | null>(null);
  const nextBtnRef = useRef<HTMLButtonElement | null>(null);
  const navPressed = useRef<{ dir: 'prev' | 'next'; from: string | null } | null>(null);
  useEffect(() => {
    const press = navPressed.current;
    // Only once the panel has actually moved (a re-read of the list under the same case is not the move).
    if (!press || press.from === caseId) return;
    const pressed = press.dir;
    const mine = pressed === 'prev' ? prevBtnRef.current : nextBtnRef.current;
    const other = pressed === 'prev' ? nextBtnRef.current : prevBtnRef.current;
    if (!mine || !mine.disabled) { navPressed.current = null; return; }
    navPressed.current = null;
    if (other && !other.disabled) other.focus();
    else document.getElementById('pa-dock-name')?.focus();
  }, [caseId, work, all]);

  if (scopeLoading || (movedAway && !scopeRefusal)) return <div className="pa"><main className="pa-main one"><Skeleton /></main></div>;
  if (scopeRefusal) {
    return <div className="pa"><main className="pa-main one"><div><StateBlock refusal={scopeRefusal} retry={() => loadScope()} />{door}</div></main>{feedback}</div>;
  }

  const counts = scope?.counts;
  const viewing = advocates.find((a) => a.id === (as || ''));
  const whose = as && viewing ? `${viewing.name}'s` : 'your';
  // EP-007: Team cases is a manager's view. The server refuses it too, so this
  // is the tidy face of a real gate, not the gate itself.
  // `identity.held` is optional defensively: a scope response without it used
  // to throw here and blank the entire workspace — a tab nobody asked for
  // taking down My work. An unreadable identity scope means no Residents tab,
  // never no screen.
  const identityTab = Boolean(identity?.held && (identity.held.create || identity.held.review || identity.held.review_counselear_arrival));
  const visibleViews = VIEWS.filter((v) => (v === 'residents' ? identityTab : v === 'returned' ? offersReturnedTab(returned.state) || view === 'returned' : (v !== 'all' && v !== 'monday-moves') || manager));
  // Views that bring their own list and need neither the search bar nor the case pane.
  const selfContained = view === 'residents' || view === 'returned' || view === 'monday-moves';
  const open = (id: string) => go({ caseId: caseId === id ? null : id });
  const onPage = (p: number) => go({ page: p });
  const onSearch = (next: string) => go({ q: next, page: 1 });
  const onLens = (next: Lens) => go({ lens: next, page: 1 });
  dropRefine.current = (k) => go({ refine: { ...refine, [k]: '' }, page: 1 }, true);
  const onRefine = (k: keyof Refine, v: string) => { setFilterNotice(null); go({ refine: { ...refine, [k]: v }, page: 1 }); };
  const onClearRefine = () => { setFilterNotice(null); go({ refine: NO_REFINE, page: 1 }); };
  const lensed = LENSED_VIEWS.includes(view);
  // The filter bar belongs to My work and Team cases; Team cases is the manager's.
  const filterable = view === 'work' || (view === 'all' && manager);
  const listPage = view === 'work' ? work : view === 'all' ? all : null;
  // Team cases' Closed lens ignores everything but the building (the server does), so the bar offers only that.
  const closedTeam = view === 'all' && lens === 'closed';
  const filtersApplied = filterable && (closedTeam ? Boolean(refine.facility) : (REFINE_KEYS[view === 'work' ? 'work' : 'all'] as readonly string[]).some((k) => k !== 'sort' && Boolean(refine[k as keyof Refine])));
  // The buildings to narrow to: the server's list when it sends one, else the buildings on the page.
  const facilityOptions: Array<{ id: string; name: string; count?: number }> = listPage?.filters?.facilities
    || (listPage?.groups || []).filter((g) => g.facility_id).map((g) => ({ id: g.facility_id as string, name: g.facility_name, count: g.residents }));
  const onMonth = (m: string) => go({ month: m, caseId: null });
  const onFilter = (f: HistoryFilter) => go({ filter: f, page: 1 });
  const hasData = view === 'work' ? Boolean(work) : view === 'follow-ups' ? Boolean(followUps)
    : view === 'sales' ? Boolean(sales) : view === 'history' ? Boolean(history) : view === 'residents' ? Boolean(identity) : view === 'returned' || view === 'monday-moves' ? true : Boolean(all);

  /**
   * EP-010: what the operating table may do on a row, decided by the server
   * (`scope.can`, which is capability AND write group) and never by the
   * screen. An editor opens for exactly one resident at a time: two open
   * liability forms is two people about to disagree about one number.
   */
  // My work and Team cases are tables; an open case docks BESIDE the table
  // (Isaiah Cotto, 2026-10-05: the board rule -- never a long in-table
  // dropdown). The other views are still lists and keep the side pane.
  const inlineDetail = view === 'work' || view === 'all';
  const docked = inlineDetail && Boolean(caseId);
  const listItems: WorkCase[] = view === 'work' ? (work?.items || []) : view === 'all' ? (all?.items || []) : [];

  const at = caseId ? listItems.findIndex((x) => x.id === caseId) : -1;

  /** A note landed: patch the row's "Latest note" where it is. Nothing reloads, nothing moves. */
  const patchLatestNote = (id: string, n: { kind: NoteKind; preview: string; recorded_at: string }) => {
    const patch = <T extends { items: WorkCase[]; groups?: FacilityGroup[] }>(page: T | null): T | null => {
      if (!page) return page;
      const fix = (c: WorkCase): WorkCase => (c.id === id
        ? { ...c, latest_note: { id: `local-${n.recorded_at}`, kind: n.kind, preview: n.preview, truncated: false, recorded_at: n.recorded_at, recorded_by_name: 'You' } }
        : c);
      return { ...page, items: page.items.map(fix), groups: page.groups ? page.groups.map((g) => ({ ...g, items: g.items.map(fix) })) : page.groups };
    };
    setWork((p) => patch(p));
    setAll((p) => patch(p));
  };

  const rowActs: RowActs = {
    // No scope yet means no act: the screen offers nothing it has not been told it may do.
    canLiability: Boolean(scope) && canDo(scope!, 'record_liability'),
    canFinResp: Boolean(scope) && canDo(scope!, 'record_financial_responsibility'),
    canNote: Boolean(scope) && canDo(scope!, 'add_note'),
    canDoc: Boolean(scope) && canDo(scope!, 'add_document'),
    canStatus: Boolean(scope) && canDo(scope!, 'change_status'),
    legend: scope?.status_legend || [],
    docClasses: scope?.document_classes || DEFAULT_DOC_CLASSES,
    sharedClasses: scope?.shared_document_classes || [],
    onOpenNotes: (c, compose) => {
      setPanelRequest((r) => ({ section: 'notes', compose, nonce: (r ? r.nonce : 0) + 1 }));
      if (caseId !== c.id) go({ caseId: c.id });
    },
    editing: rowEdit,
    onEdit: (c, what) => setRowEdit({ id: c.id, what }),
    onCloseEdit: () => setRowEdit(null),
    onSaved: async (msg, forCaseId) => {
      // Fix round 10: a row act on the case succeeded -- like every other success on it, it retires a refusal
      // still waiting for that case (which used to hide the panel's later results there until dismissed).
      dropRefusal(forCaseId);
      setNotice(msg);
      await Promise.all([loadView(), caseId ? loadDetail(caseId) : Promise.resolve(), loadScope(true)]);
    },
  };

  /*
   * The sidebar (Isaiah Cotto, 2026-10-30: one standard sidebar) in place of the header's tab strip: the same views in the
   * same order, the same links (viewPath, so every deep link still lands), the same counts as badges.
   */
  const VIEW_ICON: Record<View, typeof Inbox> = {
    work: Inbox, 'follow-ups': CalendarClock, returned: Undo2, sales: CircleDollarSign, history: HistoryIcon, all: Users,
    residents: UserSearch, 'monday-moves': ArrowRightLeft,
  };
  const viewCount = (v: View): number | null => {
    if (v === 'returned') return returned.state.status === 'ready' ? returned.state.rows.length : null;
    if (!counts || v === 'sales' || v === 'history' || v === 'residents' || v === 'monday-moves') return null;
    return v === 'work' ? counts.my_work : v === 'follow-ups' ? counts.follow_ups : counts.all_open ?? 0;
  };
  const sidebarItem = (v: View) => {
    const n = viewCount(v);
    return {
      key: v, label: VIEW_LABEL[v], icon: VIEW_ICON[v], href: viewPath(loc, v), active: view === v,
      badge: n !== null ? { value: n, label: `${n} ${n === 1 ? 'case' : 'cases'}` } : null,
    };
  };
  const WORK_VIEWS: readonly View[] = ['work', 'follow-ups', 'returned'];
  const sidebarGroups: SidebarGroup[] = [
    { key: 'work', label: 'Work', items: visibleViews.filter((v) => WORK_VIEWS.includes(v)).map(sidebarItem) },
    { key: 'reference', label: 'Reference', items: visibleViews.filter((v) => !WORK_VIEWS.includes(v)).map(sidebarItem) },
  ];

  return (
    <div>
      <WorkspaceMenu current="PATIENT_ADVOCATE" />
      <WorkspaceLayout workspace="PATIENT_ADVOCATE" navLabel="Advocate views" groups={sidebarGroups}>
      <div className="pa">
      <header className="pa-bar" ref={barRef}>
        <div className="pa-bar-inner">
          <div className="pa-ident">
            <div className="wsb-title">
            <SidebarTrigger />
            <div className="pa-where">
              <span className="pa-eyebrow">eMerge · Patient Advocate</span>
              <h1 ref={pageTitleRef} tabIndex={-1}>{VIEW_LABEL[view]}{as && viewing ? ` · ${viewing.name}` : ''}</h1>
              {counts && <span className="pa-sub" title={mondayRetiredLine(counts) || undefined}>{updatedStamp(counts)}</span>}
            </div>
            </div>
            <div className="pa-aside">
              {/* Where the manager's Unassigned tab was (2026-09-30). */}
              {manager && <Link to={UNASSIGNED_HOME} className="pa-moved">Unassigned has moved to Manifest Review</Link>}
              {manager && (
                <label className="pa-viewas">
                  <span>Viewing as</span>
                  <select value={as || ''} onChange={(e) => go({ as: e.target.value || null, page: 1, caseId: null })} aria-label="Viewing as">
                    <option value="">Myself</option>
                    {advocates.filter((a) => !a.manager).map((a) => <option key={a.id} value={a.id}>{a.name} · {a.open_cases}</option>)}
                  </select>
                </label>
              )}
              <FindResidentEntry />
              <FeedbackButton onClick={() => openFeedback('DRAWER')} />
            </div>
          </div>
        </div>
      </header>

      <main className={`pa-main${selfContained ? ' one' : ''}${inlineDetail ? (docked ? ' docked' : ' one wide') : ''}`}>
        {/* The list is the view: it settles in when a view is chosen. The docked panel beside it is not wrapped. */}
        <ViewTransition viewKey={view} className="pa-list">
          <QueueHelp view={view} />
          {view !== 'sales' && !selfContained && <SearchBar value={q} onSearch={onSearch} placeholder={view === 'history' ? 'Search by resident, facility, CounselEAR ID or case reference' : 'Search by resident, facility or case reference'} />}
          {view === 'work' && <ExportButton what="my cases" run={() => advocateApi.exportWork({ as, q, lens })} />}
          {view === 'history' && <ExportButton what="all my cases" run={() => advocateApi.exportHistory({ as, q, filter })} />}
          {view === 'all' && manager && <ExportButton what="team cases" run={() => advocateApi.exportAll({ as, q, lens })} />}
          {filtersApplied && <p className="pa-export-note">Export covers the whole lens; the filters above are not applied.</p>}
          {lensed && <LensBar lens={lens} counts={view === 'work' ? counts?.lenses : undefined} onLens={onLens} allCases={view === 'all'} />}
          {filterable && (
            <FilterBar kind={view === 'work' ? 'work' : 'all'} value={refine} onChange={onRefine} onClear={onClearRefine}
              facilities={facilityOptions} legend={scope?.status_legend || []} total={listPage ? listPage.total : null} error={filterError} notice={filterNotice} closed={closedTeam} />
          )}
          {(view === 'work' || view === 'all') && <StatusLegend legend={scope?.status_legend || []} />}
          {counts && <FreshnessNote counts={counts} />}
          {scope && <SourceHealthNotice scope={scope} />}
          {/* A panel's result is said here only where there is no docked panel (the context pane), and only for its own case. */}
          {noticeAt && (noticeAt.at === 'list' || (!inlineDetail && noticeAt.caseId === caseId)) && <p className={`pa-notice${noticeAt.stop ? ' stop' : ''}`} role={noticeAt.stop ? 'alert' : 'status'}>{noticeAt.text}</p>}
          {/* A refusal waiting with the case the context pane is on (fix round 9), until dismissed. */}
          {!inlineDetail && refusalNotice && (
            <p className="pa-notice stop" role="alert">{refusalNotice.text}{' '}
              <button type="button" className="pa-link" aria-label="Dismiss this message" onClick={() => dropRefusal(caseId)}>Dismiss</button>
            </p>
          )}
          {viewRefusal && <StateBlock refusal={viewRefusal} retry={loadView} />}
          {/* A list stays mounted while it reloads after a write, so what the
              manager just did (a preview, a confirmation) is not wiped by the
              refresh. The skeleton is only for the first load of a view. */}
          {!viewRefusal && viewLoading && !hasData && <Skeleton />}
          {/* Process 6: Medicaid-lost re-vets due now, in the PA's own work only. Hidden when there are none. */}
          {!viewRefusal && view === 'work' && scope && <RevetsDue as={as} canRecord={canDo(scope, 'change_status')} onChanged={() => { loadScope(true); }} />}
          {!viewRefusal && hasData && view === 'work' && work && <WorkList data={work} selected={caseId} onOpen={open} onPage={onPage} whose={whose} acts={rowActs} />}
          {!viewRefusal && hasData && view === 'follow-ups' && followUps && <FollowUpList data={followUps} selected={caseId} onOpen={open} onPage={onPage} />}
          {!viewRefusal && hasData && view === 'all' && all && <AllList data={all} lens={lens} selected={caseId} onOpen={open} onPage={onPage} acts={rowActs} q={q} />}
          {!viewRefusal && hasData && view === 'sales' && sales && <SalesView data={sales} selected={caseId} onMonth={onMonth} onOpen={open} whose={whose} />}
          {view === 'returned' && (returned.state.status === 'absent'
            ? <p className="pa-empty">Send-back is not turned on for this server yet, so nothing can be returned to you.</p>
            : <ReturnedToYou state={returned.state} />)}
          {view === 'monday-moves' && manager && <MondayMoveReview advocates={advocates} />}
          {/* "Open this resident" (2026-10-30): a resident with a PA case opens it in the docked case panel on the Identity
              section -- Team cases for a manager, My work otherwise (the case read decides whether they may see it); one with
              no case is shown on the Residents form itself. The patient id is never dropped. */}
          {view === 'residents' && identity && <ResidentsView identity={identity} onCreated={(_patientId, msg) => { setNotice(msg); loadScope(true); }}
            onOpen={(t) => {
              if (!t.caseId) return;
              setPanelRequest((r) => ({ section: 'identity', nonce: (r ? r.nonce : 0) + 1 }));
              go({ view: manager ? 'all' : 'work', q: t.name, page: 1, caseId: t.caseId });
            }}
            canCorrectDob={manager && Boolean(scope) && canDo(scope!, 'correct_dob')} />}
          {!viewRefusal && hasData && view === 'history' && history && <HistoryList data={history} selected={caseId} q={q} onFilter={onFilter} onOpen={open} onPage={onPage} onClearSearch={() => onSearch('')} />}
          <WorkflowPulse storageKey={`fb-pulse:PATIENT_ADVOCATE:${view}`} onFriction={() => openFeedback('WORKFLOW_PROMPT')} />
        </ViewTransition>
        {docked && caseId && (
          /*
           * The docked case panel (the board rule, 2026-10-05). It is drawn from
           * the URL's case, never from the row: a note, a correction or a status
           * that moves the resident out of this view leaves the panel exactly
           * where it is. Previous / Next walk the list on screen.
           */
          <div className="pa-dock" ref={dockMount} onKeyDown={holdFocus}
            {...(modal
              ? { role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'pa-dock-name', tabIndex: -1 }
              : { role: 'complementary', 'aria-label': 'Case panel', tabIndex: -1 })}>
            <div className="pa-dock-bar">
              <div className="pa-dock-who">
                <strong id="pa-dock-name" tabIndex={-1}>{detail && detail.case.id === caseId ? personName(detail.case) : 'Case'}</strong>
                <span>
                  {detail && detail.case.id === caseId && detail.case.facility_name ? facilityDisplayName(detail.case.facility_name) : ''}
                  {at >= 0 ? ` · ${at + 1} of ${listItems.length} on this page` : ''}
                </span>
                {/* Fix round 7: why Prev and Next are off, on its own line where no ellipsis can cut it. */}
                {at < 0 && listItems.length > 0 && (
                  <span className="pa-dock-offlist" id="pa-dock-offlist">
                    Not in this list<span className="pa-sr-only">: this resident is not on this page of the list, so Previous and Next have no neighbour to open</span>
                  </span>
                )}
              </div>
              <div className="pa-dock-nav">
                <button type="button" className="pa-dock-btn" ref={prevBtnRef} disabled={at <= 0} onClick={() => { navPressed.current = { dir: 'prev', from: caseId }; go({ caseId: listItems[at - 1].id }); }} aria-label="Previous resident"
                  aria-describedby={at < 0 && listItems.length > 0 ? 'pa-dock-offlist' : undefined}>‹ Prev</button>
                <button type="button" className="pa-dock-btn" ref={nextBtnRef} disabled={at < 0 || at >= listItems.length - 1} onClick={() => { navPressed.current = { dir: 'next', from: caseId }; go({ caseId: listItems[at + 1].id }); }} aria-label="Next resident"
                  aria-describedby={at < 0 && listItems.length > 0 ? 'pa-dock-offlist' : undefined}>Next ›</button>
                <button type="button" className="pa-dock-btn pa-dock-close" onClick={() => closePanel(caseId)} aria-label="Close the case panel">✕</button>
              </div>
            </div>
            <div ref={dockNoticeRef} tabIndex={dockNotice ? -1 : undefined}
              className={`pa-dock-msg${dockNotice ? ` pa-notice pa-dock-notice${dockNotice.stop ? ' stop' : ' ok'}` : ''}`}>
              <p className="pa-dock-said" role="status" aria-live="polite" aria-atomic="true">{dockSaid.status}</p>
              <p className="pa-dock-said" role="alert" aria-atomic="true">{dockSaid.alert}</p>
              {dockNotice && (
                <button type="button" className="pa-link" aria-label="Dismiss this message"
                  // The button leaves with the message: focus goes to the panel, never to the page (fix round 6).
                  onClick={() => { if (refusalNotice) dropRefusal(caseId); else setNotice(null); dockRef.current?.focus(); }}>Dismiss</button>
              )}
            </div>
            <CaseContext caseId={caseId} detail={detail} refusal={detailRefusal} loading={detailLoading} manager={manager} advocates={advocates} onAssign={onAssign}
              scope={scope} request={panelRequest} onNoteAdded={patchLatestNote} docked
              // Fix round 7: the result is stamped with the case the write was ABOUT, never the case on
              // screen when the answer lands (Next may have been pressed meanwhile), so it is shown only on
              // that case; and only that case is re-read, and only while the panel is still on it.
              onChanged={async (msg, forCaseId, opts) => {
                const about = forCaseId || caseId;
                const still = Boolean(about) && about === caseIdNow.current;
                // A refusal (fix round 8): while the panel is on its case the form shows it inline, so it is not
                // said twice and nothing is re-read (a reload would wipe the inline error). Once the panel has
                // moved on, it waits with its case as a stop notice that is said when the PA goes back --
                // after any number of moves (fix round 9: it used to go on the next move).
                if (opts?.stop) {
                  if (!still && about) waitRefusal(about, msg);
                  return;
                }
                // A later act on the case succeeded: a refusal still waiting for it is history.
                dropRefusal(about);
                if (still) setNotice(msg, { at: 'dock', caseId: about });
                // The panel has moved on: the result waits with its own case (said if the PA goes back to
                // it), and never displaces a message the case now on screen is showing.
                else setNoticeAt((n) => (n && n.at === 'dock' && n.caseId === caseIdNow.current ? n : { text: msg, stop: false, at: 'dock', caseId: about }));
                await Promise.all([still ? loadDetail(about!) : Promise.resolve(), loadView(), loadScope(true), returned.reload()]);
                keepFocusInDock();
              }} />
          </div>
        )}
        {!selfContained && !inlineDetail && (
          <aside className="pa-context">
            <CaseContext caseId={caseId} detail={detail} refusal={detailRefusal} loading={detailLoading} manager={manager} advocates={advocates} onAssign={onAssign}
              scope={scope} onChanged={async (msg, forCaseId, opts) => {
                const about = forCaseId || caseId;
                // A refusal is shown inline while the case is on screen; elsewhere it waits with its case.
                if (opts?.stop) {
                  if (about && about !== caseIdNow.current) setNotice(msg, { stop: true, at: 'dock', caseId: about });
                  return;
                }
                dropRefusal(about);
                setNotice(msg, { caseId: about });
                const still = Boolean(about) && about === caseIdNow.current;
                await Promise.all([still ? loadDetail(about!) : Promise.resolve(), loadView(), loadScope(true), returned.reload()]);
              }} />
          </aside>
        )}
      </main>
      {feedback}
      </div>
      </WorkspaceLayout>
    </div>
  );
}
